import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Edit2, Save, X, RefreshCw, Check } from 'lucide-react';
import { api } from '../lib/api';
import { toast } from 'sonner';

const INPUT = { width: '100%', background: 'var(--pp-overlay)', border: '1px solid var(--pp-border)', color: 'var(--pp-text)', padding: '10px 12px', fontSize: '13px', outline: 'none', borderRadius: 0, fontFamily: 'inherit' };
const LABEL = { display: 'block', fontSize: '10px', fontWeight: 600, letterSpacing: '0.15em', textTransform: 'uppercase', color: 'var(--pp-text-3)', marginBottom: '6px' };

function LinkMetaApiMasterBlock({ strategyId, strategyName, masterAccounts }) {
  const [available, setAvailable] = useState([]);
  const [picked, setPicked]       = useState('');
  const [busy, setBusy]           = useState(false);
  const existingMaster = (masterAccounts || []).find(m => m.strategy_id === strategyId);

  useEffect(() => {
    let alive = true;
    if (existingMaster) return;     // strategy already has a master — skip
    api.adminAvailableMetaApiForStrategy(strategyId)
      .then(r => { if (alive) setAvailable(r.metaapi_connections || []); })
      .catch(() => { if (alive) setAvailable([]); });
    return () => { alive = false; };
  }, [strategyId, existingMaster]);

  if (existingMaster) {
    return (
      <div style={{ marginTop: 16, padding: 10, border: '1px solid var(--pp-border)', background: 'rgba(0,255,148,0.04)', fontSize: 11, color: 'var(--pp-text-2)' }}>
        ✅ Master linked: <strong>{existingMaster.name}</strong> &middot; {existingMaster.platform} &middot; <span style={{ fontFamily: 'monospace' }}>{existingMaster.login_id || '—'}</span> @ {existingMaster.server || '—'}
      </div>
    );
  }

  return (
    <div data-testid={`link-master-block-${strategyId}`} style={{ marginTop: 16, padding: 12, border: '1px dashed var(--pp-border)', background: 'rgba(255,176,0,0.04)' }}>
      <p style={{ margin: '0 0 8px', fontSize: 11, color: '#FFB000', fontWeight: 700, letterSpacing: '0.1em' }}>NO MASTER LINKED</p>
      <p style={{ margin: '0 0 10px', fontSize: 11, color: 'var(--pp-text-3)', lineHeight: 1.5 }}>
        Promote one of your existing MetaAPI connections (MT4 or MT5) into the master for <strong>{strategyName}</strong> — no need to re-enter credentials.
      </p>
      {available.length === 0 ? (
        <p style={{ margin: 0, fontSize: 11, color: 'var(--pp-text-3)' }}>No unlinked admin MetaAPI connections found. Link a new MT4/MT5 account in Profile → Connections first.</p>
      ) : (
        <div style={{ display: 'flex', gap: 8 }}>
          <select
            data-testid="link-master-select"
            value={picked}
            onChange={e => setPicked(e.target.value)}
            style={{ ...INPUT, flex: 1 }}>
            <option value="">— pick a MetaAPI connection —</option>
            {available.map(m => (
              <option key={m.connection_id} value={m.connection_id}>
                {(m.platform || 'MT5').toUpperCase()} · {m.account_label || m.login} · {m.server} {m.is_demo ? '(demo)' : ''} {m.owner_name ? `— ${m.owner_name}` : ''}
              </option>
            ))}
          </select>
          <button
            data-testid="link-master-confirm-btn"
            disabled={!picked || busy}
            onClick={async () => {
              setBusy(true);
              try {
                const r = await api.adminLinkMetaApiAsMaster(strategyId, picked);
                if (r.ok) toast.success(`Linked ${r.master.platform} master to ${strategyName}`);
                else toast.error('Linking failed');
              } catch (e) { toast.error(e.message); }
              setBusy(false);
            }}
            className="btn-primary"
            style={{ padding: '8px 16px', fontWeight: 700, fontSize: 12 }}>
            {busy ? '...' : 'LINK AS MASTER'}
          </button>
        </div>
      )}
    </div>
  );
}



const TABS = ['overview', 'strategies', 'signals', 'followers', 'students', 'funded', 'verification', 'payments', 'discounts', 'funded-requests', 'access-codes', 'as-codes', 'passwords', 'announcements', 'proxies', 'prop-firms', 'settings', 'waitlist', 'quotes', 'metaapi', 'brokers', 'stealth', 'chats'];


function LaunchEmailsPanel() {
  const [students, setStudents] = React.useState([]);
  const [launching, setLaunching] = React.useState({});   // { user_id: true }
  const [launched, setLaunched]   = React.useState({});   // { user_id: ['welcome', ...] }

  React.useEffect(() => {
    api.getHeldEmails()
      .then(list => setStudents(list || []))
      .catch(() => {});
  }, []);

  if (!students.length) return null;

  const launchOne = async (userId, name) => {
    setLaunching(p => ({ ...p, [userId]: true }));
    try {
      const r = await api.launchStudentEmailsForUser(userId);
      setLaunched(p => ({ ...p, [userId]: r.sent || [] }));
      setStudents(prev => prev.filter(s => s.user_id !== userId));
      toast.success(`Emails sent to ${name}!`);
    } catch (e) { toast.error(e.message); }
    setLaunching(p => ({ ...p, [userId]: false }));
  };

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#00FF94' }}>
          🚀 Pending Student Launches ({students.length})
        </p>
        <button onClick={async () => {
          if (!window.confirm(`Send emails to ALL ${students.length} students at once?`)) return;
          await api.launchStudentEmails();
          toast.success('All student emails sent!');
          setStudents([]);
        }} style={{ fontSize: 11, background: 'none', border: '1px solid var(--pp-border)', color: 'var(--pp-text-3)', padding: '4px 10px', cursor: 'pointer' }}>
          Launch All
        </button>
      </div>

      {students.map(s => (
        <div key={s.user_id} style={{ background: 'var(--pp-surface)', border: '1px solid rgba(0,255,148,0.25)', padding: 14, marginBottom: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <p style={{ margin: '0 0 2px', fontWeight: 700, fontSize: 14 }}>{s.name}</p>
              <p style={{ margin: '0 0 6px', fontSize: 12, color: 'var(--pp-text-3)' }}>{s.email}</p>
              {/* What will be sent */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                {(s.emails_to_send || []).map((e, i) => (
                  <span key={i} style={{ fontSize: 9, padding: '2px 7px', background: 'rgba(41,121,255,0.12)', color: 'var(--pp-accent)', border: '1px solid rgba(41,121,255,0.25)', fontWeight: 700 }}>
                    {e.startsWith('funded:') ? `💰 ${e.replace('funded:', '')}` : e === 'welcome' ? '👋 Welcome' : e === 'active_student_passcode' ? '🎓 Passcode' : e}
                  </span>
                ))}
              </div>
              {s.passcode && (
                <p style={{ margin: '6px 0 0', fontSize: 11, color: 'var(--pp-text-3)' }}>
                  Passcode: <span className="mono" style={{ color: '#2979FF', fontWeight: 700 }}>{s.passcode}</span>
                </p>
              )}
            </div>
            <button
              onClick={() => launchOne(s.user_id, s.name)}
              disabled={launching[s.user_id]}
              style={{ flexShrink: 0, height: 40, padding: '0 16px', background: '#00FF94', border: 'none', color: '#000', fontWeight: 800, fontSize: 12, cursor: 'pointer', letterSpacing: '0.05em' }}
            >
              {launching[s.user_id] ? '...' : 'LAUNCH'}
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}


function PasswordManagerPanel() {
  const [rows, setRows]       = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [search, setSearch]   = React.useState('');
  const [resetTarget, setResetTarget] = React.useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await api.listAllPasswords();
      setRows(r || []);
    } catch (e) { toast.error(e.message); }
    setLoading(false);
  };
  React.useEffect(() => { reload(); }, []);

  const filtered = rows.filter(r => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (r.email || '').toLowerCase().includes(q) || (r.name || '').toLowerCase().includes(q);
  });

  const copyToClipboard = (txt, label) => {
    navigator.clipboard.writeText(txt);
    toast.success(`${label} copied`);
  };

  if (loading) return <p style={{ color: 'var(--pp-text-3)' }}>Loading passwords…</p>;

  return (
    <div data-testid="admin-passwords-tab">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, gap: 12, flexWrap: 'wrap' }}>
        <input
          data-testid="passwords-search"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="🔍  Search name or email…"
          style={{ flex: 1, minWidth: 240, height: 38, background: '#0a0a0a', border: '1px solid var(--pp-border)', color: 'var(--pp-text)', padding: '0 12px', fontSize: 13 }}
        />
        <span style={{ fontSize: 12, color: 'var(--pp-text-3)' }}>{filtered.length} of {rows.length} users</span>
      </div>

      <div style={{ padding: 12, background: 'rgba(255,176,0,0.06)', border: '1px solid rgba(255,176,0,0.35)', marginBottom: 16, fontSize: 11, color: 'var(--pp-text-2)', lineHeight: 1.6 }}>
        <strong style={{ color: '#FFB000' }}>⚠ Admin-only.</strong> Plaintext passwords are stored only for users who registered or had their password reset after Feb 2026. Older accounts show <em>(legacy — reset to view)</em>. Resetting also emails the user the new password.
      </div>

      <div style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border)' }}>
        {filtered.length === 0 && <p style={{ padding: 16, color: 'var(--pp-text-3)', fontSize: 12, margin: 0 }}>No users match your search.</p>}
        {filtered.map(r => {
          const hasPlain = !!r.password_plain;
          return (
            <div key={r.user_id} data-testid={`pw-row-${r.user_id}`} style={{ padding: 12, borderBottom: '1px solid var(--pp-border)', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 220px', minWidth: 200 }}>
                <p style={{ margin: 0, fontWeight: 700, fontSize: 13 }}>{r.name || '(unnamed)'}</p>
                <p style={{ margin: '2px 0 0', fontSize: 11, color: 'var(--pp-text-3)' }}>{r.email}</p>
              </div>
              <div style={{ flex: '0 0 220px', fontFamily: 'monospace', fontSize: 13 }}>
                {hasPlain
                  ? <span data-testid={`pw-${r.user_id}`} style={{ color: '#00FF94', cursor: 'pointer' }} onClick={() => copyToClipboard(r.password_plain, 'Password')} title="Click to copy">{r.password_plain}</span>
                  : <span style={{ color: 'var(--pp-text-3)', fontStyle: 'italic', fontSize: 11 }}>(legacy — reset to view)</span>
                }
              </div>
              <div style={{ flex: '0 0 auto', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <button data-testid={`pw-reset-${r.user_id}`} onClick={() => setResetTarget(r)} style={{ background: '#FFB000', border: '1px solid #FFB000', color: '#000', padding: '5px 12px', fontSize: 10, cursor: 'pointer', fontWeight: 700, letterSpacing: '0.05em' }}>RESET</button>
              </div>
            </div>
          );
        })}
      </div>

      {resetTarget && (
        <ResetPasswordModal target={resetTarget} onClose={() => { setResetTarget(null); reload(); }} />
      )}
    </div>
  );
}


function ResetPasswordModal({ target, onClose }) {
  const [newPw, setNewPw]     = React.useState('');
  const [notify, setNotify]   = React.useState(true);
  const [saving, setSaving]   = React.useState(false);

  // Suggest a friendly default that meets the 6-char floor
  React.useEffect(() => { setNewPw('Profit2026!'); }, []);

  const submit = async () => {
    if (newPw.length < 6) return toast.error('Password must be at least 6 characters');
    setSaving(true);
    try {
      const r = await api.adminResetUserPassword(target.user_id, newPw, notify);
      toast.success(`Reset! ${target.email} → ${r.new_password}${r.notified ? ' (emailed)' : ''}`);
      onClose();
    } catch (e) { toast.error(e.message); setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.78)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }} onClick={onClose}>
      <div data-testid="reset-password-modal" style={{ background: 'var(--pp-surface)', border: '1px solid #FFB000', padding: 22, width: 440, maxWidth: '95vw' }} onClick={e => e.stopPropagation()}>
        <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#FFB000', letterSpacing: '0.08em' }}>RESET PASSWORD</p>
        <p style={{ margin: '4px 0 18px', fontSize: 12, color: 'var(--pp-text-3)' }}>For <strong>{target.name}</strong> ({target.email})</p>

        <label style={{ display: 'block', marginBottom: 14 }}>
          <span style={{ fontSize: 11, letterSpacing: '0.1em', color: 'var(--pp-text-3)', textTransform: 'uppercase' }}>New Password</span>
          <input data-testid="reset-new-password" type="text" value={newPw} onChange={e => setNewPw(e.target.value)} style={{ width: '100%', height: 46, background: '#0a0a0a', border: '1px solid var(--pp-border)', color: 'var(--pp-text)', padding: '0 14px', fontSize: 14, fontFamily: 'monospace', marginTop: 6 }} />
        </label>

        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 18, cursor: 'pointer', fontSize: 12 }}>
          <input type="checkbox" checked={notify} onChange={e => setNotify(e.target.checked)} />
          Email the user their new password
        </label>

        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onClose} style={{ flex: 1, height: 42, background: 'transparent', border: '1px solid var(--pp-border)', color: 'var(--pp-text)', cursor: 'pointer' }}>Cancel</button>
          <button data-testid="submit-reset-password" disabled={saving} onClick={submit} style={{ flex: 2, height: 42, background: '#FFB000', color: '#000', fontWeight: 700, border: 'none', cursor: 'pointer' }}>{saving ? 'RESETTING…' : 'RESET PASSWORD'}</button>
        </div>
      </div>
    </div>
  );
}


function AccessCodesPanel() {
  const [codes, setCodes]       = React.useState([]);
  const [discounts, setDiscounts] = React.useState([]);
  const [quotes, setQuotes]     = React.useState([]);
  const [loading, setLoading]   = React.useState(true);
  const [filter, setFilter]     = React.useState('all');     // all | active_trader | active_student
  const [discountModal, setDiscountModal] = React.useState(null);   // user row to issue discount for

  const reload = async () => {
    setLoading(true);
    try {
      const [c, d, q] = await Promise.all([
        api.getAllAccessCodes().catch(() => []),
        api.listDiscountCodes().catch(() => []),
        api.listQuoteRequests().catch(() => []),
      ]);
      setCodes(c || []); setDiscounts(d || []); setQuotes(q || []);
    } catch (e) { toast.error(e.message); }
    setLoading(false);
  };
  React.useEffect(() => { reload(); }, []);

  const filtered = filter === 'all' ? codes : codes.filter(c => c.tier === filter);
  const openQuotes = quotes.filter(q => q.status === 'open');

  const togglePower = async (row) => {
    try {
      await api.toggleCodePower(row.tier, row.code_id, !row.power_enabled);
      toast.success(`${row.tier === 'active_student' ? 'Student' : 'Trader'} code ${row.power_enabled ? 'powered OFF' : 'powered ON'}`);
      reload();
    } catch (e) { toast.error(e.message); }
  };

  if (loading) return <p style={{ color: 'var(--pp-text-3)' }}>Loading access codes…</p>;

  return (
    <div data-testid="admin-access-codes-tab">
      {/* ── Open Quote Requests ──────────────────────────── */}
      {openQuotes.length > 0 && (
        <div style={{ marginBottom: 22, padding: 14, border: '1px solid rgba(255,176,0,0.4)', background: 'rgba(255,176,0,0.05)' }}>
          <p className="section-label" style={{ marginTop: 0, marginBottom: 10, color: '#FFB000' }}>📞 Open Quote Requests ({openQuotes.length})</p>
          {openQuotes.map(q => {
            const linkedUser = codes.find(c => c.user_id === q.user_id);
            return (
              <div key={q.request_id} data-testid={`quote-${q.request_id}`} style={{ padding: 12, background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <p style={{ margin: 0, fontWeight: 700 }}>{q.user_name} <span style={{ color: 'var(--pp-text-3)', fontWeight: 400, fontSize: 12 }}>· {q.user_email}</span></p>
                  <p style={{ margin: '4px 0 0', fontSize: 12, color: '#FFB000' }}>Target: <strong>${(q.target_price || 0).toLocaleString()}/mo</strong> · default $3,000</p>
                  {q.message && <p style={{ margin: '6px 0 0', fontSize: 11, color: 'var(--pp-text-3)', fontStyle: 'italic' }}>"{q.message}"</p>}
                </div>
                <div style={{ display: 'flex', gap: 6, flexDirection: 'column' }}>
                  {linkedUser && (
                    <button data-testid={`issue-discount-from-quote-${q.request_id}`} onClick={() => setDiscountModal(linkedUser)} className="btn-primary" style={{ background: '#00FF94', color: '#000', padding: '6px 12px', fontSize: 11, fontWeight: 700 }}>ISSUE DISCOUNT</button>
                  )}
                  <button onClick={async () => { await api.closeQuoteRequest(q.request_id); reload(); }} style={{ background: 'transparent', border: '1px solid var(--pp-border)', color: 'var(--pp-text-3)', padding: '6px 12px', fontSize: 10, cursor: 'pointer' }}>CLOSE</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Filter chips + count ─────────────────────────── */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        {[
          { id: 'all', label: `ALL (${codes.length})` },
          { id: 'active_trader', label: `TRADER (${codes.filter(c => c.tier === 'active_trader').length})` },
          { id: 'active_student', label: `STUDENT (${codes.filter(c => c.tier === 'active_student').length})` },
        ].map(f => (
          <button key={f.id} data-testid={`filter-${f.id}`} onClick={() => setFilter(f.id)} style={{ padding: '6px 14px', background: filter === f.id ? 'rgba(0,255,148,0.15)' : 'transparent', border: '1px solid ' + (filter === f.id ? '#00FF94' : 'var(--pp-border)'), color: filter === f.id ? '#00FF94' : 'var(--pp-text-2)', fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', cursor: 'pointer' }}>{f.label}</button>
        ))}
        <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--pp-text-3)' }}>{discounts.filter(d => !d.is_used).length} active discount codes</span>
      </div>

      {/* ── Codes table ──────────────────────────────────── */}
      <div style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border)' }}>
        {filtered.length === 0 && <p style={{ padding: 16, color: 'var(--pp-text-3)', fontSize: 12, margin: 0 }}>No codes yet.</p>}
        {filtered.map(c => {
          const userDiscounts = discounts.filter(d => d.user_id === c.user_id);
          const tierColor = c.tier === 'active_student' ? '#FFB000' : '#00FF94';
          return (
            <div key={c.code_id} data-testid={`code-row-${c.code_id}`} style={{ padding: 14, borderBottom: '1px solid var(--pp-border)', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 220px', minWidth: 200 }}>
                <p style={{ margin: 0, fontWeight: 700, fontSize: 13 }}>{c.user_name || '(unnamed)'}</p>
                <p style={{ margin: '2px 0 0', fontSize: 11, color: 'var(--pp-text-3)' }}>{c.user_email}</p>
              </div>
              <div style={{ flex: '0 0 auto' }}>
                <span style={{ fontSize: 9, padding: '3px 8px', background: `${tierColor}22`, border: `1px solid ${tierColor}66`, color: tierColor, fontWeight: 700, letterSpacing: '0.1em' }}>
                  {c.tier === 'active_student' ? 'STUDENT' : 'TRADER'}
                </span>
              </div>
              <div style={{ flex: '0 0 160px', fontFamily: 'monospace', fontSize: 12, color: 'var(--pp-text-2)' }}>{c.passcode}</div>
              {userDiscounts.length > 0 && (
                <div style={{ flex: '0 0 auto', fontSize: 10, color: '#00FF94' }}>{userDiscounts.length} discount{userDiscounts.length > 1 ? 's' : ''}</div>
              )}
              <button data-testid={`issue-discount-${c.user_id}`} onClick={() => setDiscountModal(c)} style={{ background: 'transparent', border: '1px solid var(--pp-border)', color: 'var(--pp-text-2)', padding: '5px 10px', fontSize: 10, cursor: 'pointer', fontWeight: 700, letterSpacing: '0.05em' }}>+ DISCOUNT</button>
              <button
                data-testid={`toggle-power-${c.code_id}`}
                onClick={() => togglePower(c)}
                style={{
                  background: c.power_enabled ? '#00FF94' : 'transparent',
                  border: '1px solid ' + (c.power_enabled ? '#00FF94' : 'var(--pp-border)'),
                  color: c.power_enabled ? '#000' : 'var(--pp-text-3)',
                  padding: '6px 14px', fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', cursor: 'pointer',
                }}
              >{c.power_enabled ? '⚡ ON' : 'OFF'}</button>
            </div>
          );
        })}
      </div>

      {/* ── All Discount Codes ───────────────────────────── */}
      <p className="section-label" style={{ marginTop: 28, marginBottom: 10 }}>All Discount Codes Issued ({discounts.length})</p>
      <div style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border)' }}>
        {discounts.length === 0 && <p style={{ padding: 16, color: 'var(--pp-text-3)', fontSize: 12, margin: 0 }}>No discount codes issued yet.</p>}
        {discounts.map(d => (
          <div key={d.discount_id} style={{ padding: 12, borderBottom: '1px solid var(--pp-border)', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 220px' }}>
              <p style={{ margin: 0, fontSize: 13, fontWeight: 700 }}>{d.user_name}</p>
              <p style={{ margin: '2px 0 0', fontSize: 11, color: 'var(--pp-text-3)' }}>{d.user_email}</p>
            </div>
            <div style={{ flex: '0 0 160px', fontFamily: 'monospace', fontSize: 12, color: '#00FF94' }}>{d.code}</div>
            <div style={{ flex: '0 0 80px', fontSize: 11 }}>
              {d.percent_off >= 100 ? <span style={{ color: '#00FF94', fontWeight: 700 }}>FREE</span> : `${d.percent_off}% off`}
            </div>
            <div style={{ flex: '0 0 90px', fontSize: 10 }}>{d.is_used ? <span style={{ color: '#666' }}>USED</span> : <span style={{ color: '#FFB000' }}>UNUSED</span>}</div>
            <div style={{ flex: '0 0 auto', fontSize: 10, color: 'var(--pp-text-3)' }}>expires {(d.expires_at || '').slice(0, 10)}</div>
          </div>
        ))}
      </div>

      {/* ── Issue Discount Modal ─────────────────────────── */}
      {discountModal && (
        <IssueDiscountModal target={discountModal} onClose={() => { setDiscountModal(null); reload(); }} />
      )}
    </div>
  );
}


function IssueDiscountModal({ target, onClose }) {
  const [percent, setPercent] = React.useState(25);
  const [days, setDays]       = React.useState(30);
  const [notes, setNotes]     = React.useState('');
  const [saving, setSaving]   = React.useState(false);

  const submit = async () => {
    if (!(percent > 0 && percent <= 100)) return toast.error('Percent must be 1-100');
    setSaving(true);
    try {
      const r = await api.createDiscountCode({
        user_id: target.user_id, percent_off: percent, expires_days: days, notes,
      });
      toast.success(`Discount code ${r.code} issued · ${percent}% off · emailed to ${target.user_email}`);
      onClose();
    } catch (e) { toast.error(e.message); setSaving(false); }
  };

  const finalPrice = Math.max(0, 3000 * (1 - percent / 100));

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.78)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }} onClick={onClose}>
      <div data-testid="issue-discount-modal" style={{ background: 'var(--pp-surface)', border: '1px solid #00FF94', padding: 22, width: 460, maxWidth: '95vw' }} onClick={e => e.stopPropagation()}>
        <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#00FF94', letterSpacing: '0.08em' }}>ISSUE DISCOUNT CODE</p>
        <p style={{ margin: '4px 0 18px', fontSize: 12, color: 'var(--pp-text-3)' }}>For <strong>{target.user_name}</strong> ({target.user_email})</p>

        <label style={{ display: 'block', marginBottom: 16 }}>
          <span style={{ fontSize: 11, letterSpacing: '0.1em', color: 'var(--pp-text-3)', textTransform: 'uppercase' }}>Percent Off (1-100)</span>
          <input data-testid="discount-percent" type="number" min="1" max="100" value={percent} onChange={e => setPercent(parseFloat(e.target.value) || 0)} style={{ width: '100%', height: 42, background: '#0a0a0a', border: '1px solid var(--pp-border)', color: 'var(--pp-text)', padding: '0 12px', fontSize: 14, marginTop: 4 }} />
        </label>

        <div style={{ padding: 12, background: 'rgba(0,255,148,0.06)', border: '1px solid rgba(0,255,148,0.3)', marginBottom: 16, fontSize: 12 }}>
          <p style={{ margin: 0 }}>Final price: <strong style={{ color: '#00FF94', fontSize: 16 }}>${finalPrice.toLocaleString()}/mo</strong> {finalPrice === 0 && <span style={{ color: '#00FF94' }}>(FREE — full bypass)</span>}</p>
        </div>

        <label style={{ display: 'block', marginBottom: 16 }}>
          <span style={{ fontSize: 11, letterSpacing: '0.1em', color: 'var(--pp-text-3)', textTransform: 'uppercase' }}>Expires (days)</span>
          <input type="number" min="1" max="365" value={days} onChange={e => setDays(parseInt(e.target.value) || 30)} style={{ width: '100%', height: 42, background: '#0a0a0a', border: '1px solid var(--pp-border)', color: 'var(--pp-text)', padding: '0 12px', fontSize: 14, marginTop: 4 }} />
        </label>

        <label style={{ display: 'block', marginBottom: 18 }}>
          <span style={{ fontSize: 11, letterSpacing: '0.1em', color: 'var(--pp-text-3)', textTransform: 'uppercase' }}>Internal Notes (optional)</span>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} style={{ width: '100%', background: '#0a0a0a', border: '1px solid var(--pp-border)', color: 'var(--pp-text)', padding: 10, fontSize: 12, resize: 'vertical', marginTop: 4 }} />
        </label>

        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onClose} style={{ flex: 1, height: 42, background: 'transparent', border: '1px solid var(--pp-border)', color: 'var(--pp-text)', cursor: 'pointer' }}>Cancel</button>
          <button data-testid="submit-discount" disabled={saving} onClick={submit} className="btn-primary" style={{ flex: 2, height: 42, background: '#00FF94', color: '#000' }}>{saving ? 'ISSUING…' : 'ISSUE & EMAIL CODE'}</button>
        </div>
      </div>
    </div>
  );
}



export default function AdminPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Overview
  const [followers, setFollowers] = useState([]);
  const [executions, setExecutions] = useState([]);

  // Strategies
  const [strategies, setStrategies] = useState([]);
  const [masterVsProjection, setMasterVsProjection] = useState([]);
  const [infusionRunning, setInfusionRunning] = useState(false);
  const [showStratForm, setShowStratForm] = useState(false);
  const [editingStrat, setEditingStrat] = useState(null);
  const [stratForm, setStratForm] = useState({ name: '', instrument: '', timeframe: 'M30', total_return_pct: '', win_rate: '', sharpe_ratio: '', profit_factor: '', max_drawdown_pct: '', status: 'active', description: '', platform: 'MetaTrader 5', passcode: '', passcode_required: false });

  // Signals
  const [signals, setSignals] = useState([]);
  const [sigForm, setSigForm] = useState({ symbol: '', action: 'buy', volume: '0.01', sl: '', tp: '', strategy_id: '' });
  const [sendingSignal, setSendingSignal] = useState(false);

  // Platform settings
  const [platformSettings, setPlatformSettings] = useState({ onboarding_wizard_enabled: false, new_signups_enabled: true, registration_passcode: 'Ready2Profit', show_ai_tab: true });
  const [savingSettings, setSavingSettings] = useState(false);

  // Prop firm rules
  const [propFirmRules, setPropFirmRules] = useState({ enabled: true, min_return_pct: 500, max_drawdown_pct: 25, default_factor: 0.10 });
  const [savingPropRules, setSavingPropRules] = useState(false);
  const [stratPropForm, setStratPropForm] = useState({});  // { strategy_id: { override, factor, note } }

  // API Keys
  const [apiKeys, setApiKeys] = useState([]);
  const [apiKeyValues, setApiKeyValues] = useState({});  // { KEY_NAME: 'new value being typed' }
  const [savingKey, setSavingKey] = useState({});        // { KEY_NAME: true }
  const [showKeyInput, setShowKeyInput] = useState({});  // { KEY_NAME: true }

  // Waitlist
  const [waitlist, setWaitlist] = useState([]);
  const [resetRequests, setResetRequests] = useState([]);

  // Quotes
  const [quotes, setQuotes] = useState([]);

  // MetaAPI
  const [metaapiMasters, setMetaapiMasters] = useState([]);
  const [mapiForm, setMapiForm] = useState({ login: '', password: '', server: '', platform: 'mt5', strategy_id: '', account_label: '' });
  const [connectingMapi, setConnectingMapi] = useState(false);

  // Master Accounts
  const [masterAccounts, setMasterAccounts] = useState([]);

  const [adminFundedAccounts, setAdminFundedAccounts] = useState([]);
  const [adminVerifications, setAdminVerifications] = useState([]);
  const [adminPayouts, setAdminPayouts] = useState([]);
  const [fundedEditId, setFundedEditId] = useState(null);
  const [fundedEditForm, setFundedEditForm] = useState({});
  // Payout deny modal
  const [denyModal, setDenyModal] = useState(null);   // full payout_request doc or null
  const [denyReasons, setDenyReasons] = useState([]); // [{code, label}]
  const [denyForm, setDenyForm] = useState({ reason_code: 'consistency_rule_failed', reason_detail: '' });
  const [denySubmitting, setDenySubmitting] = useState(false);

  // Notifications
  const [notifications, setNotifications] = useState([]);

  // Broker Accounts
  const [brokerAccounts, setBrokerAccounts] = useState([]);
  const [proxyAssignments, setProxyAssignments] = useState([]);
  const [showProxyModal, setShowProxyModal] = useState(null); // user_id
  const [proxyInventory, setProxyInventory] = useState([]);
  const [proxyInventoryLoading, setProxyInventoryLoading] = useState(false);
  const [proxyInventoryFilter, setProxyInventoryFilter] = useState('all'); // all | unassigned | assigned
  const [proxyHideInactive, setProxyHideInactive] = useState(true);  // hide expired/refunded by default
  const [proxyAssignTarget, setProxyAssignTarget] = useState(null); // the inventory row being assigned
  const [proxyAssignToUser, setProxyAssignToUser] = useState('');
  const [propFirmProfiles, setPropFirmProfiles] = useState([]);
  const [newPropFirm, setNewPropFirm] = useState({ name: '', needs_proxy: true, needs_playwright: true, notes: '', platforms: '' });
  const [proxyForm, setProxyForm] = useState({ proxy_host: '', proxy_port: 12323, proxy_username: '', proxy_password: '', proxy_label: '' });
  const [savingProxy, setSavingProxy] = useState(false);
  const [iproyalProxies, setIproyalProxies] = useState(null);
  const [activeStudents, setActiveStudents] = useState([]);
  const [showCreateStudent, setShowCreateStudent] = useState(false);
  const [newStudent, setNewStudent] = useState({ name: '', email: '', password: '', is_demo: false, generate_passcode: true, send_passcode_in_email: true, send_welcome_email: true, email_schedule: 'immediate', scheduled_at: '', strategy_ids: [], funded_account: null, proxy: null });
  const [editProfileFor, setEditProfileFor] = useState(null); // student object
  const [editProfileForm, setEditProfileForm] = useState({});
  const [addFundedFor, setAddFundedFor] = useState(null); // student object
  const [addFundedForm, setAddFundedForm] = useState({ size: 100000, account_type: 'instant', mt5_login: '', mt5_server: '', broker: '', status: 'pending_creation', daily_drawdown_limit_pct: 4, max_drawdown_limit_pct: 8, min_profit_target_pct: 9, consistency_pct: 15, notes: '', email_credentials_to_student: false });
  // Native simulated funded account modal (with optional stealth backend engine bridge)
  const [addNativeFor, setAddNativeFor] = useState(null); // student object
  const [addNativeForm, setAddNativeForm] = useState({ size: 100000, asset_class: 'futures', account_type: 'instant', min_profit_target_pct: 6, max_drawdown_limit_pct: 10, daily_drawdown_limit_pct: 5, consistency_pct: 15, backend_engine_kind: 'none', backend_engine_id: '', backend_engine_mode: 'fire_only', backfill: true });
  const [savingNative, setSavingNative] = useState(false);
  const [nativeEngineConns, setNativeEngineConns] = useState([]); // broker connections available for stealth engine binding
  const [addBybitFor, setAddBybitFor] = useState(null); // student object for Bybit
  const [addBybitForm, setAddBybitForm] = useState({ api_key: '', api_secret: '', market_type: 'linear', account_label: '', sizing_mode: 'dollar_match', fixed_qty: 0 });
  const [bybitConnsByUser, setBybitConnsByUser] = useState({}); // user_id -> connections[]
  const [savingBybit, setSavingBybit] = useState(false);
  const [bybitProxy, setBybitProxy] = useState(null);
  const [bybitProxyForm, setBybitProxyForm] = useState({ host: '', port: 12321, username: '', password: '', label: '' });
  const [bybitFallbackForm, setBybitFallbackForm] = useState({ host: '', port: 12321, username: '', password: '', label: '' });
  const [savingBybitProxy, setSavingBybitProxy] = useState(false);
  const [savingBybitFallback, setSavingBybitFallback] = useState(false);
  const [bybitProxyTest, setBybitProxyTest] = useState(null);
  const [bybitFallbackTest, setBybitFallbackTest] = useState(null);
  const [subscribeFor, setSubscribeFor] = useState(null); // student object
  const [launchingUser, setLaunchingUser] = useState({});

  // Live Chats
  const [supportChats, setSupportChats] = useState([]);
  const [firmRules, setFirmRules] = useState([]);
  const [stealthAccounts, setStealthAccounts] = useState([]);
  const [stealthExecutions, setStealthExecutions] = useState([]);
  const [stealthForm, setStealthForm] = useState({ open: false, mode: 'create', payload: {} });
  const [availableProxies, setAvailableProxies] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [chatReply, setChatReply] = useState('');

  // Payments & Discount Requests (new)
  const [adminPayments, setAdminPayments] = useState([]);
  const [paymentsStats, setPaymentsStats] = useState({ total: 0, completed: 0, pending: 0, failed: 0, total_revenue_usd: 0 });
  const [discountRequests, setDiscountRequests] = useState([]);
  const [discountApprovalPct, setDiscountApprovalPct] = useState({}); // {reqId: pct}
  const [discountMessage, setDiscountMessage] = useState({}); // {reqId: msg}

  // Funded AS Requests
  const [fundedAsRequests, setFundedAsRequests] = useState([]);
  const [fundedAmt, setFundedAmt] = useState({}); // {reqId: amount_usd}
  const [fundedMsg, setFundedMsg] = useState({}); // {reqId: message}

  // AS Passcodes (bulk generator + list)
  const [asCodes, setAsCodes] = useState([]);
  const [bulkCount, setBulkCount] = useState(10);
  const [bulkTag, setBulkTag] = useState('');
  const [bulkNote, setBulkNote] = useState('');
  const [bulkGenerating, setBulkGenerating] = useState(false);
  const [lastGeneratedBatch, setLastGeneratedBatch] = useState([]);

  const loadTabData = useCallback(async (tab) => {
    setLoading(true);
    try {
      switch (tab) {
        case 'overview':
          const [fols, execs, notifs, studsOv, fasOv, wlOv] = await Promise.all([
            api.getAdminFollowers().catch(() => []),
            api.getAdminExecutions().catch(() => []),
            api.getAdminNotifications().catch(() => []),
            api.getActiveStudents().catch(() => []),
            api.getAdminFundedAccounts().catch(() => []),
            api.getAdminWaitlist().catch(() => []),
          ]);
          setFollowers(fols || []);
          setExecutions(execs || []);
          setNotifications(notifs || []);
          setActiveStudents(studsOv || []);
          setAdminFundedAccounts(fasOv || []);
          setWaitlist(wlOv || []);
          break;
        case 'strategies':
          const [strats, mvp] = await Promise.all([
            api.getAdminStrategies().catch(() => []),
            api.getMasterVsProjection().catch(() => ({ strategies: [] })),
          ]);
          setStrategies(strats || []);
          setMasterVsProjection(mvp?.strategies || []);
          break;
        case 'signals': {
          const [sigs, masterAccts, stratsForSignals] = await Promise.all([
            api.getAdminTradeSignals().catch(() => []),
            api.getAdminMasterAccounts().catch(() => []),
            api.getAdminStrategies().catch(() => []),
          ]);
          setSignals(sigs || []);
          setMasterAccounts(masterAccts || []);
          // Hydrate strategies state so the "Send Signal" form's Strategy
          // <select> is populated even when an admin opens the Signals tab
          // directly without first visiting the Strategies tab.
          setStrategies(stratsForSignals || []);
          break;
        }
        case 'followers': {
          const [fols2, execs2] = await Promise.all([
            api.getAdminFollowers().catch(() => []),
            api.getAdminExecutions().catch(() => []),
          ]);
          setFollowers(fols2 || []);
          setExecutions(execs2 || []);
          break;
        }
        case 'funded': {
          const [fas, pays, reasons] = await Promise.all([
            api.getAdminFundedAccounts().catch(() => []),
            api.getAdminPayoutRequests().catch(() => []),
            api.getAdminPayoutDenyReasons().catch(() => []),
          ]);
          setAdminFundedAccounts(fas || []);
          setAdminPayouts(pays || []);
          setDenyReasons(reasons || []);
          break;
        }
        case 'verification': {
          const verifs = await api.getAdminVerifications().catch(() => []);
          setAdminVerifications(verifs || []);
          break;
        }
        case 'settings': {
          const settings = await api.getAdminPlatformSettings().catch(() => ({}));
          setPlatformSettings(settings);
          const resets = await api.getAdminResetRequests().catch(() => []);
          setResetRequests(resets || []);
          const keys = await api.getAdminApiKeys().catch(() => []);
          setApiKeys(keys || []);
          const pfrules = await api.getAdminPropFirmRules().catch(() => ({}));
          setPropFirmRules(pfrules);
          const bbp = await api.adminBybitProxyGet().catch(() => ({ global: null, fallback: null }));
          setBybitProxy(bbp);
          if (bbp?.global) {
            setBybitProxyForm({
              host: bbp.global.host || '', port: bbp.global.port || 12321,
              username: bbp.global.username || '', password: '',
              label: bbp.global.label || '',
            });
          }
          if (bbp?.fallback) {
            setBybitFallbackForm({
              host: bbp.fallback.host || '', port: bbp.fallback.port || 12321,
              username: bbp.fallback.username || '', password: '',
              label: bbp.fallback.label || '',
            });
          }
          break;
        }
        case 'waitlist': {
          const wl = await api.getAdminWaitlist().catch(() => []);
          setWaitlist(wl || []);
          break;
        }
        case 'quotes': {
          const qs = await api.getAdminQuotes().catch(() => []);
          setQuotes(qs || []);
          break;
        }
        case 'metaapi': {
          const masters = await api.getMetaAPIMasters().catch(() => []);
          setMetaapiMasters(masters || []);
          break;
        }
        case 'prop-firms': {
          const pf = await api.getAdminPropFirmProfiles().catch(() => []);
          setPropFirmProfiles(pf || []);
          break;
        }
        case 'proxies': {
          const [pinv, stu] = await Promise.all([
            api.getProxyInventory().catch(() => ({ proxies: [] })),
            api.getActiveStudents().catch(() => []),
          ]);
          setProxyInventory(pinv?.proxies || []);
          setActiveStudents(stu || []);
          break;
        }
        case 'brokers': {
          const [accts, proxies] = await Promise.all([
            api.getAdminBrokerAccounts().catch(() => []),
            api.getProxyAssignments().catch(() => []),
          ]);
          setBrokerAccounts(accts || []);
          setProxyAssignments(proxies || []);
          break;
        }
        case 'payments': {
          const [pays, stats] = await Promise.all([
            api.adminPayments().catch(() => []),
            api.adminPaymentsStats().catch(() => ({})),
          ]);
          setAdminPayments(pays || []);
          setPaymentsStats(stats || {});
          break;
        }
        case 'discounts': {
          const reqs = await api.adminDiscountRequests().catch(() => []);
          setDiscountRequests(reqs || []);
          break;
        }
        case 'funded-requests': {
          const reqs = await api.adminFundedAsRequests().catch(() => []);
          setFundedAsRequests(reqs || []);
          break;
        }
        case 'as-codes': {
          const codes = await api.adminListASPasscodes().catch(() => []);
          setAsCodes(codes || []);
          break;
        }
        case 'announcements': {
          const p = await api.adminAnnouncementPreview().catch(() => null);
          setAnnouncePreview(p);
          break;
        }
        case 'students': {
          const [studs, stratsForStud] = await Promise.all([
            api.getActiveStudents().catch(() => []),
            api.getAdminStrategies().catch(() => []),
          ]);
          setActiveStudents(studs || []);
          setStrategies(stratsForStud || []);
          break;
        }
        case 'chats': {
          const chats = await api.getAdminSupportChats().catch(() => []);
          setSupportChats(chats || []);
          break;
        }
        case 'stealth': {
          const [rules, sas, execs, prox] = await Promise.all([
            api.adminListFirmRules().catch(() => []),
            api.adminListStealthAccounts().catch(() => []),
            api.adminListStealthExecutions(50).catch(() => []),
            api.adminGetAvailableProxies().catch(() => ({ proxies: [] })),
          ]);
          setFirmRules(rules || []);
          setStealthAccounts(sas || []);
          setStealthExecutions(execs || []);
          setAvailableProxies(prox?.proxies || []);
          break;
        }
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadTabData(activeTab);
  }, [activeTab, loadTabData]);

  const handleSaveStrategy = async () => {
    try {
      if (editingStrat) {
        await api.updateAdminStrategy(editingStrat.strategy_id, stratForm);
        toast.success('Strategy updated!');
      } else {
        await api.createAdminStrategy({ ...stratForm, total_return_pct: parseFloat(stratForm.total_return_pct) || 0, win_rate: parseFloat(stratForm.win_rate) || 0, sharpe_ratio: parseFloat(stratForm.sharpe_ratio) || 0, profit_factor: parseFloat(stratForm.profit_factor) || 0, max_drawdown_pct: parseFloat(stratForm.max_drawdown_pct) || 0, starting_balance: 100000, final_balance: 100000 * (1 + (parseFloat(stratForm.total_return_pct) || 0) / 100), subscribers: 0 });
        toast.success('Strategy created!');
      }
      setShowStratForm(false);
      setEditingStrat(null);
      loadTabData('strategies');
    } catch (e) { toast.error(e.message); }
  };

  const handleDeleteStrategy = async (id) => {
    if (!window.confirm('Delete this strategy?')) return;
    try {
      await api.deleteAdminStrategy(id);
      toast.success('Strategy deleted!');
      loadTabData('strategies');
    } catch (e) { toast.error(e.message); }
  };

  const handleSendSignal = async () => {
    setSendingSignal(true);
    try {
      await api.sendTradeSignal({ symbol: sigForm.symbol, action: sigForm.action, volume: parseFloat(sigForm.volume) || 0.01, sl: sigForm.sl ? parseFloat(sigForm.sl) : undefined, tp: sigForm.tp ? parseFloat(sigForm.tp) : undefined, strategy_id: sigForm.strategy_id });
      toast.success('Signal sent!');
      loadTabData('signals');
    } catch (e) { toast.error(e.message); }
    setSendingSignal(false);
  };

  const handleSaveApiKey = async (keyName) => {
    const value = (apiKeyValues[keyName] || '').trim();
    setSavingKey(prev => ({ ...prev, [keyName]: true }));
    try {
      await api.setAdminApiKey(keyName, value);
      toast.success(value ? `${keyName} saved!` : `${keyName} cleared`);
      // Refresh key list
      const keys = await api.getAdminApiKeys().catch(() => []);
      setApiKeys(keys || []);
      setApiKeyValues(prev => ({ ...prev, [keyName]: '' }));
      setShowKeyInput(prev => ({ ...prev, [keyName]: false }));
    } catch (e) { toast.error(e.message); }
    setSavingKey(prev => ({ ...prev, [keyName]: false }));
  };

  const handleSavePropFirmRules = async () => {
    setSavingPropRules(true);
    try {
      const updated = await api.updateAdminPropFirmRules(propFirmRules);
      setPropFirmRules(updated);
      toast.success('Prop firm rules saved!');
    } catch (e) { toast.error(e.message); }
    setSavingPropRules(false);
  };

  const handleSaveStratPropFirm = async (stratId) => {
    const form = stratPropForm[stratId] || {};
    try {
      await api.setStrategyPropFirm(stratId, {
        prop_firm_override: form.override === 'global' ? null : form.override === 'always',
        prop_firm_factor:   form.factor === '' || form.factor === null ? null : parseFloat(form.factor),
        prop_firm_note:     form.note || '',
      });
      toast.success('Strategy prop firm settings saved!');
      loadTabData('strategies');
    } catch (e) { toast.error(e.message); }
  };

  const handleSavePlatformSettings = async () => {
    setSavingSettings(true);
    try {
      await api.updateAdminPlatformSettings(platformSettings);
      toast.success('Settings saved!');
    } catch (e) { toast.error(e.message); }
    setSavingSettings(false);
  };

  const handleConnectMetaAPIMaster = async () => {
    setConnectingMapi(true);
    try {
      await api.connectMetaAPIMasterAccount(mapiForm);
      toast.success('MetaAPI master connected!');
      loadTabData('metaapi');
    } catch (e) { toast.error(e.message); }
    setConnectingMapi(false);
  };

  return (
    <div style={{ background: 'var(--pp-bg)', minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid var(--pp-border)', position: 'sticky', top: 0, background: 'var(--pp-bg)', zIndex: 30 }}>
        <button onClick={() => navigate('/profile')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--pp-text)' }}><ArrowLeft size={20} /></button>
        <h1 style={{ margin: 0, fontSize: 18, fontWeight: 700, flex: 1 }}>ADMIN PANEL</h1>
        <button onClick={() => loadTabData(activeTab)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--pp-text-3)' }}><RefreshCw size={16} /></button>
      </div>

      {/* Tab bar */}
      <div data-testid="admin-tabs" style={{ overflowX: 'auto', borderBottom: '1px solid var(--pp-border)' }}>
        <div style={{ display: 'flex', padding: '0 16px', gap: 2, minWidth: 'max-content' }}>
          {TABS.map(t => (
            <button key={t} onClick={() => setActiveTab(t)} style={{ padding: '12px 16px', background: 'none', border: 'none', borderBottom: `2px solid ${activeTab === t ? 'var(--pp-primary)' : 'transparent'}`, color: activeTab === t ? 'var(--pp-primary)' : 'var(--pp-text-3)', fontSize: 12, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', cursor: 'pointer', whiteSpace: 'nowrap' }}>
              {t}
            </button>
          ))}
        </div>
      </div>

      <div style={{ maxWidth: 720, margin: '0 auto', padding: '16px 16px 40px' }}>
        {loading && <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><div style={{ width: 28, height: 28, border: '2px solid var(--pp-primary)', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} /></div>}

        {/* OVERVIEW */}
        {!loading && activeTab === 'overview' && (
          <div>
            {/* Launch Emails — shown when there are held emails */}
            <LaunchEmailsPanel />

            {notifications.filter(n => !n.read).length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <p className="section-label" style={{ marginBottom: 8 }}>Notifications</p>
                {notifications.filter(n => !n.read).slice(0, 5).map(n => (
                  <div key={n.notif_id} style={{ background: 'var(--pp-surface)', border: '1px solid rgba(255,176,0,0.3)', padding: 12, marginBottom: 8, display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <p style={{ margin: 0, fontSize: 13, fontWeight: 600 }}>{n.type?.replace('_', ' ').toUpperCase()}</p>
                      <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--pp-text-2)' }}>{n.message}</p>
                    </div>
                    <button onClick={async () => { await api.markNotificationRead(n.notif_id).catch(() => {}); loadTabData('overview'); }} style={{ background: 'none', border: 'none', color: 'var(--pp-success)', cursor: 'pointer' }}><Check size={14} /></button>
                  </div>
                ))}
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginBottom: 20 }}>
              {[
                { l: 'Active Students', v: activeStudents.length, c: 'var(--pp-success)' },
                { l: 'Waitlist', v: waitlist.length, c: 'var(--pp-accent)' },
                { l: 'Funded Accounts', v: adminFundedAccounts.length, c: '#FFB000' },
                { l: 'Total Followers', v: followers.length },
                { l: 'Executions', v: executions.length },
                { l: 'Unread Alerts', v: notifications.filter(n => !n.read).length },
              ].map(s => (
                <div key={s.l} style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: 14, textAlign: 'center' }}>
                  <p className="mono" style={{ margin: 0, fontSize: 24, fontWeight: 700, color: s.c || 'var(--pp-text)' }}>{s.v}</p>
                  <p className="section-label" style={{ marginTop: 4 }}>{s.l}</p>
                </div>
              ))}
            </div>

            {/* Active Students — quick-action panel */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <p className="section-label" style={{ margin: 0 }}>Active Students ({activeStudents.length})</p>
              <button data-testid="overview-goto-students" onClick={() => setActiveTab('students')} style={{ fontSize: 10, background: 'transparent', border: '1px solid var(--pp-border)', color: 'var(--pp-accent)', padding: '4px 10px', cursor: 'pointer', letterSpacing: '0.1em', fontWeight: 700 }}>MANAGE →</button>
            </div>
            {activeStudents.length === 0 ? (
              <div style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: 18, textAlign: 'center', marginBottom: 20 }}>
                <p style={{ color: 'var(--pp-text-3)', margin: 0, fontSize: 13 }}>No active students yet.</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
                {activeStudents.slice(0, 10).map(s => (
                  <div key={s.user_id} data-testid={`overview-student-${s.user_id}`} style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: 12, display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ flex: '1 1 220px', minWidth: 0 }}>
                      <p style={{ margin: 0, fontSize: 13, fontWeight: 700 }}>{s.name}</p>
                      <p style={{ margin: '2px 0 0', fontSize: 11, color: 'var(--pp-text-3)' }}>{s.email}</p>
                      {s.password_plain && (
                        <span
                          data-testid={`overview-pwd-${s.user_id}`}
                          onClick={e => { e.currentTarget.textContent = e.currentTarget.textContent === '• click to reveal password' ? s.password_plain : '• click to reveal password'; }}
                          style={{ cursor: 'pointer', fontFamily: 'monospace', fontSize: 10, color: '#666', marginTop: 2, display: 'inline-block' }}
                        >• click to reveal password</span>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                      {s.passcode && <span className="mono" style={{ fontSize: 10, color: 'var(--pp-success)', background: 'rgba(0,255,148,0.1)', padding: '3px 7px', fontWeight: 700 }}>{s.passcode}</span>}
                      {s.is_verified && <span style={{ fontSize: 8, color: 'var(--pp-success)', letterSpacing: '0.1em', fontWeight: 700 }}>✓ KYC</span>}
                      {s.funded_account && <span style={{ fontSize: 9, background: 'rgba(255,176,0,0.12)', color: '#FFB000', padding: '3px 7px', fontWeight: 700 }}>{s.funded_account.status?.replace('_',' ').toUpperCase()}</span>}
                      {s.engine_summary && (() => {
                        const eng = s.engine_summary.engine || 'none';
                        const isNone = eng === 'none' || s.engine_summary.connections === 0;
                        const isMeta = eng === 'metaapi';
                        const isPw   = eng === 'playwright_pending' || eng === 'playwright';
                        const bg = isNone ? 'rgba(120,120,120,0.12)' : isMeta ? 'rgba(0,255,148,0.15)' : isPw ? 'rgba(255,176,0,0.15)' : 'rgba(41,121,255,0.15)';
                        const fg = isNone ? '#888' : isMeta ? 'var(--pp-success)' : isPw ? '#FFB000' : 'var(--pp-accent)';
                        const br = isNone ? 'rgba(120,120,120,0.35)' : isMeta ? 'rgba(0,255,148,0.4)' : isPw ? 'rgba(255,176,0,0.4)' : 'rgba(41,121,255,0.4)';
                        const label = isNone ? 'NO BROKER LINKED' : eng.toUpperCase().replace('_','-');
                        return (
                          <span
                            data-testid={`engine-badge-${s.user_id}`}
                            title={s.engine_summary.last_error || s.engine_summary.status || ''}
                            style={{ fontSize: 9, padding: '3px 7px', fontWeight: 700, letterSpacing: '0.08em', background: bg, color: fg, border: '1px solid ' + br }}>
                            ENGINE: {label}
                            {s.engine_summary.flagged_count > 0 && ` · ⚠ ${s.engine_summary.flagged_count}`}
                            {s.engine_summary.is_prop_firm && s.engine_summary.prop_firm_name ? ` · ${s.engine_summary.prop_firm_name}` : ''}
                          </span>
                        );
                      })()}
                      <button
                        data-testid={`overview-toggle-paid-${s.user_id}`}
                        onClick={async () => {
                          const next = !(s.is_active_student || s.is_paid);
                          try { await api.adminSetPaidStatus(s.user_id, next); toast.success(next ? `${s.name} FULLY PAID` : `${s.name} revoked`); loadTabData('overview'); } catch (e) { toast.error(e.message); }
                        }}
                        style={{ fontSize: 9, background: (s.is_active_student || s.is_paid) ? 'rgba(0,255,148,0.15)' : 'transparent', border: '1px solid ' + ((s.is_active_student || s.is_paid) ? 'rgba(0,255,148,0.5)' : 'var(--pp-border)'), color: (s.is_active_student || s.is_paid) ? 'var(--pp-success)' : 'var(--pp-text-3)', padding: '3px 8px', fontWeight: 700, letterSpacing: '0.1em', cursor: 'pointer' }}
                      >{(s.is_active_student || s.is_paid) ? '✓ PAID' : 'MARK PAID'}</button>
                    </div>
                  </div>
                ))}
                {activeStudents.length > 10 && (
                  <p style={{ textAlign: 'center', fontSize: 11, color: 'var(--pp-text-3)', margin: 4 }}>… and {activeStudents.length - 10} more — click MANAGE above</p>
                )}
              </div>
            )}

            {/* Waitlist snapshot */}
            {waitlist.length > 0 && (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <p className="section-label" style={{ margin: 0 }}>Waitlist ({waitlist.length})</p>
                  <button onClick={() => setActiveTab('waitlist')} style={{ fontSize: 10, background: 'transparent', border: '1px solid var(--pp-border)', color: 'var(--pp-accent)', padding: '4px 10px', cursor: 'pointer', letterSpacing: '0.1em', fontWeight: 700 }}>MANAGE →</button>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 20 }}>
                  {waitlist.slice(0, 5).map(w => (
                    <div key={w.waitlist_id} style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                      <div>
                        <p style={{ margin: 0, fontSize: 12, fontWeight: 600 }}>{w.name}</p>
                        <p style={{ margin: '2px 0 0', fontSize: 10, color: 'var(--pp-text-3)' }}>{w.email} · {w.phone || '—'}</p>
                      </div>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <span className={w.status === 'approved' ? 'badge-success' : w.status === 'contacted' ? 'badge-accent' : 'badge-warning'} style={{ fontSize: 9 }}>{(w.status || 'PENDING').toUpperCase()}</span>
                        {w.entry_passcode && <span className="mono" style={{ fontSize: 10, color: 'var(--pp-success)', fontWeight: 700 }}>{w.entry_passcode}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {notifications.filter(n => !n.read).length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <p className="section-label" style={{ marginBottom: 8 }}>Notifications</p>
                {notifications.filter(n => !n.read).slice(0, 5).map(n => (
                  <div key={n.notif_id} style={{ background: 'var(--pp-surface)', border: '1px solid rgba(255,176,0,0.3)', padding: 12, marginBottom: 8, display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <p style={{ margin: 0, fontSize: 13, fontWeight: 600 }}>{n.type?.replace('_', ' ').toUpperCase()}</p>
                      <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--pp-text-2)' }}>{n.message}</p>
                    </div>
                    <button onClick={async () => { await api.markNotificationRead(n.notif_id).catch(() => {}); loadTabData('overview'); }} style={{ background: 'none', border: 'none', color: 'var(--pp-success)', cursor: 'pointer' }}><Check size={14} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* STRATEGIES */}
        {!loading && activeTab === 'strategies' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <p className="section-label">Strategies ({strategies.length})</p>
              <button data-testid="admin-create-strategy-button" onClick={() => { setEditingStrat(null); setStratForm({ name: '', instrument: '', timeframe: 'M30', total_return_pct: '', win_rate: '', sharpe_ratio: '', profit_factor: '', max_drawdown_pct: '', status: 'active', description: '', platform: 'MetaTrader 5', passcode: '', passcode_required: false }); setShowStratForm(true); }} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'var(--pp-primary)', border: 'none', color: '#fff', padding: '8px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}><Plus size={14} /> Add Strategy</button>
            </div>

            {/* Master-vs-Projection comparison panel */}
            <div style={{ background: 'rgba(41,121,255,0.04)', border: '1px solid rgba(41,121,255,0.25)', padding: 14, marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div>
                  <p style={{ margin: 0, fontSize: 12, fontWeight: 700, letterSpacing: '0.1em', color: '#2979FF' }}>📡 LIVE MASTER vs PROJECTED — THIS WEEK</p>
                  <p style={{ margin: '4px 0 0', fontSize: 10, color: 'var(--pp-text-3)' }}>
                    Toggles default OFF (auto-extension uses historical avg).
                    Flip ON for any week where the real master beats the projection — the 🟢 indicator highlights when REAL is winning.
                  </p>
                </div>
                <button data-testid="run-weekly-infusion"
                  disabled={infusionRunning}
                  onClick={async () => {
                    setInfusionRunning(true);
                    try {
                      const r = await api.runWeeklyInfusion();
                      toast.success(`Updated ${r.updated || 0} strategies`);
                      loadTabData('strategies');
                    } catch (e) { toast.error(e.message); }
                    setInfusionRunning(false);
                  }}
                  style={{ height: 32, padding: '0 12px', background: '#2979FF', border: 'none', color: '#fff', fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', cursor: infusionRunning ? 'wait' : 'pointer', opacity: infusionRunning ? 0.6 : 1 }}>
                  {infusionRunning ? 'RUNNING…' : '↻ RUN WEEKLY INFUSION'}
                </button>
              </div>
              {masterVsProjection.length === 0 ? (
                <p style={{ fontSize: 11, color: 'var(--pp-text-3)', margin: '6px 0 0' }}>No data — make sure strategies have a `master_account_id` linked.</p>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 10, marginTop: 10 }}>
                  {masterVsProjection.map(m => (
                    <div key={m.strategy_id} data-testid={`mvp-${m.strategy_id}`} style={{ background: 'var(--pp-surface)', border: `1px solid ${m.real_better_than_projection ? 'rgba(0,255,148,0.5)' : 'var(--pp-border)'}`, padding: 10 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                        <p style={{ margin: 0, fontSize: 12, fontWeight: 700 }}>{m.name}</p>
                        {m.real_better_than_projection && (
                          <span style={{ fontSize: 9, background: 'rgba(0,255,148,0.15)', color: '#00FF94', padding: '2px 6px', fontWeight: 800, letterSpacing: '0.08em' }}>🟢 REAL +{(m.real_weekly_pnl_pct - m.projected_weekly_pct).toFixed(2)}pp</span>
                        )}
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, fontSize: 10 }}>
                        <div>
                          <p style={{ margin: 0, color: 'var(--pp-text-3)', letterSpacing: '0.06em' }}>PROJECTED/wk</p>
                          <p className="mono" style={{ margin: 0, fontSize: 13, fontWeight: 700, color: 'var(--pp-text)' }}>+{(m.projected_weekly_pct || 0).toFixed(2)}%</p>
                        </div>
                        <div>
                          <p style={{ margin: 0, color: 'var(--pp-text-3)', letterSpacing: '0.06em' }}>REAL MASTER/wk</p>
                          <p className="mono" style={{ margin: 0, fontSize: 13, fontWeight: 700, color: m.real_data_ok ? (m.real_weekly_pnl_pct >= 0 ? '#00FF94' : '#FF3333') : 'var(--pp-text-3)' }}>
                            {m.real_data_ok ? `${m.real_weekly_pnl_pct >= 0 ? '+' : ''}${m.real_weekly_pnl_pct.toFixed(2)}%` : '—'}
                          </p>
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 8, paddingTop: 8, borderTop: '1px solid var(--pp-border)' }}>
                        <span style={{ fontSize: 9, color: 'var(--pp-text-3)', letterSpacing: '0.06em', fontWeight: 600 }}>USE REAL MASTER</span>
                        <button data-testid={`toggle-infusion-${m.strategy_id}`}
                          onClick={async () => {
                            try {
                              await api.toggleMasterInfusion(m.strategy_id, !m.use_real_master_infusion);
                              toast.success(`${m.name}: real-master infusion ${!m.use_real_master_infusion ? 'ON' : 'OFF'}`);
                              loadTabData('strategies');
                            } catch (e) { toast.error(e.message); }
                          }}
                          style={{ width: 38, height: 20, background: m.use_real_master_infusion ? '#00FF94' : 'rgba(63,63,70,0.5)', borderRadius: 10, border: 'none', cursor: 'pointer', position: 'relative', padding: 0 }}>
                          <span style={{ position: 'absolute', top: 2, left: m.use_real_master_infusion ? 20 : 2, width: 16, height: 16, borderRadius: '50%', background: '#fff', transition: 'left 150ms' }} />
                        </button>
                      </div>
                      {m.real_data_error && !m.real_data_ok && (
                        <p style={{ margin: '6px 0 0', fontSize: 9, color: '#FFB000' }}>{m.real_data_error}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {showStratForm && (
              <div style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: 16, marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <p style={{ margin: 0, fontWeight: 700 }}>{editingStrat ? 'Edit Strategy' : 'New Strategy'}</p>
                  <button onClick={() => { setShowStratForm(false); setEditingStrat(null); }} style={{ background: 'none', border: 'none', color: 'var(--pp-text-3)', cursor: 'pointer' }}><X size={18} /></button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  {[
                    { k: 'name', l: 'Name', p: 'e.g. S.A.S.S.' },
                    { k: 'instrument', l: 'Instrument', p: 'e.g. XAU/USD' },
                    { k: 'timeframe', l: 'Timeframe', p: 'M30' },
                    { k: 'platform', l: 'Platform', p: 'MetaTrader 5' },
                    { k: 'total_return_pct', l: 'Total Return %', p: '1001', t: 'number' },
                    { k: 'win_rate', l: 'Win Rate %', p: '85.7', t: 'number' },
                    { k: 'sharpe_ratio', l: 'Sharpe', p: '2.84', t: 'number' },
                    { k: 'profit_factor', l: 'Profit Factor', p: '3.73', t: 'number' },
                    { k: 'max_drawdown_pct', l: 'Max Drawdown %', p: '24.83', t: 'number' },
                    { k: 'passcode', l: 'Passcode (optional)', p: 'leave blank = open' },
                  ].map(f => (
                    <div key={f.k}>
                      <label style={LABEL}>{f.l}</label>
                      <input style={INPUT} type={f.t || 'text'} value={stratForm[f.k] || ''} placeholder={f.p} onChange={e => setStratForm(prev => ({ ...prev, [f.k]: e.target.value }))} />
                    </div>
                  ))}
                  <div>
                    <label style={LABEL}>Status</label>
                    <select style={{ ...INPUT, cursor: 'pointer' }} value={stratForm.status} onChange={e => setStratForm(prev => ({ ...prev, status: e.target.value }))}>
                      <option value="active">Active</option>
                      <option value="backtesting">Backtesting</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                </div>
                <div style={{ marginTop: 12 }}>
                  <label style={LABEL}>Description</label>
                  <textarea style={{ ...INPUT, minHeight: 60, resize: 'vertical' }} value={stratForm.description || ''} placeholder="Strategy description..." onChange={e => setStratForm(prev => ({ ...prev, description: e.target.value }))} />
                </div>
                <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
                  <button onClick={() => { setShowStratForm(false); setEditingStrat(null); }} style={{ flex: 1, height: 44, background: 'transparent', border: '1px solid var(--pp-border)', color: 'var(--pp-text)', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
                  <button onClick={handleSaveStrategy} className="btn-primary" style={{ flex: 2, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}><Save size={14} /> Save</button>
                </div>

                {editingStrat && (
                  <LinkMetaApiMasterBlock
                    strategyId={editingStrat.strategy_id}
                    strategyName={editingStrat.name}
                    masterAccounts={masterAccounts}
                  />
                )}
              </div>
            )}

            <div data-testid="admin-strategies-table">
              {/* Pending Review: user-created strategies awaiting approval */}
              {strategies.filter(s => s.is_user_created && !s.is_approved).length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <p style={{ fontSize: 11, fontWeight: 700, color: '#FFB000', letterSpacing: '0.1em', marginBottom: 8 }}>PENDING REVIEW ({strategies.filter(s => s.is_user_created && !s.is_approved).length})</p>
                  {strategies.filter(s => s.is_user_created && !s.is_approved).map(s => (
                    <div key={s.strategy_id} data-testid={`pending-strategy-${s.strategy_id}`} style={{ background: 'rgba(255,176,0,0.06)', border: '1px solid rgba(255,176,0,0.3)', padding: 14, marginBottom: 8 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <p style={{ margin: 0, fontWeight: 700, fontSize: 14 }}>{s.name}</p>
                            <span style={{ fontSize: 9, background: 'rgba(255,176,0,0.15)', color: '#FFB000', padding: '2px 8px', fontWeight: 700 }}>COMMUNITY</span>
                          </div>
                          <p style={{ margin: '3px 0 0', fontSize: 11, color: 'var(--pp-text-3)' }}>By {s.creator_name || 'Unknown'} &middot; {s.instrument} &middot; {s.risk_level}</p>
                          {s.algorithm_notes && <p style={{ margin: '4px 0 0', fontSize: 11, color: 'var(--pp-text-2)' }}>{s.algorithm_notes}</p>}
                        </div>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button
                            data-testid={`approve-strategy-${s.strategy_id}`}
                            onClick={async () => { try { await api.approveStrategy(s.strategy_id); toast.success(`${s.name} approved!`); loadTabData('strategies'); } catch (e) { toast.error(e.message); } }}
                            style={{ background: 'rgba(0,255,148,0.1)', border: '1px solid rgba(0,255,148,0.3)', color: 'var(--pp-success)', fontSize: 11, padding: '5px 12px', cursor: 'pointer', fontWeight: 700 }}
                          >Approve</button>
                          <button
                            data-testid={`reject-strategy-${s.strategy_id}`}
                            onClick={async () => { try { await api.rejectStrategy(s.strategy_id, 'Rejected by admin'); toast.success(`${s.name} rejected`); loadTabData('strategies'); } catch (e) { toast.error(e.message); } }}
                            style={{ background: 'rgba(255,51,51,0.1)', border: '1px solid rgba(255,51,51,0.3)', color: 'var(--pp-error)', fontSize: 11, padding: '5px 12px', cursor: 'pointer', fontWeight: 700 }}
                          >Reject</button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {strategies.map(s => (
                <div key={s.strategy_id} style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: 14, marginBottom: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <p style={{ margin: 0, fontWeight: 700, fontSize: 14 }}>{s.name}</p>
                        <span className={s.status === 'active' ? 'badge-success' : 'badge-warning'}>{(s.status || '').toUpperCase()}</span>
                        {s.passcode_required && <span className="badge-accent">PASSCODE</span>}
                      </div>
                      <p style={{ margin: '3px 0 0', fontSize: 11, color: 'var(--pp-text-3)' }}>{s.instrument} · {s.timeframe} · {s.total_return_pct}% return · {s.win_rate}% win</p>
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button onClick={() => { setEditingStrat(s); setStratForm({ name: s.name, instrument: s.instrument, timeframe: s.timeframe, total_return_pct: s.total_return_pct, win_rate: s.win_rate, sharpe_ratio: s.sharpe_ratio, profit_factor: s.profit_factor, max_drawdown_pct: s.max_drawdown_pct, status: s.status, description: s.description, platform: s.platform, passcode: s.passcode || '', passcode_required: s.passcode_required || false }); setShowStratForm(true); }} style={{ background: 'none', border: '1px solid var(--pp-border)', color: 'var(--pp-text-3)', padding: '5px 8px', cursor: 'pointer' }}><Edit2 size={13} /></button>
                      <button onClick={() => handleDeleteStrategy(s.strategy_id)} style={{ background: 'none', border: '1px solid rgba(255,51,51,0.3)', color: 'var(--pp-error)', padding: '5px 8px', cursor: 'pointer' }}><Trash2 size={13} /></button>
                    </div>
                  </div>
                  {s.passcode && <p style={{ margin: 0, fontSize: 10, color: 'var(--pp-text-3)' }}>Passcode: <span className="mono" style={{ color: 'var(--pp-warning)' }}>{s.passcode}</span></p>}

                  {/* Prop firm override panel — inline, always visible per strategy */}
                  <div style={{ marginTop: 10, padding: '10px 12px', background: 'var(--pp-overlay)', border: '1px solid var(--pp-border)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 11 }}>🏦</span>
                        <p style={{ margin: 0, fontSize: 11, fontWeight: 700, color: s.prop_firm_safety_mode ? 'var(--pp-warning)' : 'var(--pp-text-3)' }}>
                          Prop Firm: {s.prop_firm_override === true ? 'ALWAYS SAFETY' : s.prop_firm_override === false ? 'NEVER (DISABLED)' : s.prop_firm_safety_mode ? 'SAFETY ACTIVE (auto)' : 'Standard (auto)'}
                          {s.prop_firm_factor && ` · ${(s.prop_firm_factor * 100).toFixed(0)}% factor`}
                        </p>
                      </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                      <div>
                        <label style={{ ...LABEL, fontSize: 8 }}>Override</label>
                        <select
                          style={{ ...INPUT, fontSize: 11, padding: '6px 8px', cursor: 'pointer' }}
                          value={stratPropForm[s.strategy_id]?.override ?? (s.prop_firm_override === true ? 'always' : s.prop_firm_override === false ? 'never' : 'global')}
                          onChange={e => setStratPropForm(p => ({ ...p, [s.strategy_id]: { ...(p[s.strategy_id] || {}), override: e.target.value } }))}
                        >
                          <option value="global">Auto (global rule)</option>
                          <option value="always">Always safety</option>
                          <option value="never">Never (disabled)</option>
                        </select>
                      </div>
                      <div>
                        <label style={{ ...LABEL, fontSize: 8 }}>Factor (e.g. 0.10 = 10%)</label>
                        <input
                          style={{ ...INPUT, fontSize: 11, padding: '6px 8px' }}
                          type="number" step="0.01" min="0.01" max="1"
                          placeholder="use global"
                          value={stratPropForm[s.strategy_id]?.factor ?? (s.prop_firm_factor !== undefined && s.prop_firm_factor !== null ? s.prop_firm_factor : '')}
                          onChange={e => setStratPropForm(p => ({ ...p, [s.strategy_id]: { ...(p[s.strategy_id] || {}), factor: e.target.value } }))}
                        />
                      </div>
                      <div>
                        <label style={{ ...LABEL, fontSize: 8 }}>Note (shown to users)</label>
                        <input
                          style={{ ...INPUT, fontSize: 11, padding: '6px 8px' }}
                          placeholder="optional"
                          value={stratPropForm[s.strategy_id]?.note ?? (s.prop_firm_note || '')}
                          onChange={e => setStratPropForm(p => ({ ...p, [s.strategy_id]: { ...(p[s.strategy_id] || {}), note: e.target.value } }))}
                        />
                      </div>
                    </div>
                    <button
                      onClick={() => handleSaveStratPropFirm(s.strategy_id)}
                      style={{ marginTop: 8, padding: '5px 14px', background: 'var(--pp-primary)', border: 'none', color: '#fff', fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', cursor: 'pointer' }}
                    >
                      SAVE PROP SETTINGS
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* SIGNALS */}
        {!loading && activeTab === 'signals' && (
          <div>
            <p className="section-label" style={{ marginBottom: 12 }}>Send Trade Signal</p>
            <div style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: 16, marginBottom: 20 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                <div><label style={LABEL}>Symbol</label><input style={INPUT} placeholder="e.g. XAUUSD" value={sigForm.symbol} onChange={e => setSigForm(p => ({ ...p, symbol: e.target.value }))} /></div>
                <div><label style={LABEL}>Action</label><select style={{ ...INPUT, cursor: 'pointer' }} value={sigForm.action} onChange={e => setSigForm(p => ({ ...p, action: e.target.value }))}><option value="buy">BUY</option><option value="sell">SELL</option></select></div>
                <div><label style={LABEL}>Volume</label><input style={INPUT} type="number" step="0.01" value={sigForm.volume} onChange={e => setSigForm(p => ({ ...p, volume: e.target.value }))} /></div>
                <div><label style={LABEL}>Strategy</label><select style={{ ...INPUT, cursor: 'pointer' }} value={sigForm.strategy_id} onChange={e => setSigForm(p => ({ ...p, strategy_id: e.target.value }))}><option value="">Select strategy...</option>{strategies.map(s => <option key={s.strategy_id} value={s.strategy_id}>{s.name}</option>)}</select></div>
                <div><label style={LABEL}>Stop Loss</label><input style={INPUT} type="number" step="0.01" placeholder="optional" value={sigForm.sl} onChange={e => setSigForm(p => ({ ...p, sl: e.target.value }))} /></div>
                <div><label style={LABEL}>Take Profit</label><input style={INPUT} type="number" step="0.01" placeholder="optional" value={sigForm.tp} onChange={e => setSigForm(p => ({ ...p, tp: e.target.value }))} /></div>
              </div>
              <button onClick={handleSendSignal} disabled={sendingSignal || !sigForm.symbol} className="btn-primary" style={{ width: '100%', height: 48 }}>{sendingSignal ? 'SENDING...' : 'SEND SIGNAL TO ALL FOLLOWERS'}</button>
            </div>

            <p className="section-label" style={{ marginBottom: 10 }}>Recent Signals ({signals.length})</p>
            {signals.slice(0, 10).map((sig, i) => (
              <div key={sig.signal_id || i} style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: 12, marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span className="mono" style={{ fontWeight: 700, fontSize: 14 }}>{sig.symbol}</span>
                    <span style={{ fontSize: 10, padding: '2px 6px', background: sig.action === 'buy' ? 'rgba(0,255,148,0.15)' : 'rgba(255,51,51,0.15)', color: sig.action === 'buy' ? 'var(--pp-success)' : 'var(--pp-error)', fontWeight: 700 }}>{(sig.action || '').toUpperCase()}</span>
                  </div>
                  <p style={{ margin: '3px 0 0', fontSize: 11, color: 'var(--pp-text-3)' }}>Vol: {sig.volume} · {sig.received_at?.slice(0, 16)}</p>
                </div>
                <span className={sig.status === 'executed' ? 'badge-success' : 'badge-warning'}>{(sig.status || 'pending').toUpperCase()}</span>
              </div>
            ))}
          </div>
        )}

        {/* FOLLOWERS */}
        {!loading && activeTab === 'followers' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <p className="section-label" style={{ margin: 0 }}>Followers ({followers.length})</p>
              <button
                data-testid="cleanup-test-accounts-btn"
                onClick={async () => {
                  if (!window.confirm('Sweep all TEST_/demo/qtest/fundedtest/kycgate accounts + TEST_ waitlist rows?\n\nAdmin and the universal demo user will be preserved.')) return;
                  try {
                    const res = await api.cleanupTestAccounts();
                    if ((res.users_count || 0) === 0 && (res.waitlist_count || 0) === 0) {
                      toast.success('No test accounts to delete — you\'re clean! ✨');
                    } else {
                      toast.success(`Deleted ${res.users_count} user(s) and ${res.waitlist_count} waitlist row(s).`);
                    }
                    const [f, wl] = await Promise.all([
                      api.getAdminFollowers().catch(() => []),
                      api.getAdminWaitlist().catch(() => []),
                    ]);
                    setFollowers(f || []);
                    setWaitlist(wl || []);
                  } catch (e) { toast.error('Cleanup failed: ' + (e?.message || e)); }
                }}
                style={{ background: 'rgba(255,51,51,0.12)', border: '1px solid var(--pp-error)', color: 'var(--pp-error)', padding: '6px 14px', fontSize: 11, letterSpacing: '0.1em', fontWeight: 700, cursor: 'pointer' }}
              >SWEEP TEST ACCOUNTS</button>
            </div>
            {followers.map((f, i) => (
              <div key={i} style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: 14, marginBottom: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                  <div style={{ flex: 1 }}>
                    <p style={{ margin: '0 0 4px', fontWeight: 700, fontSize: 14 }}>
                      {f.user?.name || 'Unknown'}
                      {f.user?.is_demo && <span style={{ fontSize: 9, marginLeft: 8, padding: '2px 6px', background: 'rgba(255,153,0,0.15)', color: 'var(--pp-warning)', letterSpacing: '0.1em' }}>DEMO</span>}
                    </p>
                    <p style={{ margin: 0, fontSize: 12, color: 'var(--pp-text-3)' }}>{f.user?.email}</p>
                  </div>
                  {f.user?.role !== 'admin' && f.user?.user_id !== 'user_demo_universal' && (
                    <button
                      data-testid={`delete-user-${f.user?.user_id}`}
                      onClick={async () => {
                        if (!window.confirm(`Delete ${f.user?.email}?\n\nThis cascades: proxies, subscriptions, broker accounts, funded accounts, payments, push subs, etc.`)) return;
                        try {
                          await api.deleteAdminUser(f.user.user_id);
                          const fresh = await api.getAdminFollowers().catch(() => []);
                          setFollowers(fresh || []);
                        } catch (e) { alert('Delete failed: ' + (e?.message || e)); }
                      }}
                      style={{ background: 'transparent', border: '1px solid var(--pp-error)', color: 'var(--pp-error)', padding: '4px 10px', fontSize: 10, letterSpacing: '0.1em', fontWeight: 700, cursor: 'pointer' }}
                    >DELETE</button>
                  )}
                </div>
                <p className="section-label" style={{ marginBottom: 6 }}>Subscriptions ({f.subscriptions?.length || 0})</p>
                {(f.subscriptions || []).map((sub, j) => (
                  <div key={j} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid rgba(39,39,42,0.5)' }}>
                    <span style={{ fontSize: 12 }}>{sub.strategy_name}</span>
                    <span style={{ fontSize: 11, color: 'var(--pp-text-3)' }}>
                      {sub.copy_settings?.risk_multiplier || 1}x · {sub.copy_settings?.lot_size_mode || 'fixed'}
                    </span>
                  </div>
                ))}
              </div>
            ))}
            <p className="section-label" style={{ margin: '20px 0 12px' }}>Recent Executions ({executions.length})</p>
            <div style={{ overflowX: 'auto' }}>
              <table className="pp-table" style={{ width: '100%' }}>
                <thead><tr><th>User</th><th>Symbol</th><th>Action</th><th>P&L</th><th>Status</th><th>Date</th></tr></thead>
                <tbody>
                  {executions.slice(0, 20).map((e, i) => (
                    <tr key={i}>
                      <td className="mono" style={{ fontSize: 11 }}>{e.user_id?.slice(-6)}</td>
                      <td className="mono" style={{ fontWeight: 700 }}>{e.symbol}</td>
                      <td><span style={{ fontSize: 9, padding: '2px 6px', background: e.action === 'buy' ? 'rgba(0,255,148,0.15)' : 'rgba(255,51,51,0.15)', color: e.action === 'buy' ? 'var(--pp-success)' : 'var(--pp-error)' }}>{(e.action || '').toUpperCase()}</span></td>
                      <td className="mono" style={{ color: (e.pnl || 0) >= 0 ? 'var(--pp-success)' : 'var(--pp-error)' }}>{(e.pnl || 0) >= 0 ? '+' : ''}${(e.pnl || 0).toFixed(2)}</td>
                      <td><span className={e.status === 'executed' ? 'badge-success' : 'badge-warning'}>{(e.status || '').toUpperCase()}</span></td>
                      <td style={{ fontSize: 10, color: 'var(--pp-text-3)' }}>{e.executed_at?.slice(0, 10)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* SETTINGS */}
        {!loading && activeTab === 'settings' && (
          <div>
            {/* ── Post-Redeploy Sync ─────────────────────────────────
                One-click idempotent migrations to run AFTER every prod
                redeploy. Reconciles the prop firm list, promotes any
                stuck Volumetrica connections to active, backfills
                default fields. Safe to run multiple times. */}
            <p className="section-label" style={{ marginBottom: 12 }}>Post-Redeploy Sync</p>
            <div data-testid="admin-post-redeploy-sync" style={{ background: 'var(--pp-surface)', border: '1px solid rgba(0,255,148,0.25)', padding: 16, marginBottom: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 280 }}>
                  <p style={{ margin: 0, fontWeight: 700, fontSize: 14, color: '#00FF94' }}>Run Redeploy Migrations</p>
                  <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--pp-text-3)', lineHeight: 1.6 }}>
                    Run this <strong>once after every production deploy</strong>. It reconciles the prop firm list to the canonical 18, promotes any stuck Volumetrica connections to active (so they appear in students' profiles), and backfills default fields on legacy records. Idempotent — safe to re-run.
                  </p>
                </div>
                <button
                  data-testid="run-redeploy-migrations-btn"
                  onClick={async () => {
                    if (!window.confirm('Run all post-redeploy migrations? Safe to run multiple times.')) return;
                    const t = toast.loading('Running migrations...');
                    try {
                      const r = await api.runRedeployMigrations();
                      toast.dismiss(t);
                      const lines = (r.steps || []).map(s => {
                        const detail = Object.entries(s).filter(([k]) => k !== 'step').map(([k, v]) => `${k}=${typeof v === 'object' ? JSON.stringify(v) : v}`).join(', ');
                        return `• ${s.step}: ${detail}`;
                      }).join('\n');
                      toast.success(`Migrations complete\n\n${lines}`, { duration: 12000 });
                    } catch (e) {
                      toast.dismiss(t);
                      toast.error(`Migrations failed: ${e.message}`);
                    }
                  }}
                  className="btn-primary"
                  style={{ background: '#00FF94', color: '#000', padding: '12px 22px', fontWeight: 700, letterSpacing: '0.08em', whiteSpace: 'nowrap' }}
                >RUN MIGRATIONS</button>
              </div>

              <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--pp-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 280 }}>
                  <p style={{ margin: 0, fontWeight: 700, fontSize: 13 }}>System Health Check</p>
                  <p style={{ margin: '4px 0 0', fontSize: 11, color: 'var(--pp-text-3)' }}>
                    Verifies critical env vars (Resend, Twelve Data, encryption keys) and recent email delivery stats. Use when a student reports a missing reset/welcome email.
                  </p>
                </div>
                <button
                  data-testid="run-system-health-btn"
                  onClick={async () => {
                    try {
                      const r = await api.getSystemHealth();
                      const env = Object.entries(r.env_vars || {}).map(([k, v]) => `${v ? '✓' : '✗'} ${k}`).join('\n');
                      const em = r.email_delivery_24h || {};
                      const summary = `Env vars:\n${env}\n\nEmail delivery (24h):\n• sent: ${em.password_resets_sent}\n• failed: ${em.password_resets_failed}\n• legacy: ${em.legacy_no_status}\n\n${r.advice || ''}`;
                      toast.success(summary, { duration: 15000 });
                    } catch (e) {
                      toast.error(`Health check failed: ${e.message}`);
                    }
                  }}
                  style={{ background: 'transparent', border: '1px solid var(--pp-border)', color: 'var(--pp-text-2)', padding: '10px 20px', cursor: 'pointer', fontWeight: 700, letterSpacing: '0.08em', whiteSpace: 'nowrap', fontSize: 12 }}
                >CHECK</button>
              </div>
            </div>

            <p className="section-label" style={{ marginBottom: 12 }}>Platform Settings</p>
            <div data-testid="admin-platform-settings-form" style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: 16, marginBottom: 20 }}>
              {[
                { k: 'onboarding_wizard_enabled', l: 'Client Setup Wizard', desc: 'Enable onboarding wizard for new users' },
                { k: 'new_signups_enabled', l: 'New Registrations', desc: 'Allow new users to register' },
                { k: 'show_ai_tab', l: 'Show AI Tab', desc: 'Show AI Edge Analyzer tab' },
              ].map(s => (
                <div key={s.k} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 0', borderBottom: '1px solid var(--pp-border)' }}>
                  <div>
                    <p style={{ margin: 0, fontWeight: 600, fontSize: 14 }}>{s.l}</p>
                    <p style={{ margin: '2px 0 0', fontSize: 12, color: 'var(--pp-text-3)' }}>{s.desc}</p>
                  </div>
                  <button onClick={() => setPlatformSettings(prev => ({ ...prev, [s.k]: !prev[s.k] }))} style={{ width: 36, height: 20, background: platformSettings[s.k] ? 'rgba(41,121,255,0.3)' : 'var(--pp-border)', border: '1px solid ' + (platformSettings[s.k] ? 'var(--pp-accent)' : 'var(--pp-border-active)'), borderRadius: 10, cursor: 'pointer', position: 'relative', display: 'flex', alignItems: 'center', padding: '0 3px' }}>
                    <div style={{ width: 14, height: 14, borderRadius: '50%', background: platformSettings[s.k] ? 'var(--pp-accent)' : 'var(--pp-text-3)', transition: 'transform 150ms, background 150ms', transform: `translateX(${platformSettings[s.k] ? 16 : 0}px)` }} />
                  </button>
                </div>
              ))}
              <div style={{ paddingTop: 16 }}>
                <label style={LABEL}>Registration Passcode</label>
                <input style={INPUT} type="text" value={platformSettings.registration_passcode || ''} onChange={e => setPlatformSettings(prev => ({ ...prev, registration_passcode: e.target.value }))} placeholder="Leave blank to disable passcode" />
                <p style={{ margin: '6px 0 0', fontSize: 11, color: 'var(--pp-text-3)' }}>Current: <span className="mono" style={{ color: 'var(--pp-warning)' }}>{platformSettings.registration_passcode || '(none)'}</span></p>
              </div>
              <div style={{ paddingTop: 14 }}>
                <label style={LABEL}>Default Strategy Access Code</label>
                <input style={INPUT} type="text" value={platformSettings.default_strategy_passcode || ''} onChange={e => setPlatformSettings(prev => ({ ...prev, default_strategy_passcode: e.target.value }))} placeholder="e.g. TrepAlgo22" />
                <p style={{ margin: '6px 0 0', fontSize: 11, color: 'var(--pp-text-3)' }}>
                  Required for all strategy subscriptions (unless ActiveStudent or strategy has own code).{' '}
                  Current: <span className="mono" style={{ color: 'var(--pp-warning)' }}>{platformSettings.default_strategy_passcode || '(none)'}</span>
                </p>
              </div>

              <button onClick={handleSavePlatformSettings} disabled={savingSettings} className="btn-primary" style={{ width: '100%', height: 48, marginTop: 16 }}>{savingSettings ? 'SAVING...' : 'SAVE SETTINGS'}</button>
            </div>

            {/* Bybit Global Proxy */}
            <p className="section-label" style={{ marginBottom: 8, marginTop: 4 }}>Bybit Global Proxy</p>
            <p style={{ fontSize: 12, color: 'var(--pp-text-3)', marginBottom: 12, lineHeight: 1.6 }}>
              All Bybit API calls (verify, sync, place_order, close_position, closed-PnL) route through this ONE IPRoyal IP.
              Recommended: <strong style={{ color: '#F7A600' }}>Germany (Frankfurt) — IPRoyal ISP static</strong>. Avoid US/CN/SG/CA-ON/CA-QC
              (Bybit blocks these). Per-student overrides available via API for prop-firm-specific cases.
            </p>
            <div data-testid="bybit-proxy-config" style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: 16, marginBottom: 20 }}>
              {bybitProxy?.global && (
                <div style={{ background: 'rgba(247,166,0,0.08)', border: '1px solid rgba(247,166,0,0.3)', padding: 10, marginBottom: 14 }}>
                  <p style={{ margin: 0, fontSize: 10, color: '#F7A600', fontWeight: 700, letterSpacing: '0.1em' }}>ACTIVE GLOBAL PROXY</p>
                  <p className="mono" style={{ margin: '4px 0 0', fontSize: 12, fontWeight: 700 }}>{bybitProxy.global.label || `${bybitProxy.global.host}:${bybitProxy.global.port}`}</p>
                  <p className="mono" style={{ margin: '2px 0 0', fontSize: 10, color: 'var(--pp-text-3)' }}>{bybitProxy.global.host}:{bybitProxy.global.port} · user={bybitProxy.global.username || '—'}</p>
                </div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10 }}>
                <div style={{ gridColumn: '1 / -1' }}><label style={LABEL}>Proxy Label</label><input style={INPUT} value={bybitProxyForm.label} onChange={e => setBybitProxyForm(p => ({ ...p, label: e.target.value }))} placeholder="Frankfurt DE · IPRoyal ISP" /></div>
                <div><label style={LABEL}>Host</label><input data-testid="bybit-proxy-host" style={INPUT} value={bybitProxyForm.host} onChange={e => setBybitProxyForm(p => ({ ...p, host: e.target.value }))} placeholder="geo.iproyal.com" /></div>
                <div><label style={LABEL}>Port</label><input data-testid="bybit-proxy-port" style={INPUT} type="number" value={bybitProxyForm.port} onChange={e => setBybitProxyForm(p => ({ ...p, port: parseInt(e.target.value) || 12321 }))} /></div>
                <div><label style={LABEL}>Username</label><input data-testid="bybit-proxy-user" style={INPUT} value={bybitProxyForm.username} onChange={e => setBybitProxyForm(p => ({ ...p, username: e.target.value }))} placeholder="iproyal_xxx" /></div>
                <div><label style={LABEL}>Password</label><input data-testid="bybit-proxy-pass" style={INPUT} type="password" value={bybitProxyForm.password} onChange={e => setBybitProxyForm(p => ({ ...p, password: e.target.value }))} placeholder="••••••••" /></div>
              </div>
              <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
                <button
                  data-testid="bybit-proxy-save"
                  disabled={savingBybitProxy || !bybitProxyForm.host}
                  onClick={async () => {
                    setSavingBybitProxy(true);
                    try {
                      const r = await api.adminBybitProxySetGlobal(bybitProxyForm);
                      toast.success('Saved — global Bybit proxy active');
                      setBybitProxy({ ...bybitProxy, global: r.global });
                      setBybitProxyForm(p => ({ ...p, password: '' }));
                    } catch (e) { toast.error(e.message); }
                    setSavingBybitProxy(false);
                  }}
                  className="btn-primary" style={{ flex: 2, height: 42, background: '#F7A600', color: '#000' }}
                >{savingBybitProxy ? 'SAVING…' : 'SAVE PROXY'}</button>
                <button
                  data-testid="bybit-proxy-test"
                  onClick={async () => {
                    setBybitProxyTest({ testing: true });
                    try {
                      const r = await api.adminBybitProxyTest('primary');
                      setBybitProxyTest(r);
                      if (r.ok) toast.success(`Primary OK — Bybit reachable via ${r.proxy}`);
                      else toast.error(`Primary test failed: ${r.error || r.http || 'unknown'}`);
                    } catch (e) { toast.error(e.message); setBybitProxyTest({ ok: false, error: e.message }); }
                  }}
                  style={{ flex: 1, height: 42, background: 'transparent', border: '1px solid var(--pp-border)', color: 'var(--pp-text)', fontWeight: 700, letterSpacing: '0.1em', cursor: 'pointer' }}
                >TEST</button>
              </div>
              {bybitProxyTest && !bybitProxyTest.testing && (
                <div style={{ marginTop: 10, padding: 8, background: bybitProxyTest.ok ? 'rgba(0,255,148,0.08)' : 'rgba(255,51,51,0.08)', border: `1px solid ${bybitProxyTest.ok ? 'rgba(0,255,148,0.3)' : 'rgba(255,51,51,0.3)'}`, fontSize: 11, color: bybitProxyTest.ok ? '#00FF94' : '#FF6B6B' }}>
                  {bybitProxyTest.ok
                    ? `✓ Bybit /v5/market/time reached via ${bybitProxyTest.proxy}`
                    : `✗ ${bybitProxyTest.error || `HTTP ${bybitProxyTest.http}`}`}
                </div>
              )}

              {/* Fallback proxy slot — auto-used when primary hits CloudFront block / 429 / connection error */}
              <div style={{ marginTop: 22, paddingTop: 16, borderTop: '1px dashed var(--pp-border)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <p style={{ margin: 0, fontSize: 11, fontWeight: 700, color: 'var(--pp-text)', letterSpacing: '0.1em' }}>FALLBACK PROXY</p>
                  <span style={{ fontSize: 9, color: 'var(--pp-text-3)' }}>auto-used when primary fails</span>
                </div>
                <p style={{ margin: '0 0 12px', fontSize: 11, color: 'var(--pp-text-3)', lineHeight: 1.5 }}>
                  If the primary proxy hits a CloudFront 403, 429 rate-limit, or connection timeout, the Bybit call auto-retries through this IP. Recommend a DIFFERENT country than primary (e.g. 🇳🇱 Netherlands if primary is 🇩🇪 Germany) so geo-blocks affecting one won't affect the other.
                </p>
                {bybitProxy?.fallback && (
                  <div style={{ background: 'rgba(129,129,255,0.08)', border: '1px solid rgba(129,129,255,0.3)', padding: 10, marginBottom: 14 }}>
                    <p style={{ margin: 0, fontSize: 10, color: '#8181FF', fontWeight: 700, letterSpacing: '0.1em' }}>ACTIVE FALLBACK PROXY</p>
                    <p className="mono" style={{ margin: '4px 0 0', fontSize: 12, fontWeight: 700 }}>{bybitProxy.fallback.label || `${bybitProxy.fallback.host}:${bybitProxy.fallback.port}`}</p>
                    <p className="mono" style={{ margin: '2px 0 0', fontSize: 10, color: 'var(--pp-text-3)' }}>{bybitProxy.fallback.host}:{bybitProxy.fallback.port} · user={bybitProxy.fallback.username || '—'}</p>
                  </div>
                )}
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10 }}>
                  <div style={{ gridColumn: '1 / -1' }}><label style={LABEL}>Proxy Label</label><input style={INPUT} value={bybitFallbackForm.label} onChange={e => setBybitFallbackForm(p => ({ ...p, label: e.target.value }))} placeholder="Amsterdam NL · IPRoyal ISP" /></div>
                  <div><label style={LABEL}>Host</label><input style={INPUT} value={bybitFallbackForm.host} onChange={e => setBybitFallbackForm(p => ({ ...p, host: e.target.value }))} placeholder="geo.iproyal.com" /></div>
                  <div><label style={LABEL}>Port</label><input style={INPUT} type="number" value={bybitFallbackForm.port} onChange={e => setBybitFallbackForm(p => ({ ...p, port: parseInt(e.target.value) || 12321 }))} /></div>
                  <div><label style={LABEL}>Username</label><input style={INPUT} value={bybitFallbackForm.username} onChange={e => setBybitFallbackForm(p => ({ ...p, username: e.target.value }))} placeholder="iproyal_xxx" /></div>
                  <div><label style={LABEL}>Password</label><input style={INPUT} type="password" value={bybitFallbackForm.password} onChange={e => setBybitFallbackForm(p => ({ ...p, password: e.target.value }))} placeholder="••••••••" /></div>
                </div>
                <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
                  <button
                    data-testid="bybit-fallback-save"
                    disabled={savingBybitFallback || !bybitFallbackForm.host}
                    onClick={async () => {
                      setSavingBybitFallback(true);
                      try {
                        const r = await api.adminBybitProxySetFallback(bybitFallbackForm);
                        toast.success('Saved — Bybit fallback proxy active');
                        setBybitProxy({ ...bybitProxy, fallback: r.fallback });
                        setBybitFallbackForm(p => ({ ...p, password: '' }));
                      } catch (e) { toast.error(e.message); }
                      setSavingBybitFallback(false);
                    }}
                    style={{ flex: 2, height: 42, background: '#8181FF', color: '#0A0A14', fontWeight: 700, letterSpacing: '0.1em', cursor: 'pointer', border: 'none' }}
                  >{savingBybitFallback ? 'SAVING…' : 'SAVE FALLBACK'}</button>
                  <button
                    onClick={async () => {
                      setBybitFallbackTest({ testing: true });
                      try {
                        const r = await api.adminBybitProxyTest('fallback');
                        setBybitFallbackTest(r);
                        if (r.ok) toast.success(`Fallback OK — Bybit reachable via ${r.proxy}`);
                        else toast.error(`Fallback test: ${r.error || r.http || 'unknown'}`);
                      } catch (e) { toast.error(e.message); setBybitFallbackTest({ ok: false, error: e.message }); }
                    }}
                    style={{ flex: 1, height: 42, background: 'transparent', border: '1px solid var(--pp-border)', color: 'var(--pp-text)', fontWeight: 700, letterSpacing: '0.1em', cursor: 'pointer' }}
                  >TEST</button>
                  {bybitProxy?.fallback && (
                    <button
                      onClick={async () => {
                        if (!window.confirm('Remove the fallback proxy?')) return;
                        try { await api.adminBybitProxyDelFallback(); toast.success('Fallback removed'); setBybitProxy({ ...bybitProxy, fallback: null }); setBybitFallbackForm({ host:'', port:12321, username:'', password:'', label:'' }); } catch(e) { toast.error(e.message); }
                      }}
                      style={{ flex: 'none', width: 42, height: 42, background: 'transparent', border: '1px solid var(--pp-error)', color: 'var(--pp-error)', cursor: 'pointer' }}
                    >×</button>
                  )}
                </div>
                {bybitFallbackTest && !bybitFallbackTest.testing && (
                  <div style={{ marginTop: 10, padding: 8, background: bybitFallbackTest.ok ? 'rgba(0,255,148,0.08)' : 'rgba(255,51,51,0.08)', border: `1px solid ${bybitFallbackTest.ok ? 'rgba(0,255,148,0.3)' : 'rgba(255,51,51,0.3)'}`, fontSize: 11, color: bybitFallbackTest.ok ? '#00FF94' : '#FF6B6B' }}>
                    {bybitFallbackTest.ok
                      ? `✓ Fallback reached via ${bybitFallbackTest.proxy}`
                      : `✗ ${bybitFallbackTest.error || `HTTP ${bybitFallbackTest.http}`}`}
                  </div>
                )}
              </div>
            </div>

            {/* Prop Firm Risk Rules */}
            <p className="section-label" style={{ marginBottom: 8, marginTop: 4 }}>Prop Firm Risk Rules</p>
            <p style={{ fontSize: 12, color: 'var(--pp-text-3)', marginBottom: 12, lineHeight: 1.6 }}>
              When a strategy exceeds either threshold below, prop firm accounts receive a reduced default trade size.
              Per-strategy overrides (set in Strategies tab) take priority over these global rules.
            </p>
            <div style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: 16, marginBottom: 20 }}>
              {/* Master enable toggle */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: 14, borderBottom: '1px solid var(--pp-border)', marginBottom: 14 }}>
                <div>
                  <p style={{ margin: 0, fontWeight: 600, fontSize: 14 }}>Prop Firm Safety Mode</p>
                  <p style={{ margin: '2px 0 0', fontSize: 12, color: 'var(--pp-text-3)' }}>Enable or disable the entire prop firm sizing system</p>
                </div>
                <button onClick={() => setPropFirmRules(p => ({ ...p, enabled: !p.enabled }))} style={{ width: 36, height: 20, background: propFirmRules.enabled ? 'rgba(41,121,255,0.3)' : 'var(--pp-border)', border: `1px solid ${propFirmRules.enabled ? 'var(--pp-accent)' : 'var(--pp-border-active)'}`, borderRadius: 10, cursor: 'pointer', position: 'relative', display: 'flex', alignItems: 'center', padding: '0 3px' }}>
                  <div style={{ width: 14, height: 14, borderRadius: '50%', background: propFirmRules.enabled ? 'var(--pp-accent)' : 'var(--pp-text-3)', transition: 'transform 150ms', transform: `translateX(${propFirmRules.enabled ? 16 : 0}px)` }} />
                </button>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                <div>
                  <label style={LABEL}>Min Return % trigger</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input style={{ ...INPUT, flex: 1 }} type="number" value={propFirmRules.min_return_pct || ''} onChange={e => setPropFirmRules(p => ({ ...p, min_return_pct: e.target.value }))} placeholder="500" />
                    <span style={{ fontSize: 12, color: 'var(--pp-text-3)', flexShrink: 0 }}>%</span>
                  </div>
                  <p style={{ margin: '4px 0 0', fontSize: 10, color: 'var(--pp-text-3)' }}>Strategies above this return trigger prop safety</p>
                </div>
                <div>
                  <label style={LABEL}>Max Drawdown % trigger</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input style={{ ...INPUT, flex: 1 }} type="number" value={propFirmRules.max_drawdown_pct || ''} onChange={e => setPropFirmRules(p => ({ ...p, max_drawdown_pct: e.target.value }))} placeholder="25" />
                    <span style={{ fontSize: 12, color: 'var(--pp-text-3)', flexShrink: 0 }}>%</span>
                  </div>
                  <p style={{ margin: '4px 0 0', fontSize: 10, color: 'var(--pp-text-3)' }}>Strategies above this drawdown trigger prop safety</p>
                </div>
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={LABEL}>Default Prop Firm Factor</label>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {[['5%', 0.05], ['10%', 0.10], ['15%', 0.15], ['20%', 0.20], ['25%', 0.25], ['50%', 0.50]].map(([label, val]) => (
                    <button key={val} onClick={() => setPropFirmRules(p => ({ ...p, default_factor: val }))} style={{ padding: '6px 14px', background: parseFloat(propFirmRules.default_factor) === val ? 'var(--pp-primary)' : 'transparent', border: `1px solid ${parseFloat(propFirmRules.default_factor) === val ? 'var(--pp-primary)' : 'var(--pp-border)'}`, color: parseFloat(propFirmRules.default_factor) === val ? '#fff' : 'var(--pp-text-2)', fontSize: 13, cursor: 'pointer', fontWeight: parseFloat(propFirmRules.default_factor) === val ? 700 : 400 }}>{label}</button>
                  ))}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1, minWidth: 120 }}>
                    <input style={{ ...INPUT, width: 80 }} type="number" step="0.01" min="0.01" max="1" value={parseFloat(propFirmRules.default_factor) || ''} onChange={e => setPropFirmRules(p => ({ ...p, default_factor: parseFloat(e.target.value) || 0.10 }))} placeholder="0.10" />
                    <span style={{ fontSize: 12, color: 'var(--pp-text-3)' }}>custom</span>
                  </div>
                </div>
                <p style={{ margin: '6px 0 0', fontSize: 11, color: 'var(--pp-text-3)' }}>
                  Current: prop firm accounts get <strong style={{ color: 'var(--pp-warning)' }}>{(parseFloat(propFirmRules.default_factor) * 100).toFixed(0)}%</strong> of master trade size (before their risk multiplier)
                </p>
              </div>

              <button onClick={handleSavePropFirmRules} disabled={savingPropRules} className="btn-primary" style={{ width: '100%', height: 48 }}>
                {savingPropRules ? 'SAVING...' : 'SAVE PROP FIRM RULES'}
              </button>
            </div>

            {/* API Keys */}
            <p className="section-label" style={{ marginBottom: 8, marginTop: 8 }}>API Keys</p>
            <p style={{ fontSize: 12, color: 'var(--pp-text-3)', marginBottom: 14, lineHeight: 1.6 }}>
              Keys are stored encrypted in the database. Changes take effect immediately — no restart needed.
            </p>
            <div style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', marginBottom: 20 }}>
              {apiKeys.map((k) => (
                <div key={k.key_name} style={{ padding: 14, borderBottom: '1px solid var(--pp-border)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                        <p style={{ margin: 0, fontWeight: 700, fontSize: 13 }}>{k.label}</p>
                        {k.is_set
                          ? <span className="badge-success" style={{ fontSize: 8 }}>SET</span>
                          : <span className="badge-error" style={{ fontSize: 8 }}>NOT SET</span>
                        }
                      </div>
                      <p style={{ margin: 0, fontSize: 11, color: 'var(--pp-text-3)' }}>{k.hint}</p>
                      {k.is_set && !showKeyInput[k.key_name] && (
                        <p className="mono" style={{ margin: '6px 0 0', fontSize: 11, color: 'var(--pp-text-3)', letterSpacing: '0.05em' }}>{k.masked}</p>
                      )}
                    </div>
                    <button
                      onClick={() => setShowKeyInput(prev => ({ ...prev, [k.key_name]: !prev[k.key_name] }))}
                      style={{ flexShrink: 0, background: 'none', border: '1px solid var(--pp-border)', color: 'var(--pp-text-2)', padding: '5px 12px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}
                    >
                      {showKeyInput[k.key_name] ? 'Cancel' : k.is_set ? 'Update' : 'Add Key'}
                    </button>
                  </div>
                  {showKeyInput[k.key_name] && (
                    <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
                      <input
                        style={{ ...INPUT, flex: 1, fontFamily: 'monospace', fontSize: 12 }}
                        type="text"
                        placeholder={`Paste ${k.label} here…`}
                        value={apiKeyValues[k.key_name] || ''}
                        onChange={e => setApiKeyValues(prev => ({ ...prev, [k.key_name]: e.target.value }))}
                        autoComplete="off"
                        spellCheck={false}
                      />
                      <button
                        onClick={() => handleSaveApiKey(k.key_name)}
                        disabled={savingKey[k.key_name]}
                        className="btn-primary"
                        style={{ height: 40, padding: '0 16px', fontSize: 12, flexShrink: 0 }}
                      >
                        {savingKey[k.key_name] ? '...' : 'Save'}
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Reset requests */}
            {resetRequests.length > 0 && (
              <>
                <p className="section-label" style={{ marginBottom: 10 }}>Pending Password Resets ({resetRequests.length})</p>
                {resetRequests.map((r, i) => (
                  <div key={i} style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: 12, marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <p style={{ margin: 0, fontSize: 13, fontWeight: 600 }}>{r.email}</p>
                      <p style={{ margin: '2px 0 0', fontSize: 11, color: 'var(--pp-text-3)' }}>Code: <span className="mono" style={{ color: 'var(--pp-warning)', fontWeight: 700, fontSize: 16 }}>{r.code}</span></p>
                    </div>
                    <p style={{ margin: 0, fontSize: 11, color: 'var(--pp-text-3)' }}>{r.created_at?.slice(0, 16)}</p>
                  </div>
                ))}
              </>
            )}
          </div>
        )}

        {/* WAITLIST */}
        {!loading && activeTab === 'waitlist' && (
          <div>
            <p className="section-label" style={{ marginBottom: 12 }}>Waitlist ({waitlist.length})</p>
            {waitlist.length === 0 ? (
              <div style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: 20, textAlign: 'center' }}>
                <p style={{ color: 'var(--pp-text-3)', margin: 0 }}>No waitlist entries</p>
              </div>
            ) : (
              waitlist.map(w => (
                <div key={w.waitlist_id} style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: 14, marginBottom: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <p style={{ margin: 0, fontWeight: 700, fontSize: 14 }}>{w.name}</p>
                      <p style={{ margin: '3px 0', fontSize: 12, color: 'var(--pp-text-3)' }}>{w.email} · {w.phone}</p>
                      {w.password && <p style={{ margin: '2px 0', fontSize: 11, color: 'var(--pp-text-2)' }}>Password: <span className="mono" style={{ color: 'var(--pp-accent)' }}>{w.password}</span></p>}
                      <p style={{ margin: 0, fontSize: 11, color: 'var(--pp-text-3)' }}>{w.created_at?.slice(0, 16)}</p>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                      <span className={w.status === 'approved' ? 'badge-success' : w.status === 'contacted' ? 'badge-accent' : 'badge-warning'}>{(w.status || 'PENDING').toUpperCase()}</span>
                      {w.entry_passcode && <span className="mono" style={{ fontSize: 11, color: 'var(--pp-success)', fontWeight: 700 }}>{w.entry_passcode}</span>}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                    {w.status !== 'approved' && (
                      <button data-testid={`approve-waitlist-${w.waitlist_id}`} onClick={async () => {
                        try {
                          const res = await api.approveWaitlist(w.waitlist_id);
                          toast.success(`Approved! Passcode ${res.entry_passcode} sent to ${res.sent_to}`);
                          loadTabData('waitlist');
                        } catch (e) { toast.error(e.message); }
                      }} style={{ padding: '6px 14px', background: 'var(--pp-success)', border: 'none', color: '#000', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>APPROVE & SEND PASSCODE</button>
                    )}
                    {w.status !== 'contacted' && w.status !== 'approved' && (
                      <button onClick={async () => { await api.updateAdminWaitlist(w.waitlist_id, { status: 'contacted' }).catch(() => {}); loadTabData('waitlist'); toast.success('Marked as contacted!'); }} style={{ padding: '6px 14px', background: 'transparent', border: '1px solid var(--pp-border)', color: 'var(--pp-accent)', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>MARK CONTACTED</button>
                    )}
                    <button
                      data-testid={`delete-waitlist-${w.waitlist_id}`}
                      onClick={async () => {
                        if (!window.confirm(`Delete waitlist entry for ${w.email}?`)) return;
                        try {
                          await api.deleteAdminWaitlist(w.waitlist_id);
                          toast.success('Waitlist entry deleted');
                          loadTabData('waitlist');
                        } catch (e) { toast.error(e.message); }
                      }}
                      style={{ padding: '6px 14px', background: 'transparent', border: '1px solid var(--pp-error)', color: 'var(--pp-error)', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
                    >DELETE</button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* QUOTES */}
        {!loading && activeTab === 'quotes' && (
          <div>
            <p className="section-label" style={{ marginBottom: 12 }}>Quote Requests ({quotes.length})</p>
            {quotes.map(q => (
              <div key={q.quote_id} style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: 14, marginBottom: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                  <div>
                    <p style={{ margin: 0, fontWeight: 700, fontSize: 14 }}>{q.user_name}</p>
                    <p style={{ margin: '3px 0', fontSize: 12, color: 'var(--pp-text-3)' }}>{q.user_email} · {q.user_phone}</p>
                    <p style={{ margin: 0, fontSize: 12, color: 'var(--pp-accent)' }}>Strategy: {q.strategy_name}</p>
                  </div>
                  <span className={q.status === 'contacted' ? 'badge-success' : q.status === 'converted' ? 'badge-accent' : 'badge-warning'}>{(q.status || 'NEW').toUpperCase()}</span>
                </div>
                {q.message && <p style={{ fontSize: 12, color: 'var(--pp-text-2)', margin: '8px 0 0', fontStyle: 'italic' }}>"{q.message}"</p>}
                {q.status === 'new' && (
                  <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                    {['contacted', 'converted', 'closed'].map(s => (
                      <button key={s} onClick={async () => { await api.updateAdminQuote(q.quote_id, { status: s }).catch(() => {}); loadTabData('quotes'); toast.success(`Marked as ${s}`); }} style={{ padding: '5px 12px', background: 'transparent', border: '1px solid var(--pp-border)', color: 'var(--pp-text-3)', fontSize: 10, fontWeight: 600, cursor: 'pointer', letterSpacing: '0.1em' }}>{s.toUpperCase()}</button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* METAAPI */}
        {!loading && activeTab === 'metaapi' && (
          <div>
            {/* Diagnostic strip */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10, marginBottom: 16 }}>
              <button
                data-testid="check-pw-health"
                onClick={async () => {
                  try {
                    const h = await api.getPlaywrightHealth();
                    const lines = `Playwright installed: ${h.playwright_installed}\nBrowser running: ${h.browser_running}\nActive contexts: ${h.active_contexts}\nReason: ${h.reason || '—'}`;
                    alert('Playwright Health\n\n' + lines);
                  } catch (e) { toast.error(e.message); }
                }}
                style={{ height: 56, background: 'rgba(41,121,255,0.1)', border: '1px solid var(--pp-accent)', color: 'var(--pp-accent)', fontWeight: 700, letterSpacing: '0.1em', fontSize: 12, cursor: 'pointer' }}
              >🎭 PLAYWRIGHT HEALTH</button>

              <button
                data-testid="check-metaapi-cloud"
                onClick={async () => {
                  toast.success('Querying MetaAPI cloud — may take 10-30s…');
                  try {
                    const r = await api.getMetaApiCloudAccounts();
                    if (r.error) {
                      alert(`MetaAPI cloud probe FAILED:\n\n${r.error}\n\nCode: ${r.code || '—'}`);
                      return;
                    }
                    const lines = (r.accounts || []).map(a =>
                      `• ${a.name || a._id || a.id}  [${a.state || '?'}]  login=${a.login}  server=${a.server}  ${a.linked_in_ppp_db ? '✓ linked' : '✗ unlinked'}`
                    ).join('\n');
                    alert(`MetaAPI Cloud — ${r.count} account(s) on ${r.base}\n\n${lines || '(none)'}`);
                  } catch (e) { toast.error(e.message); }
                }}
                style={{ height: 56, background: 'rgba(0,255,148,0.1)', border: '1px solid var(--pp-success)', color: 'var(--pp-success)', fontWeight: 700, letterSpacing: '0.1em', fontSize: 12, cursor: 'pointer' }}
              >🔍 INSPECT METAAPI CLOUD</button>

              <button
                data-testid="view-pw-screenshots"
                onClick={async () => {
                  try {
                    const r = await api.getPlaywrightScreenshots();
                    if (!r.files?.length) {
                      alert('No Playwright diagnostic screenshots yet.\nRun a "Test MT5 Login" from a student card to generate one.');
                      return;
                    }
                    const list = r.files.slice(0, 20).map(f => `${f.name}  (${(f.size/1024).toFixed(0)}kb)`).join('\n');
                    const open = window.confirm(`Latest Playwright screenshots:\n\n${list}\n\nClick OK to open the newest one.`);
                    if (open) window.open(r.files[0].url, '_blank');
                  } catch (e) { toast.error(e.message); }
                }}
                style={{ height: 56, background: 'rgba(255,176,0,0.1)', border: '1px solid #FFB000', color: '#FFB000', fontWeight: 700, letterSpacing: '0.1em', fontSize: 12, cursor: 'pointer' }}
              >📸 PLAYWRIGHT SCREENSHOTS</button>

              <button
                data-testid="auto-link-metaapi-accounts"
                onClick={async () => {
                  if (!window.confirm('Pull all MetaAPI cloud accounts and auto-link them to PPP students by login/name?\n\n• Brianna $200K → Brianna\n• Gyasa Chimara → Gyasa\n• Master accounts → admin role\n• Unmatched → marked as orphan')) return;
                  toast.success('Auto-linking cloud accounts… (~10s)');
                  try {
                    const r = await api.autoLinkMetaApiCloudAccounts();
                    if (r.error) { alert(`FAILED: ${r.error}`); return; }
                    const lines = [];
                    lines.push(`Fetched: ${r.fetched}`);
                    lines.push(`✅ Linked to students: ${r.linked_to_students.length}`);
                    r.linked_to_students.forEach(l => lines.push(`   • ${l.name} → user ${l.user_id?.slice(0,12)}`));
                    lines.push(`👑 Master accounts: ${r.linked_as_masters.length}`);
                    r.linked_as_masters.forEach(m => lines.push(`   • ${m.name}`));
                    lines.push(`⚠️ Orphans (no match): ${r.orphans.length}`);
                    r.orphans.forEach(o => lines.push(`   • ${o.name} login=${o.login}`));
                    lines.push(`⏭️ Skipped (already linked): ${r.skipped_already_linked.length}`);
                    alert(lines.join('\n'));
                    loadTabData('students');
                  } catch (e) { toast.error(e.message); }
                }}
                style={{ height: 56, background: 'rgba(0,255,148,0.1)', border: '1px solid var(--pp-success)', color: 'var(--pp-success)', fontWeight: 700, letterSpacing: '0.1em', fontSize: 12, cursor: 'pointer' }}
              >🔗 AUTO-LINK CLOUD ACCOUNTS</button>

              <button
                data-testid="seed-known-mt5-creds"
                onClick={async () => {
                  if (!window.confirm('Seed known MT5 credentials for Mathieu (40013695) and Gyasa (40013621), then auto-link them to MetaAPI Cloud?\n\nIdempotent — students already linked are skipped.')) return;
                  toast.success('Seeding credentials and deploying… (~30s)');
                  try {
                    const r = await api.seedKnownMt5Creds();
                    const lines = [];
                    if (r.token_warning) lines.push(`⚠ ${r.token_warning}\n`);
                    lines.push(`✅ Seeded: ${r.seeded.length}`);
                    r.seeded.forEach(s => lines.push(`   • ${s.email} (${s.label}) → ${s.auto_link.ok ? '✓ deployed' : '✗ ' + (s.auto_link.code || 'failed')}${s.auto_link.metaapi_account_id ? ' [' + s.auto_link.metaapi_account_id.slice(0, 8) + ']' : ''}`));
                    lines.push(`⏭️ Skipped (already complete): ${r.skipped.length}`);
                    r.skipped.forEach(s => lines.push(`   • ${s.email}`));
                    lines.push(`⚠️ Failed: ${r.failed.length}`);
                    r.failed.forEach(f => lines.push(`   • ${f.email}: ${f.reason}`));
                    alert(lines.join('\n'));
                    loadTabData('students');
                  } catch (e) { toast.error(e.message); }
                }}
                style={{ height: 56, background: 'rgba(41,121,255,0.1)', border: '1px solid var(--pp-accent)', color: 'var(--pp-accent)', fontWeight: 700, letterSpacing: '0.1em', fontSize: 12, cursor: 'pointer' }}
              >🌱 SEED KNOWN MT5 CREDS</button>

              <button
                data-testid="cleanup-and-refresh-metaapi"
                onClick={async () => {
                  if (!window.confirm('Run a full prod cleanup?\n\n• Deletes connections whose cloud account no longer exists\n• Removes mis-bound or duplicate connections (keeps the one bound to the right user)\n• Re-binds every cloud account to the correct PPP user via funded_accounts.mt5_login\n• Refreshes status to "active"\n\nIdempotent — safe to re-run.')) return;
                  toast.success('Cleaning + refreshing… (~10s)');
                  try {
                    const r = await api.cleanupAndRefreshMetaApi();
                    if (r.error) { alert(`FAILED: ${r.error}`); return; }
                    const lines = [`Summary: ${r.summary}\n`];
                    lines.push(`🗑 Stale (cloud gone): ${r.deleted_stale_no_cloud.length}`);
                    r.deleted_stale_no_cloud.forEach(x => lines.push(`   • ${x.connection_id} (was user ${x.user_id?.slice(0,12)})`));
                    lines.push(`\n🗑 Orphan/duplicate: ${r.deleted_orphans_or_dupes.length}`);
                    r.deleted_orphans_or_dupes.forEach(x => lines.push(`   • ${x.connection_id} (was bound to wrong user ${x.wrong_user_id?.slice(0,12)})`));
                    lines.push(`\n🔁 Rebound to correct user: ${r.rebound.length}`);
                    r.rebound.forEach(x => lines.push(`   • ${x.name || x.connection_id} → user ${x.user_id?.slice(0,12)}`));
                    lines.push(`\n♻ Refreshed: ${r.refreshed.length}`);
                    alert(lines.join('\n'));
                    loadTabData('students');
                  } catch (e) { toast.error(e.message); }
                }}
                style={{ height: 56, background: 'rgba(255,51,51,0.08)', border: '1px solid var(--pp-error)', color: 'var(--pp-error)', fontWeight: 700, letterSpacing: '0.1em', fontSize: 12, cursor: 'pointer' }}
              >🧹 CLEANUP + REFRESH</button>
            </div>

            <p className="section-label" style={{ marginBottom: 12 }}>Connect MetaAPI Master Account</p>
            <div style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: 16, marginBottom: 20 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                {[
                  { k: 'login', l: 'MT5 Login' },
                  { k: 'password', l: 'MT5 Password', t: 'password' },
                  { k: 'server', l: 'Server Name' },
                  { k: 'account_label', l: 'Label (optional)' },
                ].map(f => (
                  <div key={f.k}>
                    <label style={LABEL}>{f.l}</label>
                    <input style={INPUT} type={f.t || 'text'} value={mapiForm[f.k] || ''} onChange={e => setMapiForm(p => ({ ...p, [f.k]: e.target.value }))} />
                  </div>
                ))}
                <div>
                  <label style={LABEL}>Platform</label>
                  <select style={{ ...INPUT, cursor: 'pointer' }} value={mapiForm.platform} onChange={e => setMapiForm(p => ({ ...p, platform: e.target.value }))}>
                    <option value="mt5">MT5</option>
                    <option value="mt4">MT4</option>
                  </select>
                </div>
                <div>
                  <label style={LABEL}>Strategy</label>
                  <select style={{ ...INPUT, cursor: 'pointer' }} value={mapiForm.strategy_id} onChange={e => setMapiForm(p => ({ ...p, strategy_id: e.target.value }))}>
                    <option value="">Select strategy...</option>
                    {strategies.map(s => <option key={s.strategy_id} value={s.strategy_id}>{s.name}</option>)}
                  </select>
                </div>
              </div>
              <button onClick={handleConnectMetaAPIMaster} disabled={connectingMapi} className="btn-primary" style={{ width: '100%', height: 48, marginTop: 14 }}>
                {connectingMapi ? 'CONNECTING...' : 'CONNECT MASTER ACCOUNT'}
              </button>
              <p style={{ fontSize: 11, color: 'var(--pp-text-3)', marginTop: 8 }}>Requires METAAPI_TOKEN in backend .env</p>
            </div>

            <p className="section-label" style={{ marginBottom: 12 }}>MetaAPI Master Accounts ({metaapiMasters.length})</p>
            {metaapiMasters.map(m => (
              <div key={m.connection_id} style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: 14, marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <p style={{ margin: 0, fontWeight: 700, fontSize: 14 }}>{m.account_label}</p>
                  <p style={{ margin: '3px 0 0', fontSize: 11, color: 'var(--pp-text-3)' }}>Login: {m.login} · {m.server}</p>
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <span className={m.status === 'connected' ? 'badge-success' : 'badge-warning'}>{(m.status || 'deploying').toUpperCase()}</span>
                  <button onClick={async () => { await api.deleteMetaAPIMaster(m.connection_id).catch(() => {}); loadTabData('metaapi'); toast.success('Master removed'); }} style={{ background: 'none', border: '1px solid rgba(255,51,51,0.3)', color: 'var(--pp-error)', padding: '5px 8px', cursor: 'pointer' }}><Trash2 size={13} /></button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ACTIVE STUDENTS */}
        {!loading && activeTab === 'students' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <p className="section-label" style={{ margin: 0 }}>ACTIVE STUDENTS ({activeStudents.length})</p>
              <button data-testid="add-student-btn" onClick={() => { setShowCreateStudent(true); setNewStudent({ name: '', email: '', password: '', is_demo: false, generate_passcode: true, send_passcode_in_email: true, send_welcome_email: true, email_schedule: 'immediate', scheduled_at: '', strategy_ids: [], funded_account: null, proxy: null }); }} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'var(--pp-primary)', border: 'none', color: '#fff', padding: '8px 14px', fontSize: 11, fontWeight: 700, cursor: 'pointer', letterSpacing: '0.05em' }}>+ ADD STUDENT</button>
            </div>
            {activeStudents.length === 0 ? (
              <div style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: 24, textAlign: 'center' }}><p style={{ color: 'var(--pp-text-3)', margin: 0, fontSize: 13 }}>No active students yet. Generate passcodes from the Verification tab.</p></div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {activeStudents.map(s => (
                  <div key={s.user_id} data-testid={`student-card-${s.user_id}`} style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: 16 }}>
                    {/* Header */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                      <div>
                        <p style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>{s.name}</p>
                        <p style={{ margin: '2px 0 0', fontSize: 11, color: 'var(--pp-text-3)' }}>{s.email}</p>
                        {s.password_plain && (
                          <span
                            data-testid={`pwd-reveal-${s.user_id}`}
                            onClick={e => { e.currentTarget.textContent = e.currentTarget.textContent === '••••••••' ? s.password_plain : '••••••••'; }}
                            style={{ cursor: 'pointer', fontFamily: 'monospace', fontSize: 11, color: '#666', marginTop: 2, display: 'inline-block' }}
                          >••••••••</span>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        {s.is_verified && <span style={{ fontSize: 9, background: 'rgba(0,255,148,0.15)', border: '1px solid rgba(0,255,148,0.3)', color: 'var(--pp-success)', padding: '3px 8px', fontWeight: 700, letterSpacing: '0.1em' }}>KYC VERIFIED</span>}
                        {s.is_active_student && <span style={{ fontSize: 9, background: 'rgba(41,121,255,0.15)', border: '1px solid rgba(41,121,255,0.3)', color: 'var(--pp-accent)', padding: '3px 8px', fontWeight: 700, letterSpacing: '0.1em' }}>ACTIVE</span>}
                        {s.tier === 'pending_activation' && (
                          <span data-testid={`pending-activation-${s.user_id}`} style={{ fontSize: 9, background: 'rgba(255,176,0,0.15)', border: '1px solid rgba(255,176,0,0.45)', color: '#FFB000', padding: '3px 8px', fontWeight: 700, letterSpacing: '0.1em' }}>PENDING ACTIVATION</span>
                        )}
                        {s.is_demo && (
                          <span style={{ fontSize: 9, background: 'rgba(120,120,120,0.15)', border: '1px solid rgba(120,120,120,0.45)', color: '#999', padding: '3px 8px', fontWeight: 700, letterSpacing: '0.1em' }}>DEMO</span>
                        )}
                        {s.engine_summary && (() => {
                          const eng = s.engine_summary.engine || 'none';
                          const isNone = eng === 'none' || s.engine_summary.connections === 0;
                          const isMeta = eng === 'metaapi';
                          const isPw   = eng === 'playwright_pending' || eng === 'playwright';
                          const bg = isNone ? 'rgba(120,120,120,0.12)' : isMeta ? 'rgba(0,255,148,0.15)' : isPw ? 'rgba(255,176,0,0.15)' : 'rgba(41,121,255,0.15)';
                          const fg = isNone ? '#888' : isMeta ? 'var(--pp-success)' : isPw ? '#FFB000' : 'var(--pp-accent)';
                          const br = isNone ? 'rgba(120,120,120,0.35)' : isMeta ? 'rgba(0,255,148,0.4)' : isPw ? 'rgba(255,176,0,0.4)' : 'rgba(41,121,255,0.4)';
                          const label = isNone ? 'NO BROKER' : eng.toUpperCase().replace('_','-');
                          return (
                            <span
                              data-testid={`student-engine-${s.user_id}`}
                              title={s.engine_summary.last_error || s.engine_summary.status || ''}
                              style={{ fontSize: 9, padding: '3px 8px', fontWeight: 700, letterSpacing: '0.1em', background: bg, color: fg, border: '1px solid ' + br }}>
                              {label}
                              {s.engine_summary.flagged_count > 0 && ` · ⚠${s.engine_summary.flagged_count}`}
                            </span>
                          );
                        })()}
                        <button
                          data-testid={`toggle-paid-${s.user_id}`}
                          onClick={async () => {
                            const next = !(s.is_active_student || s.is_paid);
                            try {
                              await api.adminSetPaidStatus(s.user_id, next);
                              toast.success(next ? `${s.name} marked FULLY PAID` : `${s.name} access revoked`);
                              loadTabData('students');
                            } catch (e) { toast.error(e.message); }
                          }}
                          style={{
                            fontSize: 9,
                            background: (s.is_active_student || s.is_paid) ? 'rgba(0,255,148,0.15)' : 'transparent',
                            border: (s.is_active_student || s.is_paid) ? '1px solid rgba(0,255,148,0.5)' : '1px solid var(--pp-border)',
                            color: (s.is_active_student || s.is_paid) ? 'var(--pp-success)' : 'var(--pp-text-3)',
                            padding: '4px 10px',
                            fontWeight: 700,
                            letterSpacing: '0.1em',
                            cursor: 'pointer',
                          }}
                          title={(s.is_active_student || s.is_paid) ? 'Click to revoke paid access (requires payment to reactivate)' : 'Click to mark student as fully paid and bypass checkout'}
                        >
                          {(s.is_active_student || s.is_paid) ? '✓ FULLY PAID' : 'MARK FULLY PAID'}
                        </button>
                      </div>
                    </div>
                    {/* Info Grid */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
                      {/* Passcode */}
                      <div style={{ background: 'var(--pp-overlay)', padding: 10, border: '1px solid var(--pp-border)' }}>
                        <p style={{ margin: 0, fontSize: 9, color: 'var(--pp-text-3)', letterSpacing: '0.1em', fontWeight: 600, marginBottom: 4 }}>PASSCODE</p>
                        {s.passcode ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <span className="mono" style={{ fontSize: 14, fontWeight: 700, color: 'var(--pp-success)' }}>{s.passcode}</span>
                            {s.passcode_used && <span style={{ fontSize: 8, background: 'rgba(255,176,0,0.15)', color: '#FFB000', padding: '2px 6px', fontWeight: 700 }}>USED</span>}
                            <button onClick={async () => { try { await api.sendPasscode({ user_id: s.user_id }); toast.success(`Sent to ${s.email}`); } catch (e) { toast.error(e.message); } }} style={{ background: 'none', border: '1px solid rgba(0,255,148,0.3)', color: 'var(--pp-success)', fontSize: 9, padding: '3px 8px', cursor: 'pointer', fontWeight: 700 }}>SEND</button>
                          </div>
                        ) : (
                          <button onClick={async () => { try { const r = await api.generatePasscode({ user_id: s.user_id, send_email: false }); toast.success(`Generated: ${r.passcode}`); loadTabData('students'); } catch (e) { toast.error(e.message); } }} style={{ background: 'rgba(41,121,255,0.12)', border: '1px solid rgba(41,121,255,0.3)', color: 'var(--pp-accent)', fontSize: 10, padding: '4px 10px', cursor: 'pointer', fontWeight: 700 }}>GENERATE</button>
                        )}
                      </div>
                      {/* Proxy */}
                      <div style={{ background: 'var(--pp-overlay)', padding: 10, border: '1px solid var(--pp-border)' }}>
                        <p style={{ margin: 0, fontSize: 9, color: 'var(--pp-text-3)', letterSpacing: '0.1em', fontWeight: 600, marginBottom: 4 }}>PROXY</p>
                        {s.proxy ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--pp-success)' }}>{s.proxy.label}</span>
                            <span style={{ color: 'var(--pp-text-3)', fontSize: 10, fontFamily: 'monospace' }}>{s.proxy.host}:{s.proxy.port}</span>
                            <button data-testid={`edit-proxy-card-${s.user_id}`} onClick={() => { setShowProxyModal(s.user_id); setProxyForm({ proxy_host: s.proxy.host || '', proxy_port: s.proxy.port || 12323, proxy_username: '', proxy_password: '', proxy_label: s.proxy.label || '' }); }} style={{ background: 'rgba(41,121,255,0.12)', border: '1px solid rgba(41,121,255,0.3)', color: 'var(--pp-accent)', fontSize: 9, padding: '3px 8px', cursor: 'pointer', fontWeight: 700, letterSpacing: '0.1em' }}>EDIT</button>
                          </div>
                        ) : (
                          <button data-testid={`assign-proxy-card-${s.user_id}`} onClick={() => { setShowProxyModal(s.user_id); setProxyForm({ proxy_host: '', proxy_port: 12323, proxy_username: '', proxy_password: '', proxy_label: '' }); }} style={{ background: 'none', border: '1px solid var(--pp-border)', color: 'var(--pp-accent)', fontSize: 10, padding: '4px 10px', cursor: 'pointer', fontWeight: 600 }}>ASSIGN PROXY</button>
                        )}
                      </div>
                      {/* Funded Account */}
                      <div style={{ background: 'var(--pp-overlay)', padding: 10, border: '1px solid var(--pp-border)' }}>
                        <p style={{ margin: 0, fontSize: 9, color: 'var(--pp-text-3)', letterSpacing: '0.1em', fontWeight: 600, marginBottom: 4 }}>FUNDED ACCOUNT</p>
                        {s.funded_account ? (
                          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
                            <span style={{ fontSize: 12, fontWeight: 600, color: s.funded_account.status === 'active' ? 'var(--pp-success)' : '#FFB000' }}>{s.funded_account.status?.toUpperCase()}</span>
                            {s.funded_account.size > 0 && <span className="mono" style={{ fontSize: 11, color: 'var(--pp-text-2)' }}>${Number(s.funded_account.size).toLocaleString()}</span>}
                            {s.funded_account.mt5_login && <span className="mono" style={{ fontSize: 11, color: 'var(--pp-text-2)' }}>MT5: {s.funded_account.mt5_login}</span>}
                            <button data-testid={`add-more-funded-${s.user_id}`} onClick={() => { setAddFundedFor(s); setAddFundedForm({ size: 100000, account_type: 'instant', mt5_login: '', mt5_server: '', broker: '', status: 'pending_creation', daily_drawdown_limit_pct: 4, max_drawdown_limit_pct: 8, min_profit_target_pct: 9, consistency_pct: 15, notes: '', email_credentials_to_student: false }); }} style={{ background: 'rgba(41,121,255,0.12)', border: '1px solid rgba(41,121,255,0.3)', color: 'var(--pp-accent)', fontSize: 9, padding: '3px 8px', cursor: 'pointer', fontWeight: 700, letterSpacing: '0.1em' }}>+ ADD</button>
                            <button data-testid={`add-native-funded-${s.user_id}`}
                              onClick={async () => {
                                setAddNativeFor(s);
                                setAddNativeForm({ size: 100000, asset_class: 'futures', account_type: 'instant', min_profit_target_pct: 6, max_drawdown_limit_pct: 10, daily_drawdown_limit_pct: 5, consistency_pct: 15, backend_engine_kind: 'none', backend_engine_id: '', backend_engine_mode: 'fire_only', backfill: true });
                                try {
                                  const accts = await api.getAdminBrokerAccounts();
                                  setNativeEngineConns(accts || []);
                                } catch (e) { setNativeEngineConns([]); }
                              }}
                              style={{ background: 'rgba(0,255,148,0.12)', border: '1px solid rgba(0,255,148,0.3)', color: '#00FF94', fontSize: 9, padding: '3px 8px', cursor: 'pointer', fontWeight: 700, letterSpacing: '0.1em' }}>+ NATIVE</button>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button data-testid={`assign-funded-${s.user_id}`} onClick={() => { setAddFundedFor(s); setAddFundedForm({ size: 100000, account_type: 'instant', mt5_login: '', mt5_server: '', broker: '', status: 'pending_creation', daily_drawdown_limit_pct: 4, max_drawdown_limit_pct: 8, min_profit_target_pct: 9, consistency_pct: 15, notes: '', email_credentials_to_student: false }); }} style={{ background: 'none', border: '1px solid var(--pp-border)', color: 'var(--pp-accent)', fontSize: 10, padding: '4px 10px', cursor: 'pointer', fontWeight: 600 }}>ASSIGN FUNDED ACCOUNT</button>
                            <button data-testid={`assign-native-${s.user_id}`}
                              onClick={async () => {
                                setAddNativeFor(s);
                                setAddNativeForm({ size: 100000, asset_class: 'futures', account_type: 'instant', min_profit_target_pct: 6, max_drawdown_limit_pct: 10, daily_drawdown_limit_pct: 5, consistency_pct: 15, backend_engine_kind: 'none', backend_engine_id: '', backend_engine_mode: 'fire_only', backfill: true });
                                try {
                                  const accts = await api.getAdminBrokerAccounts();
                                  setNativeEngineConns(accts || []);
                                } catch (e) { setNativeEngineConns([]); }
                              }}
                              style={{ background: 'rgba(0,255,148,0.08)', border: '1px solid rgba(0,255,148,0.3)', color: '#00FF94', fontSize: 10, padding: '4px 10px', cursor: 'pointer', fontWeight: 700 }}>+ NATIVE</button>
                          </div>
                        )}
                      </div>
                      {/* Strategies */}
                      <div style={{ background: 'var(--pp-overlay)', padding: 10, border: '1px solid var(--pp-border)' }}>
                        <p style={{ margin: 0, fontSize: 9, color: 'var(--pp-text-3)', letterSpacing: '0.1em', fontWeight: 600, marginBottom: 4 }}>STRATEGIES ({s.subscriptions?.length || 0})</p>
                        {s.subscriptions?.length > 0 ? (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 4 }}>
                            {s.subscriptions.map((sub, si) => (
                              <span key={si} style={{ fontSize: 10, background: 'rgba(41,121,255,0.12)', color: 'var(--pp-accent)', padding: '2px 6px', fontWeight: 600 }}>{sub.strategy_name}</span>
                            ))}
                          </div>
                        ) : (
                          <span style={{ fontSize: 11, color: 'var(--pp-text-3)', display: 'block', marginBottom: 4 }}>None subscribed</span>
                        )}
                        <button data-testid={`subscribe-strats-${s.user_id}`} onClick={() => setSubscribeFor(s)} style={{ background: 'rgba(41,121,255,0.12)', border: '1px solid rgba(41,121,255,0.3)', color: 'var(--pp-accent)', fontSize: 9, padding: '3px 8px', cursor: 'pointer', fontWeight: 700, letterSpacing: '0.1em' }}>MANAGE</button>
                      </div>
                      {/* Brokers */}
                      <div style={{ background: 'var(--pp-overlay)', padding: 10, border: '1px solid var(--pp-border)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                          <p style={{ margin: 0, fontSize: 9, color: 'var(--pp-text-3)', letterSpacing: '0.1em', fontWeight: 600 }}>BROKER CONNECTIONS ({s.broker_connections?.length || 0})</p>
                          <button
                            data-testid={`add-bybit-${s.user_id}`}
                            onClick={async () => {
                              setAddBybitFor(s);
                              setAddBybitForm({ api_key: '', api_secret: '', market_type: 'linear', account_label: `Bybit — ${s.name || ''}`, sizing_mode: 'dollar_match', fixed_qty: 0 });
                              try { const conns = await api.adminBybitList(s.user_id); setBybitConnsByUser(prev => ({ ...prev, [s.user_id]: conns || [] })); } catch {}
                            }}
                            style={{ background: 'rgba(247,166,0,0.12)', border: '1px solid rgba(247,166,0,0.4)', color: '#F7A600', fontSize: 9, padding: '3px 8px', cursor: 'pointer', fontWeight: 700, letterSpacing: '0.1em' }}
                          >+ BYBIT</button>
                        </div>
                        {s.broker_connections?.length > 0 ? (
                          s.broker_connections.map((b, bi) => (
                            <div key={bi} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '4px 0', borderBottom: bi < s.broker_connections.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none' }}>
                              <p className="mono" style={{ margin: 0, fontSize: 11, color: 'var(--pp-text-2)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.label || `#${b.login}`} · {b.server}</p>
                              {b.connection_id && (
                                <>
                                  <button
                                    data-testid={`student-test-pw-mt5-${b.connection_id}`}
                                    title="Test MT5 web-terminal login via Playwright (uses assigned proxy)"
                                    onClick={async () => {
                                      toast.success('Running Playwright MT5 login test… (~30s)');
                                      try {
                                        const r = await api.testPlaywrightMT5Login(b.connection_id);
                                        let msg = `Result: ${r.ok ? '✅ OK' : '❌ FAIL'}\nCode: ${r.code || '—'}\nDetail: ${r.detail || '—'}`;
                                        if (r.code === 'metaquotes_canvas_terminal_not_automatable') {
                                          msg = '⚠️ MetaQuotes redesigned the MT5 web terminal as a WebAssembly/canvas app (Feb 2026).\n\n' +
                                                'Playwright DOM automation is NO LONGER possible against the new terminal — this is a platform-level change by MetaQuotes, not a Profit Pulse bug.\n\n' +
                                                '✅ How to restore this account:\n' +
                                                '  1. Top up MetaAPI Cloud credits (instant, ~$25/account/month), OR\n' +
                                                '  2. Issue this account as a Funded Account from the admin side — MetaAPI provisions it on your remaining credits. When the student links the same login+server from their side, the dedup feature will reuse the same MetaAPI cloud account (zero extra credits).';
                                        }
                                        if (r.screenshot) {
                                          msg += `\n\nScreenshot: /api/admin/playwright/screenshot/${r.screenshot.split('/').pop()}`;
                                          if (window.confirm(msg + '\n\nOpen screenshot?')) {
                                            const fname = r.screenshot.split('/').pop();
                                            window.open(`${window.location.origin}/api/admin/playwright/screenshot/${fname}`, '_blank');
                                          }
                                        } else {
                                          alert(msg);
                                        }
                                      } catch (err) { toast.error(err.message); }
                                    }}
                                    style={{ background: 'rgba(255,176,0,0.12)', border: '1px solid rgba(255,176,0,0.4)', color: '#FFB000', padding: '2px 8px', fontSize: 9, cursor: 'pointer', fontWeight: 700, letterSpacing: '0.05em' }}
                                  >🎭 PW</button>
                                  <button
                                    data-testid={`student-reveal-broker-pw-${b.connection_id}`}
                                    onClick={async (e) => {
                                      const btn = e.currentTarget;
                                      if (btn.dataset.revealed === '1') {
                                        btn.textContent = '🔒';
                                        btn.dataset.revealed = '0';
                                        btn.style.color = 'var(--pp-text-3)';
                                        return;
                                      }
                                      try {
                                        const r = await api.revealBrokerPassword(b.connection_id);
                                        btn.textContent = r.password || '(empty)';
                                        btn.dataset.revealed = '1';
                                        btn.style.color = 'var(--pp-warning)';
                                      } catch (err) { toast.error(err.message); }
                                    }}
                                    style={{ background: 'none', border: '1px solid var(--pp-border)', color: 'var(--pp-text-3)', padding: '2px 6px', fontSize: 9, fontFamily: 'monospace', cursor: 'pointer', fontWeight: 700 }}
                                  >🔒</button>
                                  {b.platform_kind === 'tradelocker' && (
                                    <button
                                      data-testid={`backfill-tl-${b.connection_id}`}
                                      title="Backfill all historical closed trades from TradeLocker (up to 2 years)."
                                      onClick={async (e) => {
                                        const daysStr = window.prompt(
                                          `Backfill closed trades for "${b.label || b.login}".\n\n` +
                                          `How many days back? (1-730, default 365)`,
                                          '365'
                                        );
                                        if (!daysStr) return;
                                        const days = Math.max(1, Math.min(730, parseInt(daysStr, 10) || 365));
                                        const btn = e.currentTarget;
                                        const old = btn.textContent;
                                        btn.disabled = true;
                                        btn.textContent = '⏳';
                                        try {
                                          await api.backfillTradeLockerClosedTrades(b.connection_id, days);
                                          toast.success(`Backfill queued (${days}d). Reload trade history in 30-90s.`);
                                        } catch (err) { toast.error(err.message); }
                                        btn.textContent = old;
                                        btn.disabled = false;
                                      }}
                                      style={{ background: 'rgba(0,255,148,0.08)', border: '1px solid #00FF94', color: '#00FF94', padding: '2px 7px', fontSize: 9, cursor: 'pointer', fontWeight: 800, letterSpacing: '0.05em' }}
                                    >⟲ BACKFILL</button>
                                  )}
                                  <button
                                    data-testid={`student-unlink-broker-${b.connection_id}`}
                                    title={`Unlink this ${b.platform_kind || 'broker'} from the student.`}
                                    onClick={async () => {
                                      const kind = b.platform_kind || 'metaapi';
                                      let confirmed = false;
                                      let wantCloud = false;
                                      if (kind === 'metaapi') {
                                        wantCloud = window.confirm(`Unlink "${b.label || b.login}" from ${s.name}?\n\nClick OK = also DELETE the MetaAPI cloud account (frees provisioning quota).\nClick Cancel = local-only unlink (cloud account stays).`);
                                        confirmed = true;
                                      } else {
                                        confirmed = window.confirm(`Unlink ${kind.toUpperCase()} "${b.label || b.login}" from ${s.name}?`);
                                      }
                                      if (!confirmed) return;
                                      try {
                                        let r;
                                        switch (kind) {
                                          case 'tradovate':   r = await api.adminDeleteTradovateConnection(b.connection_id); break;
                                          case 'rithmic':     r = await api.adminDeleteRithmicConnection(b.connection_id); break;
                                          case 'tradelocker': r = await api.adminDeleteTradeLockerConnection(b.connection_id); break;
                                          case 'matchtrader': r = await api.adminDeleteMatchTraderConnection(b.connection_id); break;
                                          case 'dxtrade':     r = await api.adminDeleteDxTradeConnection(b.connection_id); break;
                                          case 'bybit':       r = await api.adminBybitDelete(b.connection_id); break;
                                          default:            r = await api.adminDeleteMetaAPIConnection(b.connection_id, wantCloud);
                                        }
                                        toast.success(`Unlinked ${b.label || b.login}${wantCloud && r?.cloud_result ? ' + cloud removed' : ''}`);
                                        loadTabData('students');
                                      } catch (err) { toast.error(err.message); }
                                    }}
                                    style={{ background: 'transparent', border: '1px solid var(--pp-error)', color: 'var(--pp-error)', padding: '2px 7px', fontSize: 9, cursor: 'pointer', fontWeight: 800 }}
                                  >×</button>
                                </>
                              )}
                            </div>
                          ))
                        ) : (
                          <span style={{ fontSize: 11, color: 'var(--pp-text-3)' }}>No brokers linked</span>
                        )}
                      </div>
                    </div>
                    {/* Action bar */}
                    <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap', borderTop: '1px solid var(--pp-border)', paddingTop: 12 }}>
                      {s.welcome_email_pending && (
                        <button
                          data-testid={`launch-${s.user_id}`}
                          disabled={launchingUser[s.user_id]}
                          onClick={async () => {
                            setLaunchingUser(p => ({ ...p, [s.user_id]: true }));
                            try { await api.launchStudentEmailsForUser(s.user_id); toast.success(`🚀 Emails launched for ${s.name}`); loadTabData('students'); } catch (e) { toast.error(e.message); }
                            setLaunchingUser(p => ({ ...p, [s.user_id]: false }));
                          }}
                          style={{ height: 36, padding: '0 16px', background: '#00FF94', border: 'none', color: '#000', fontWeight: 800, fontSize: 12, cursor: 'pointer', letterSpacing: '0.1em', flexGrow: 1, minWidth: 140 }}
                        >{launchingUser[s.user_id] ? 'LAUNCHING…' : '🚀 LAUNCH STUDENT'}</button>
                      )}
                      <button
                        data-testid={`edit-profile-${s.user_id}`}
                        onClick={() => { setEditProfileFor(s); setEditProfileForm({ name: s.name || '', email: s.email || '', phone: s.phone || '', password: '', kyc_verified: !!s.is_verified }); }}
                        style={{ height: 36, padding: '0 14px', background: 'transparent', border: '1px solid var(--pp-border)', color: 'var(--pp-text)', fontSize: 11, cursor: 'pointer', fontWeight: 700, letterSpacing: '0.08em' }}
                      >EDIT PROFILE</button>
                      <button
                        data-testid={`delete-student-${s.user_id}`}
                        onClick={async () => {
                          if (!window.confirm(`Delete ${s.name} (${s.email}) permanently?\nThis removes all proxies, subscriptions, funded accounts, payments, etc.`)) return;
                          try { await api.deleteAdminUser(s.user_id); toast.success(`${s.name} deleted`); loadTabData('students'); } catch (e) { toast.error(e.message); }
                        }}
                        style={{ height: 36, padding: '0 14px', background: 'transparent', border: '1px solid var(--pp-error)', color: 'var(--pp-error)', fontSize: 11, cursor: 'pointer', fontWeight: 700, letterSpacing: '0.08em' }}
                      >DELETE</button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* EDIT PROFILE MODAL */}
            {editProfileFor && (
              <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }} onClick={() => setEditProfileFor(null)}>
                <div data-testid="edit-profile-modal" style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: 22, width: 460, maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <p style={{ margin: 0, fontSize: 14, fontWeight: 700, letterSpacing: '0.08em' }}>EDIT — {editProfileFor.name?.toUpperCase()}</p>
                    <button onClick={() => setEditProfileFor(null)} style={{ background: 'none', border: 'none', color: 'var(--pp-text-3)', fontSize: 18, cursor: 'pointer' }}>×</button>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div><label style={LABEL}>Full Name</label><input style={INPUT} value={editProfileForm.name || ''} onChange={e => setEditProfileForm(p => ({ ...p, name: e.target.value }))} /></div>
                    <div><label style={LABEL}>Email</label><input style={INPUT} type="email" value={editProfileForm.email || ''} onChange={e => setEditProfileForm(p => ({ ...p, email: e.target.value }))} /></div>
                    <div><label style={LABEL}>Phone</label><input style={INPUT} value={editProfileForm.phone || ''} onChange={e => setEditProfileForm(p => ({ ...p, phone: e.target.value }))} /></div>
                    <div><label style={LABEL}>Password (leave blank to keep)</label><input style={{ ...INPUT, fontFamily: 'monospace' }} type="text" value={editProfileForm.password || ''} placeholder="New password" onChange={e => setEditProfileForm(p => ({ ...p, password: e.target.value }))} /></div>
                    <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12, color: 'var(--pp-text-2)', cursor: 'pointer' }}>
                      <input type="checkbox" checked={!!editProfileForm.kyc_verified} onChange={e => setEditProfileForm(p => ({ ...p, kyc_verified: e.target.checked }))} /> KYC Verified
                    </label>
                  </div>
                  <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
                    <button onClick={() => setEditProfileFor(null)} style={{ flex: 1, height: 42, background: 'transparent', border: '1px solid var(--pp-border)', color: 'var(--pp-text)', cursor: 'pointer' }}>Cancel</button>
                    <button
                      data-testid="save-profile-btn"
                      onClick={async () => {
                        const payload = { ...editProfileForm };
                        if (!payload.password) delete payload.password;
                        try { await api.updateAdminUser(editProfileFor.user_id, payload); toast.success('Profile updated'); setEditProfileFor(null); loadTabData('students'); } catch (e) { toast.error(e.message); }
                      }}
                      className="btn-primary"
                      style={{ flex: 2, height: 42 }}
                    >SAVE CHANGES</button>
                  </div>
                </div>
              </div>
            )}

            {/* ADD BYBIT MODAL */}
            {addBybitFor && (
              <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }} onClick={() => setAddBybitFor(null)}>
                <div data-testid="add-bybit-modal" style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: 22, width: 560, maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                    <p style={{ margin: 0, fontSize: 14, fontWeight: 700, letterSpacing: '0.08em' }}>BYBIT (CRYPTO) — {addBybitFor.name?.toUpperCase()}</p>
                    <button onClick={() => setAddBybitFor(null)} style={{ background: 'none', border: 'none', color: 'var(--pp-text-3)', fontSize: 18, cursor: 'pointer' }}>×</button>
                  </div>
                  <p style={{ margin: '4px 0 14px', fontSize: 11, color: 'var(--pp-text-3)', lineHeight: 1.5 }}>
                    Admin attaches a Bybit API key on behalf of <strong>{addBybitFor.name}</strong>. Student does not need to handle credentials. Sizing uses contract-estimation (matches master's $-per-$1-asset-move parity). Routes any crypto pair (ETH, BTC, LTC, SOL, …).
                  </p>
                  {/* Existing connections */}
                  {(bybitConnsByUser[addBybitFor.user_id] || []).length > 0 && (
                    <div style={{ marginBottom: 14, border: '1px solid var(--pp-border)', padding: 10, background: 'var(--pp-overlay)' }}>
                      <p style={{ margin: '0 0 6px', fontSize: 9, color: 'var(--pp-text-3)', letterSpacing: '0.1em', fontWeight: 600 }}>EXISTING BYBIT CONNECTIONS</p>
                      {(bybitConnsByUser[addBybitFor.user_id] || []).map(c => (
                        <div key={c.connection_id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <p className="mono" style={{ margin: 0, fontSize: 11, color: 'var(--pp-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.account_label} <span style={{ color: '#F7A600' }}>· {c.market_type}</span></p>
                            <p className="mono" style={{ margin: 0, fontSize: 10, color: 'var(--pp-text-3)' }}>
                              {c.balance != null ? `Wallet: $${Number(c.balance).toLocaleString(undefined,{maximumFractionDigits:2})} · Equity: $${Number(c.equity || 0).toLocaleString(undefined,{maximumFractionDigits:2})} · Open: ${c.open_positions || 0}` : 'not synced yet'}
                            </p>
                          </div>
                          <div style={{ display: 'flex', gap: 4 }}>
                            <button onClick={async () => { try { const r = await api.adminBybitSync(c.connection_id); if (r.ok) { toast.success(`Synced — Wallet $${Number(r.state.balance||0).toFixed(2)} · Eq $${Number(r.state.equity||0).toFixed(2)} · ${r.state.open_positions} open`); const next = await api.adminBybitList(addBybitFor.user_id); setBybitConnsByUser(prev => ({ ...prev, [addBybitFor.user_id]: next || [] })); } else { toast.error(`Sync: ${r.error}`); } } catch(e) { toast.error(e.message); } }} style={{ background: 'rgba(0,255,148,0.1)', border: '1px solid rgba(0,255,148,0.4)', color: '#00FF94', fontSize: 9, padding: '3px 7px', cursor: 'pointer', fontWeight: 700 }}>SYNC</button>
                            <button onClick={async () => { if (!window.confirm(`Remove "${c.account_label}"?`)) return; try { await api.adminBybitDelete(c.connection_id); const next = await api.adminBybitList(addBybitFor.user_id); setBybitConnsByUser(prev => ({ ...prev, [addBybitFor.user_id]: next || [] })); toast.success('Removed'); } catch(e) { toast.error(e.message); } }} style={{ background: 'transparent', border: '1px solid var(--pp-error)', color: 'var(--pp-error)', fontSize: 9, padding: '3px 7px', cursor: 'pointer', fontWeight: 700 }}>×</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {/* New form */}
                  <p style={{ margin: '0 0 8px', fontSize: 9, color: 'var(--pp-text-3)', letterSpacing: '0.1em', fontWeight: 600 }}>ADD NEW BYBIT CONNECTION</p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <div style={{ gridColumn: '1 / -1' }}><label style={LABEL}>Account Label</label><input style={INPUT} value={addBybitForm.account_label} onChange={e => setAddBybitForm(p => ({ ...p, account_label: e.target.value }))} placeholder="e.g. Bybit Linear — John" /></div>
                    <div style={{ gridColumn: '1 / -1' }}><label style={LABEL}>API Key</label><input data-testid="bybit-api-key" style={INPUT} value={addBybitForm.api_key} onChange={e => setAddBybitForm(p => ({ ...p, api_key: e.target.value }))} placeholder="paste student's Bybit API key (admin only)" /></div>
                    <div style={{ gridColumn: '1 / -1' }}><label style={LABEL}>API Secret</label><input data-testid="bybit-api-secret" style={INPUT} type="password" value={addBybitForm.api_secret} onChange={e => setAddBybitForm(p => ({ ...p, api_secret: e.target.value }))} placeholder="paste API secret" /></div>
                    <div><label style={LABEL}>Market Type</label>
                      <select style={{ ...INPUT, cursor: 'pointer' }} value={addBybitForm.market_type} onChange={e => setAddBybitForm(p => ({ ...p, market_type: e.target.value }))}>
                        <option value="linear">Linear (USDT Perp)</option>
                        <option value="inverse">Inverse (Coin-margined Perp)</option>
                        <option value="spot">Spot</option>
                      </select>
                    </div>
                    <div><label style={LABEL}>Sizing Mode</label>
                      <select style={{ ...INPUT, cursor: 'pointer' }} value={addBybitForm.sizing_mode} onChange={e => setAddBybitForm(p => ({ ...p, sizing_mode: e.target.value }))}>
                        <option value="dollar_match">Match $/$1-move (recommended)</option>
                        <option value="fixed_qty">Fixed qty per signal</option>
                      </select>
                    </div>
                    {addBybitForm.sizing_mode === 'fixed_qty' && (
                      <div style={{ gridColumn: '1 / -1' }}><label style={LABEL}>Fixed Qty (units)</label><input style={INPUT} type="number" step="0.001" value={addBybitForm.fixed_qty} onChange={e => setAddBybitForm(p => ({ ...p, fixed_qty: parseFloat(e.target.value) || 0 }))} placeholder="e.g. 0.1 ETH per signal" /></div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                    <button onClick={() => setAddBybitFor(null)} style={{ flex: 1, height: 42, background: 'transparent', border: '1px solid var(--pp-border)', color: 'var(--pp-text)', cursor: 'pointer' }}>Cancel</button>
                    <button
                      data-testid="bybit-connect-btn"
                      disabled={savingBybit || !addBybitForm.api_key || !addBybitForm.api_secret}
                      onClick={async () => {
                        setSavingBybit(true);
                        try {
                          const r = await api.adminBybitConnect({ user_id: addBybitFor.user_id, api_key: addBybitForm.api_key.trim(), api_secret: addBybitForm.api_secret.trim(), market_type: addBybitForm.market_type, account_label: addBybitForm.account_label, sizing_mode: addBybitForm.sizing_mode, fixed_qty: addBybitForm.fixed_qty });
                          toast.success(`Bybit connected — Wallet $${Number(r.verified?.total_wallet_usd || 0).toLocaleString()} · Equity $${Number(r.verified?.total_equity_usd || 0).toLocaleString()}`);
                          setAddBybitForm(p => ({ ...p, api_key: '', api_secret: '' }));
                          const next = await api.adminBybitList(addBybitFor.user_id);
                          setBybitConnsByUser(prev => ({ ...prev, [addBybitFor.user_id]: next || [] }));
                        } catch (e) { toast.error(e.message); }
                        setSavingBybit(false);
                      }}
                      className="btn-primary" style={{ flex: 2, height: 42, background: '#F7A600', color: '#000' }}
                    >{savingBybit ? 'VERIFYING…' : 'VERIFY & ATTACH'}</button>
                  </div>
                </div>
              </div>
            )}

            {/* ADD FUNDED ACCOUNT MODAL */}
            {addFundedFor && (
              <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }} onClick={() => setAddFundedFor(null)}>
                <div data-testid="add-funded-modal" style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: 22, width: 520, maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <p style={{ margin: 0, fontSize: 14, fontWeight: 700, letterSpacing: '0.08em' }}>NEW FUNDED ACCOUNT — {addFundedFor.name?.toUpperCase()}</p>
                    <button onClick={() => setAddFundedFor(null)} style={{ background: 'none', border: 'none', color: 'var(--pp-text-3)', fontSize: 18, cursor: 'pointer' }}>×</button>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <div><label style={LABEL}>Account Size ($)</label><input style={INPUT} type="number" step="1000" value={addFundedForm.size} onChange={e => setAddFundedForm(p => ({ ...p, size: parseFloat(e.target.value) || 0 }))} /></div>
                    <div><label style={LABEL}>Account Type</label>
                      <select style={{ ...INPUT, cursor: 'pointer' }} value={addFundedForm.account_type} onChange={e => setAddFundedForm(p => ({ ...p, account_type: e.target.value }))}>
                        <option value="instant">Instant Funded</option>
                        <option value="evaluation">Evaluation / Challenge</option>
                      </select>
                    </div>
                    <div><label style={LABEL}>MT5 Login</label><input style={INPUT} value={addFundedForm.mt5_login} onChange={e => setAddFundedForm(p => ({ ...p, mt5_login: e.target.value }))} placeholder="optional" /></div>
                    {/* Server + Broker fields intentionally removed — admin
                        no longer needs to attach a stealth broker name when
                        issuing a funded account. Native engine bridges handle
                        execution routing internally. */}
                    <div><label style={LABEL}>Status</label>
                      <select style={{ ...INPUT, cursor: 'pointer' }} value={addFundedForm.status} onChange={e => setAddFundedForm(p => ({ ...p, status: e.target.value }))}>
                        {['pending_creation','applied','approved','active'].map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                    <div><label style={LABEL}>Daily DD Limit (%)</label><input style={INPUT} type="number" step="0.1" value={addFundedForm.daily_drawdown_limit_pct} onChange={e => setAddFundedForm(p => ({ ...p, daily_drawdown_limit_pct: parseFloat(e.target.value) || 0 }))} /></div>
                    <div><label style={LABEL}>Max DD Limit (%)</label><input style={INPUT} type="number" step="0.1" value={addFundedForm.max_drawdown_limit_pct} onChange={e => setAddFundedForm(p => ({ ...p, max_drawdown_limit_pct: parseFloat(e.target.value) || 0 }))} /></div>
                    <div><label style={LABEL}>Min Profit Target (%)</label><input style={INPUT} type="number" step="0.1" value={addFundedForm.min_profit_target_pct} onChange={e => setAddFundedForm(p => ({ ...p, min_profit_target_pct: parseFloat(e.target.value) || 0 }))} /></div>
                    <div><label style={LABEL}>Consistency (%)</label><input style={INPUT} type="number" step="0.1" value={addFundedForm.consistency_pct} onChange={e => setAddFundedForm(p => ({ ...p, consistency_pct: parseFloat(e.target.value) || 0 }))} /></div>
                  </div>
                  <div style={{ marginTop: 10 }}><label style={LABEL}>Notes</label><input style={INPUT} value={addFundedForm.notes || ''} onChange={e => setAddFundedForm(p => ({ ...p, notes: e.target.value }))} placeholder="Internal notes..." /></div>
                  <label data-testid="email-credentials-toggle" style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginTop: 14, padding: 10, background: 'rgba(255,176,0,0.06)', border: '1px solid rgba(255,176,0,0.25)', cursor: 'pointer' }}>
                    <input type="checkbox" checked={!!addFundedForm.email_credentials_to_student} onChange={e => setAddFundedForm(p => ({ ...p, email_credentials_to_student: e.target.checked }))} style={{ marginTop: 2 }} />
                    <div style={{ flex: 1 }}>
                      <p style={{ margin: 0, fontSize: 11, fontWeight: 700, color: '#FFB000', letterSpacing: '0.08em' }}>EMAIL MT5 LOGIN CREDENTIALS TO STUDENT</p>
                      <p style={{ margin: '4px 0 0', fontSize: 10, color: 'var(--pp-text-3)', lineHeight: 1.5 }}>
                        Off (default): student gets only an "Account Approved" notice — no login/password emailed. On: full backend credentials are sent (legacy behavior).
                      </p>
                    </div>
                  </label>
                  <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                    <button onClick={() => setAddFundedFor(null)} style={{ flex: 1, height: 42, background: 'transparent', border: '1px solid var(--pp-border)', color: 'var(--pp-text)', cursor: 'pointer' }}>Cancel</button>
                    <button
                      data-testid="create-funded-btn"
                      onClick={async () => {
                        try { await api.createFundedForUser(addFundedFor.user_id, addFundedForm); toast.success('Funded account created'); setAddFundedFor(null); loadTabData('students'); } catch (e) { toast.error(e.message); }
                      }}
                      className="btn-primary" style={{ flex: 2, height: 42 }}
                    >CREATE FUNDED ACCOUNT</button>
                  </div>
                </div>
              </div>
            )}

            {/* ADD NATIVE SIMULATED FUNDED ACCOUNT MODAL */}
            {addNativeFor && (
              <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }} onClick={() => setAddNativeFor(null)}>
                <div data-testid="add-native-modal" style={{ background: 'var(--pp-surface)', border: '1px solid rgba(0,255,148,0.35)', padding: 22, width: 560, maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <p style={{ margin: 0, fontSize: 14, fontWeight: 700, letterSpacing: '0.08em', color: '#00FF94' }}>NEW NATIVE SIMULATED ACCOUNT — {addNativeFor.name?.toUpperCase()}</p>
                    <button onClick={() => setAddNativeFor(null)} style={{ background: 'none', border: 'none', color: 'var(--pp-text-3)', fontSize: 18, cursor: 'pointer' }}>×</button>
                  </div>
                  <p style={{ margin: '0 0 14px', fontSize: 11, color: 'var(--pp-text-3)' }}>Simulated balance · honors student multiplier · same prop firm rules as real accounts.</p>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <div><label style={LABEL}>Account Size ($)</label>
                      <input data-testid="native-size-input" style={INPUT} type="number" step="1000" value={addNativeForm.size} onChange={e => setAddNativeForm(p => ({ ...p, size: parseFloat(e.target.value) || 0 }))} />
                    </div>
                    <div><label style={LABEL}>Asset Class</label>
                      <select data-testid="native-asset-class-select" style={{ ...INPUT, cursor: 'pointer' }} value={addNativeForm.asset_class} onChange={e => setAddNativeForm(p => ({ ...p, asset_class: e.target.value }))}>
                        <option value="futures">Futures (MNQ, MES, MGC…)</option>
                        <option value="forex">Forex (EURUSD, XAUUSD…)</option>
                      </select>
                    </div>
                    <div><label style={LABEL}>Account Type</label>
                      <select style={{ ...INPUT, cursor: 'pointer' }} value={addNativeForm.account_type} onChange={e => setAddNativeForm(p => ({ ...p, account_type: e.target.value }))}>
                        <option value="instant">Instant Funded</option>
                        <option value="evaluation">Evaluation / Challenge</option>
                      </select>
                    </div>
                    <div><label style={LABEL}>Consistency (%)</label>
                      <input style={INPUT} type="number" step="0.1" value={addNativeForm.consistency_pct} onChange={e => setAddNativeForm(p => ({ ...p, consistency_pct: parseFloat(e.target.value) || 0 }))} />
                    </div>
                    <div><label style={LABEL}>Min Profit Target (%)</label>
                      <input style={INPUT} type="number" step="0.1" value={addNativeForm.min_profit_target_pct} onChange={e => setAddNativeForm(p => ({ ...p, min_profit_target_pct: parseFloat(e.target.value) || 0 }))} />
                    </div>
                    <div><label style={LABEL}>Max DD Limit (%)</label>
                      <input style={INPUT} type="number" step="0.1" value={addNativeForm.max_drawdown_limit_pct} onChange={e => setAddNativeForm(p => ({ ...p, max_drawdown_limit_pct: parseFloat(e.target.value) || 0 }))} />
                    </div>
                    <div><label style={LABEL}>Daily DD Limit (%)</label>
                      <input style={INPUT} type="number" step="0.1" value={addNativeForm.daily_drawdown_limit_pct} onChange={e => setAddNativeForm(p => ({ ...p, daily_drawdown_limit_pct: parseFloat(e.target.value) || 0 }))} />
                    </div>
                  </div>

                  {/* ── Stealth Backend Engine Bridge ───────────────────── */}
                  <div style={{ marginTop: 18, padding: 12, border: '1px dashed rgba(0,255,148,0.35)', background: 'rgba(0,255,148,0.04)' }}>
                    <p style={{ margin: 0, fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', color: '#00FF94' }}>STEALTH BACKEND ENGINE (OPTIONAL)</p>
                    <p style={{ margin: '4px 0 10px', fontSize: 11, color: 'var(--pp-text-3)' }}>Bind to a real broker connection. Frontend stays simulated; every fill also fires on the bound broker. Leave as <strong>None</strong> for pure paper.</p>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                      <div>
                        <label style={LABEL}>Engine Kind</label>
                        <select
                          data-testid="native-engine-kind-select"
                          style={{ ...INPUT, cursor: 'pointer' }}
                          value={addNativeForm.backend_engine_kind}
                          onChange={e => setAddNativeForm(p => ({ ...p, backend_engine_kind: e.target.value, backend_engine_id: '' }))}
                        >
                          <option value="none">None (pure simulated)</option>
                          <option value="volumetrica">Volumetrica / AquaFutures</option>
                          <option value="metaapi">MetaAPI (MT4/MT5)</option>
                          <option value="tradelocker">TradeLocker</option>
                          <option value="bybit">Bybit</option>
                          <option value="matchtrader">MatchTrader</option>
                          <option value="tradovate">Tradovate</option>
                        </select>
                      </div>
                      <div>
                        <label style={LABEL}>Mode</label>
                        <select
                          style={{ ...INPUT, cursor: 'pointer', opacity: addNativeForm.backend_engine_kind === 'none' ? 0.4 : 1 }}
                          disabled={addNativeForm.backend_engine_kind === 'none'}
                          value={addNativeForm.backend_engine_mode}
                          onChange={e => setAddNativeForm(p => ({ ...p, backend_engine_mode: e.target.value }))}
                        >
                          <option value="fire_only">Fire only (balance stays simulated)</option>
                          <option value="mirror_state">Mirror real fills (sync state from broker)</option>
                        </select>
                      </div>
                    </div>

                    {addNativeForm.backend_engine_kind !== 'none' && (() => {
                      const kindLabelMap = { volumetrica: 'Volumetrica', metaapi: 'MetaAPI', tradelocker: 'TradeLocker', bybit: 'Bybit', matchtrader: 'MatchTrader', tradovate: 'Tradovate' };
                      const wantedLabel = kindLabelMap[addNativeForm.backend_engine_kind];
                      const matching = (nativeEngineConns || []).filter(a => (a.broker_type || '').toLowerCase() === wantedLabel.toLowerCase() && (a.user_id === addNativeFor.user_id));
                      const matchingAll = (nativeEngineConns || []).filter(a => (a.broker_type || '').toLowerCase() === wantedLabel.toLowerCase());
                      const list = matching.length ? matching : matchingAll;
                      const isMetaAPI = addNativeForm.backend_engine_kind === 'metaapi';
                      return (
                        <div style={{ marginTop: 10 }}>
                          {isMetaAPI && (
                            <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
                              <button
                                type="button"
                                data-testid="native-mt-mode-existing"
                                onClick={() => setAddNativeForm(p => ({ ...p, metaapi_link_mode: 'existing', backend_engine_id: '' }))}
                                style={{ flex: 1, height: 30, fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', cursor: 'pointer', background: (addNativeForm.metaapi_link_mode || 'existing') === 'existing' ? '#00FF94' : 'transparent', color: (addNativeForm.metaapi_link_mode || 'existing') === 'existing' ? '#000' : 'var(--pp-text)', border: '1px solid rgba(0,255,148,0.4)' }}
                              >USE EXISTING CONNECTION</button>
                              <button
                                type="button"
                                data-testid="native-mt-mode-manual"
                                onClick={() => setAddNativeForm(p => ({ ...p, metaapi_link_mode: 'manual', backend_engine_id: '' }))}
                                style={{ flex: 1, height: 30, fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', cursor: 'pointer', background: addNativeForm.metaapi_link_mode === 'manual' ? '#00FF94' : 'transparent', color: addNativeForm.metaapi_link_mode === 'manual' ? '#000' : 'var(--pp-text)', border: '1px solid rgba(0,255,148,0.4)' }}
                              >ENTER LOGIN MANUALLY</button>
                            </div>
                          )}

                          {isMetaAPI && addNativeForm.metaapi_link_mode === 'manual' ? (
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                              <div><label style={LABEL}>MT Login</label>
                                <input data-testid="native-mt-login" style={INPUT} placeholder="e.g. 1234567" value={addNativeForm.mt_login || ''} onChange={e => setAddNativeForm(p => ({ ...p, mt_login: e.target.value }))} />
                              </div>
                              <div><label style={LABEL}>Server</label>
                                <input data-testid="native-mt-server" style={INPUT} placeholder="e.g. Chimara-SRV01" value={addNativeForm.mt_server || ''} onChange={e => setAddNativeForm(p => ({ ...p, mt_server: e.target.value }))} />
                              </div>
                              <div style={{ gridColumn: '1 / -1' }}><label style={LABEL}>Password</label>
                                <input data-testid="native-mt-password" style={INPUT} type="password" placeholder="MT5 / MT4 password" value={addNativeForm.mt_password || ''} onChange={e => setAddNativeForm(p => ({ ...p, mt_password: e.target.value }))} />
                              </div>
                              <div><label style={LABEL}>Platform</label>
                                <select style={{ ...INPUT, cursor: 'pointer' }} value={addNativeForm.mt_platform || 'mt5'} onChange={e => setAddNativeForm(p => ({ ...p, mt_platform: e.target.value }))}>
                                  <option value="mt5">MT5</option>
                                  <option value="mt4">MT4</option>
                                </select>
                              </div>
                              <div><label style={LABEL}>Label (optional)</label>
                                <input style={INPUT} placeholder="Friendly name" value={addNativeForm.mt_label || ''} onChange={e => setAddNativeForm(p => ({ ...p, mt_label: e.target.value }))} />
                              </div>
                              <p style={{ gridColumn: '1 / -1', margin: 0, fontSize: 10, color: 'var(--pp-text-3)' }}>A new MetaAPI cloud account is provisioned in the background (~1-2 min). The Native account will be wired to it automatically — no historical backfill will run.</p>
                            </div>
                          ) : (
                            <>
                              <label style={LABEL}>{wantedLabel} Connection ({list.length} available{matching.length === 0 && matchingAll.length > 0 ? ' — none for this student, showing all' : ''})</label>
                              {list.length > 0 ? (
                                <select
                                  data-testid="native-engine-id-select"
                                  style={{ ...INPUT, cursor: 'pointer' }}
                                  value={addNativeForm.backend_engine_id}
                                  onChange={e => setAddNativeForm(p => ({ ...p, backend_engine_id: e.target.value }))}
                                >
                                  <option value="">— Select connection —</option>
                                  {list.map(c => (
                                    <option key={c.connection_id} value={c.connection_id}>
                                      {c.account_label || c.login || c.connection_id} · {c.user_name || c.user_email || c.user_id?.slice(0,8)} · {c.status || '?'}
                                    </option>
                                  ))}
                                </select>
                              ) : (
                                <input
                                  data-testid="native-engine-id-input"
                                  style={INPUT}
                                  placeholder={`No ${wantedLabel} connections found — paste connection_id manually`}
                                  value={addNativeForm.backend_engine_id}
                                  onChange={e => setAddNativeForm(p => ({ ...p, backend_engine_id: e.target.value }))}
                                />
                              )}
                            </>
                          )}
                        </div>
                      );
                    })()}
                  </div>

                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14, cursor: addNativeForm.backend_engine_kind === 'metaapi' ? 'not-allowed' : 'pointer', fontSize: 12, color: addNativeForm.backend_engine_kind === 'metaapi' ? 'var(--pp-text-3)' : 'var(--pp-text-2)', opacity: addNativeForm.backend_engine_kind === 'metaapi' ? 0.5 : 1 }}>
                    <input type="checkbox" disabled={addNativeForm.backend_engine_kind === 'metaapi'} checked={addNativeForm.backend_engine_kind === 'metaapi' ? false : addNativeForm.backfill} onChange={e => setAddNativeForm(p => ({ ...p, backfill: e.target.checked }))} />
                    Backfill historic master signals (replays closed signals, no emails)
                    {addNativeForm.backend_engine_kind === 'metaapi' && <span style={{ fontSize: 10, color: '#FFB000', marginLeft: 6 }}>— disabled when linked to MetaAPI (real account)</span>}
                  </label>

                  <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
                    <button onClick={() => setAddNativeFor(null)} style={{ flex: 1, height: 42, background: 'transparent', border: '1px solid var(--pp-border)', color: 'var(--pp-text)', cursor: 'pointer' }}>Cancel</button>
                    <button
                      data-testid="create-native-btn"
                      disabled={savingNative || !addNativeForm.size || (
                        addNativeForm.backend_engine_kind !== 'none' && (
                          addNativeForm.backend_engine_kind === 'metaapi' && addNativeForm.metaapi_link_mode === 'manual'
                            ? !(addNativeForm.mt_login && addNativeForm.mt_password && addNativeForm.mt_server)
                            : !addNativeForm.backend_engine_id
                        )
                      )}
                      onClick={async () => {
                        if (!addNativeForm.size || addNativeForm.size <= 0) { toast.error('Invalid size'); return; }
                        const isManualMT = addNativeForm.backend_engine_kind === 'metaapi' && addNativeForm.metaapi_link_mode === 'manual';
                        if (addNativeForm.backend_engine_kind !== 'none' && !isManualMT && !addNativeForm.backend_engine_id) {
                          toast.error('Pick a broker connection or set Engine Kind to None'); return;
                        }
                        if (isManualMT && !(addNativeForm.mt_login && addNativeForm.mt_password && addNativeForm.mt_server)) {
                          toast.error('Enter MT login, password, and server'); return;
                        }
                        setSavingNative(true);
                        try {
                          const payload = {
                            user_id: addNativeFor.user_id,
                            size: addNativeForm.size,
                            asset_class: addNativeForm.asset_class,
                            account_type: addNativeForm.account_type,
                            min_profit_target_pct: addNativeForm.min_profit_target_pct,
                            max_drawdown_limit_pct: addNativeForm.max_drawdown_limit_pct,
                            daily_drawdown_limit_pct: addNativeForm.daily_drawdown_limit_pct,
                            consistency_pct: addNativeForm.consistency_pct,
                          };
                          if (addNativeForm.backend_engine_kind !== 'none') {
                            payload.backend_engine_kind = addNativeForm.backend_engine_kind;
                            payload.backend_engine_mode = addNativeForm.backend_engine_mode;
                            if (isManualMT) {
                              payload.metaapi_manual = {
                                login: addNativeForm.mt_login,
                                password: addNativeForm.mt_password,
                                server: addNativeForm.mt_server,
                                platform: addNativeForm.mt_platform || 'mt5',
                                label: addNativeForm.mt_label || '',
                              };
                            } else {
                              payload.backend_engine_id = addNativeForm.backend_engine_id;
                            }
                          }
                          const fa = await api.createNativeFundedAccount(payload);
                          toast.success(`Native funded ${fa.label} created${fa.backend_engine_kind ? ` · bridged → ${fa.backend_engine_kind}` : ''}`);
                          // Skip backfill when bridged to a real MetaAPI account — backfilling
                          // would pollute the live broker history with synthetic trades.
                          const skipBackfill = addNativeForm.backend_engine_kind === 'metaapi';
                          if (addNativeForm.backfill && !skipBackfill) {
                            try {
                              const r = await api.backfillSimulatedAccount(fa.funded_account_id);
                              toast.success(`Backfilled ${r.replayed} signals · $${Number(r.total_pnl || 0).toLocaleString()} P&L`);
                            } catch (e) { toast.error(`Backfill failed: ${e.message}`); }
                          }
                          setAddNativeFor(null);
                          loadTabData('students');
                        } catch (e) { toast.error(e.message); }
                        setSavingNative(false);
                      }}
                      className="btn-primary" style={{ flex: 2, height: 42, background: '#00FF94', color: '#000', opacity: savingNative ? 0.6 : 1 }}
                    >{savingNative ? 'CREATING…' : 'CREATE NATIVE ACCOUNT'}</button>
                  </div>
                </div>
              </div>
            )}


            {/* SUBSCRIBE STRATEGIES MODAL */}
            {subscribeFor && (
              <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }} onClick={() => setSubscribeFor(null)}>
                <div data-testid="subscribe-modal" style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: 22, width: 460, maxWidth: '95vw', maxHeight: '85vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                    <p style={{ margin: 0, fontSize: 14, fontWeight: 700, letterSpacing: '0.08em' }}>STRATEGIES — {subscribeFor.name?.toUpperCase()}</p>
                    <button onClick={() => setSubscribeFor(null)} style={{ background: 'none', border: 'none', color: 'var(--pp-text-3)', fontSize: 18, cursor: 'pointer' }}>×</button>
                  </div>
                  <p style={{ margin: '0 0 12px', fontSize: 11, color: 'var(--pp-text-3)' }}>Tap to toggle. Admin subscriptions bypass passcodes.</p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {strategies.map(strat => {
                      const isSub = (subscribeFor.subscriptions || []).some(x => x.strategy_id === strat.strategy_id);
                      return (
                        <button
                          key={strat.strategy_id}
                          data-testid={`toggle-strat-${strat.strategy_id}`}
                          onClick={async () => {
                            try {
                              if (isSub) { await api.adminUnsubscribeUser(subscribeFor.user_id, strat.strategy_id); toast.success(`Unsubscribed from ${strat.name}`); }
                              else { await api.adminSubscribeUser(subscribeFor.user_id, strat.strategy_id); toast.success(`Subscribed to ${strat.name}`); }
                              // Refresh the selected user + list
                              const fresh = await api.getActiveStudents();
                              setActiveStudents(fresh || []);
                              const freshUser = (fresh || []).find(x => x.user_id === subscribeFor.user_id);
                              if (freshUser) setSubscribeFor(freshUser);
                            } catch (e) { toast.error(e.message); }
                          }}
                          style={{
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            padding: '12px 14px',
                            background: isSub ? 'rgba(0,255,148,0.08)' : 'var(--pp-overlay)',
                            border: '1px solid ' + (isSub ? 'rgba(0,255,148,0.4)' : 'var(--pp-border)'),
                            color: 'var(--pp-text)',
                            cursor: 'pointer',
                            fontSize: 13,
                            fontWeight: 600,
                          }}
                        >
                          <span>{strat.name}{strat.instrument ? ` · ${strat.instrument}` : ''}</span>
                          <span style={{ fontSize: 11, color: isSub ? 'var(--pp-success)' : 'var(--pp-text-3)', letterSpacing: '0.1em', fontWeight: 700 }}>{isSub ? '✓ SUBSCRIBED' : 'TAP TO ADD'}</span>
                        </button>
                      );
                    })}
                    {strategies.length === 0 && (
                      <p style={{ color: 'var(--pp-text-3)', fontSize: 12, textAlign: 'center' }}>No strategies available. Create some in the Strategies tab first.</p>
                    )}
                  </div>
                  <button onClick={() => setSubscribeFor(null)} style={{ marginTop: 14, width: '100%', height: 42, background: 'transparent', border: '1px solid var(--pp-border)', color: 'var(--pp-text)', cursor: 'pointer' }}>DONE</button>
                </div>
              </div>
            )}

            {/* Create Student Modal */}
            {showCreateStudent && (
              <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setShowCreateStudent(false)}>
                <div data-testid="create-student-modal" style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: 24, width: 480, maxWidth: '95vw', maxHeight: '85vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                    <p style={{ margin: 0, fontWeight: 700, fontSize: 16, letterSpacing: '0.08em' }}>CREATE NEW STUDENT</p>
                    <button onClick={() => setShowCreateStudent(false)} style={{ background: 'none', border: 'none', color: 'var(--pp-text-3)', cursor: 'pointer', fontSize: 18 }}>x</button>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <div><label style={LABEL}>Full Name</label><input style={INPUT} value={newStudent.name} onChange={e => setNewStudent(s => ({ ...s, name: e.target.value }))} placeholder="Mathieu Gagnon" /></div>
                    <div><label style={LABEL}>Email</label><input style={INPUT} value={newStudent.email} onChange={e => setNewStudent(s => ({ ...s, email: e.target.value }))} placeholder="student@email.com" /></div>
                    <div><label style={LABEL}>Password</label><input style={INPUT} value={newStudent.password} onChange={e => setNewStudent(s => ({ ...s, password: e.target.value }))} placeholder="Set initial password" /></div>

                    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--pp-text-2)', cursor: 'pointer' }}>
                        <input type="checkbox" checked={newStudent.generate_passcode} onChange={e => setNewStudent(s => ({ ...s, generate_passcode: e.target.checked }))} /> Generate Passcode
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--pp-text-2)', cursor: 'pointer' }}>
                        <input type="checkbox" checked={newStudent.send_passcode_in_email} onChange={e => setNewStudent(s => ({ ...s, send_passcode_in_email: e.target.checked }))} /> Include Passcode in Email
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--pp-text-2)', cursor: 'pointer' }}>
                        <input type="checkbox" checked={newStudent.is_demo} onChange={e => setNewStudent(s => ({ ...s, is_demo: e.target.checked }))} /> Demo Account
                      </label>
                    </div>

                    {/* Welcome Email Schedule */}
                    <div>
                      <label style={LABEL}>Welcome Email</label>
                      <select
                        data-testid="email-schedule"
                        value={newStudent.email_schedule}
                        onChange={e => setNewStudent(s => ({ ...s, email_schedule: e.target.value, send_welcome_email: e.target.value !== 'manual' }))}
                        style={{ ...INPUT, cursor: 'pointer' }}
                      >
                        <option value="immediate">Send immediately on launch</option>
                        <option value="scheduled">Schedule for later</option>
                        <option value="manual">Don't send — I'll send manually</option>
                      </select>
                      {newStudent.email_schedule === 'scheduled' && (
                        <input
                          data-testid="email-schedule-datetime"
                          type="datetime-local"
                          value={newStudent.scheduled_at}
                          onChange={e => setNewStudent(s => ({ ...s, scheduled_at: e.target.value }))}
                          min={new Date().toISOString().slice(0, 16)}
                          style={{ ...INPUT, marginTop: 8 }}
                        />
                      )}
                    </div>

                    <div>
                      <label style={LABEL}>Subscribe to Strategies (optional)</label>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {(strategies || []).map(st => (
                          <button key={st.strategy_id} onClick={() => setNewStudent(s => ({ ...s, strategy_ids: s.strategy_ids.includes(st.strategy_id) ? s.strategy_ids.filter(id => id !== st.strategy_id) : [...s.strategy_ids, st.strategy_id] }))}
                            style={{ padding: '5px 10px', fontSize: 10, fontWeight: 700, cursor: 'pointer', background: newStudent.strategy_ids.includes(st.strategy_id) ? 'rgba(41,121,255,0.2)' : 'transparent', border: `1px solid ${newStudent.strategy_ids.includes(st.strategy_id) ? '#2979FF' : 'var(--pp-border)'}`, color: newStudent.strategy_ids.includes(st.strategy_id) ? '#2979FF' : 'var(--pp-text-3)' }}>
                            {st.name}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Funded Account Section */}
                    <div>
                      <label style={LABEL}>Funded Account</label>
                      <select
                        data-testid="funded-account-type"
                        value={newStudent.funded_account?.account_type || 'none'}
                        onChange={e => {
                          const val = e.target.value;
                          if (val === 'none') {
                            setNewStudent(s => ({ ...s, funded_account: null }));
                          } else if (val === 'instant') {
                            setNewStudent(s => ({ ...s, funded_account: { ...(s.funded_account || {}), account_type: val, min_profit_target_pct: '9', daily_drawdown_limit_pct: '4', max_drawdown_limit_pct: '8', consistency_pct: '15' } }));
                          } else {
                            setNewStudent(s => ({ ...s, funded_account: { ...(s.funded_account || {}), account_type: val, min_profit_target_pct: '11', daily_drawdown_limit_pct: '4', max_drawdown_limit_pct: '8', consistency_pct: '15' } }));
                          }
                        }}
                        style={{ ...INPUT, cursor: 'pointer' }}
                      >
                        <option value="none">No funded account yet</option>
                        <option value="instant">Instant Funded — full strategy access immediately</option>
                        <option value="challenge">Challenge/Evaluation — must hit profit target first</option>
                      </select>
                    </div>

                    {newStudent.funded_account && newStudent.funded_account.account_type !== 'none' && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: 12, border: '1px solid var(--pp-border)', background: 'var(--pp-overlay)' }}>
                        <div>
                          <label style={LABEL}>Account Size</label>
                          <input style={INPUT} placeholder="$50K / $100K / $200K" value={newStudent.funded_account.amount || ''} onChange={e => setNewStudent(s => ({ ...s, funded_account: { ...s.funded_account, amount: e.target.value } }))} />
                        </div>
                        <div>
                          <label style={LABEL}>MT5 Login Number (if known)</label>
                          <input style={INPUT} placeholder="e.g. 1234567" value={newStudent.funded_account.mt5_login || ''} onChange={e => setNewStudent(s => ({ ...s, funded_account: { ...s.funded_account, mt5_login: e.target.value } }))} />
                        </div>
                        <div>
                          <label style={LABEL}>Broker Server</label>
                          <input style={INPUT} placeholder="e.g. Chimara-SRV01" value={newStudent.funded_account.broker_server || ''} onChange={e => setNewStudent(s => ({ ...s, funded_account: { ...s.funded_account, broker_server: e.target.value } }))} />
                        </div>
                        {newStudent.funded_account.account_type === 'challenge' && (
                          <>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                              <div>
                                <label style={LABEL}>Daily Drawdown %</label>
                                <input style={INPUT} type="number" placeholder="5" value={newStudent.funded_account.daily_drawdown_limit_pct || ''} onChange={e => setNewStudent(s => ({ ...s, funded_account: { ...s.funded_account, daily_drawdown_limit_pct: e.target.value } }))} />
                              </div>
                              <div>
                                <label style={LABEL}>Max Drawdown %</label>
                                <input style={INPUT} type="number" placeholder="10" value={newStudent.funded_account.max_drawdown_limit_pct || ''} onChange={e => setNewStudent(s => ({ ...s, funded_account: { ...s.funded_account, max_drawdown_limit_pct: e.target.value } }))} />
                              </div>
                              <div>
                                <label style={LABEL}>Profit Target %</label>
                                <input style={INPUT} type="number" placeholder="8" value={newStudent.funded_account.min_profit_target_pct || ''} onChange={e => setNewStudent(s => ({ ...s, funded_account: { ...s.funded_account, min_profit_target_pct: e.target.value } }))} />
                              </div>
                              <div>
                                <label style={LABEL}>Consistency %</label>
                                <input style={INPUT} type="number" placeholder="30" value={newStudent.funded_account.consistency_pct || ''} onChange={e => setNewStudent(s => ({ ...s, funded_account: { ...s.funded_account, consistency_pct: e.target.value } }))} />
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    )}

                    {/* Proxy assignment (optional) */}
                    <div>
                      <label style={LABEL}>Proxy Assignment (optional)</label>
                      <select
                        value={newStudent.proxy?.enabled ? 'manual' : 'none'}
                        onChange={e => {
                          if (e.target.value === 'none') setNewStudent(s => ({ ...s, proxy: null }));
                          else setNewStudent(s => ({ ...s, proxy: { enabled: true, proxy_host: '', proxy_port: 12323, proxy_username: '', proxy_password: '', proxy_label: '' } }));
                        }}
                        style={{ ...INPUT, cursor: 'pointer' }}
                      >
                        <option value="none">No proxy yet (assign later)</option>
                        <option value="manual">Assign a static residential proxy</option>
                      </select>
                    </div>

                    {newStudent.proxy?.enabled && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: 12, border: '1px solid var(--pp-border)', background: 'var(--pp-overlay)' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10 }}>
                          <div>
                            <label style={LABEL}>Proxy Host</label>
                            <input data-testid="new-student-proxy-host" style={INPUT} value={newStudent.proxy.proxy_host || ''} placeholder="geo.iproyal.com" onChange={e => setNewStudent(s => ({ ...s, proxy: { ...s.proxy, proxy_host: e.target.value } }))} />
                          </div>
                          <div>
                            <label style={LABEL}>Port</label>
                            <input data-testid="new-student-proxy-port" style={INPUT} type="number" value={newStudent.proxy.proxy_port || 12323} onChange={e => setNewStudent(s => ({ ...s, proxy: { ...s.proxy, proxy_port: parseInt(e.target.value) || 12323 } }))} />
                          </div>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                          <div>
                            <label style={LABEL}>Username</label>
                            <input data-testid="new-student-proxy-user" style={INPUT} value={newStudent.proxy.proxy_username || ''} onChange={e => setNewStudent(s => ({ ...s, proxy: { ...s.proxy, proxy_username: e.target.value } }))} />
                          </div>
                          <div>
                            <label style={LABEL}>Password</label>
                            <input data-testid="new-student-proxy-pass" style={INPUT} type="password" value={newStudent.proxy.proxy_password || ''} onChange={e => setNewStudent(s => ({ ...s, proxy: { ...s.proxy, proxy_password: e.target.value } }))} />
                          </div>
                        </div>
                        <div>
                          <label style={LABEL}>Label</label>
                          <input data-testid="new-student-proxy-label" style={INPUT} value={newStudent.proxy.proxy_label || ''} placeholder="e.g. US-Static-01" onChange={e => setNewStudent(s => ({ ...s, proxy: { ...s.proxy, proxy_label: e.target.value } }))} />
                        </div>
                      </div>
                    )}

                    <button data-testid="launch-student-btn" disabled={!newStudent.name || !newStudent.email || !newStudent.password} onClick={async () => {
                      try {
                        const res = await api.createStudent(newStudent);
                        // If proxy was specified, assign it after student creation
                        if (newStudent.proxy?.enabled && newStudent.proxy.proxy_host && res.user_id) {
                          try {
                            await api.assignProxy({
                              user_id: res.user_id,
                              proxy_host: newStudent.proxy.proxy_host,
                              proxy_port: newStudent.proxy.proxy_port || 12323,
                              proxy_username: newStudent.proxy.proxy_username || '',
                              proxy_password: newStudent.proxy.proxy_password || '',
                              proxy_label: newStudent.proxy.proxy_label || '',
                            });
                          } catch (pe) { toast.warning(`Student created but proxy assign failed: ${pe.message}`); }
                        }
                        toast.success(`Student ${res.name} created!${res.passcode ? ` Passcode: ${res.passcode}` : ''}`);
                        setShowCreateStudent(false);
                        loadTabData('students');
                      } catch (e) { toast.error(e.message); }
                    }} style={{ width: '100%', height: 48, background: 'var(--pp-success)', border: 'none', color: '#000', fontWeight: 800, fontSize: 13, cursor: 'pointer', letterSpacing: '0.12em', marginTop: 4 }}>
                      LAUNCH STUDENT
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* FUNDED ACCOUNTS */}
        {!loading && activeTab === 'funded' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, gap: 12, flexWrap: 'wrap' }}>
              <p className="section-label">Funded Accounts ({adminFundedAccounts.length})</p>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ fontSize: 11, color: 'var(--pp-text-3)' }}>{adminPayouts.filter(p => p.status === 'pending').length} payout(s) pending</span>
                <button
                  data-testid="admin-bulk-convert-native-btn"
                  title="Convert every non-native funded account to Native Simulated in one shot. Preserves balance/equity/progress. Idempotent."
                  onClick={async () => {
                    const nonNative = adminFundedAccounts.filter(f => !f.is_simulated);
                    if (nonNative.length === 0) {
                      toast('All accounts are already Native.');
                      return;
                    }
                    if (!window.confirm(`Convert ALL ${nonNative.length} non-native funded account(s) to Native Simulated?\n\n• Balance/equity/progress preserved per-account\n• Open MT5 positions mirrored as paper trades\n• Closed-deal history backfilled (last 365 days)\n• No emails fire\n• Already-native accounts are skipped\n\nThis is safe to re-run.`)) return;
                    try {
                      const r = await api.adminBulkConvertToNative();
                      toast.success(`Bulk convert: ${r.converted_count} converted, ${r.skipped_count} skipped, ${r.error_count} errors.`);
                      if (r.error_count > 0) {
                        console.warn('Bulk convert errors:', r.errors);
                      }
                      loadTabData('funded');
                    } catch (e) { toast.error(e.message); }
                  }}
                  style={{ fontSize: 11, background: 'rgba(0,255,148,0.12)', border: '1px solid rgba(0,255,148,0.5)', color: '#00FF94', padding: '6px 12px', cursor: 'pointer', fontWeight: 700, letterSpacing: '0.08em' }}>
                  ↪ CONVERT ALL TO NATIVE
                </button>
              </div>
            </div>

            {/* Pending payout requests first */}
            {adminPayouts.filter(p => p.status === 'pending').map(p => (
              <div key={p.payout_id} data-testid={`admin-payout-${p.payout_id}`} style={{ background: 'rgba(0,255,148,0.06)', border: '1px solid rgba(0,255,148,0.3)', padding: 14, marginBottom: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                  <div>
                    <p style={{ margin: 0, fontWeight: 700, fontSize: 14, color: 'var(--pp-success)' }}>PAYOUT REQUEST</p>
                    <p style={{ margin: '3px 0 0', fontSize: 12, color: 'var(--pp-text-3)' }}>{p.user_name} · {p.payout_method?.replace('_',' ')}</p>
                  </div>
                  <p className="mono" style={{ margin: 0, fontSize: 20, fontWeight: 800, color: 'var(--pp-success)' }}>${p.amount?.toLocaleString()}</p>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button data-testid={`admin-payout-approve-${p.payout_id}`}
                    onClick={async () => {
                      await api.adminApprovePayout(p.funded_account_id, p.payout_id).catch(e => toast.error(e.message));
                      loadTabData('funded');
                      toast.success('Payout approved! New account record created.');
                    }} className="btn-primary" style={{ flex: 1, height: 44 }}>
                    APPROVE + CREATE NEW ACCOUNT
                  </button>
                  <button data-testid={`admin-payout-deny-${p.payout_id}`}
                    onClick={() => {
                      setDenyModal(p);
                      setDenyForm({ reason_code: (denyReasons[0]?.code || 'consistency_rule_failed'), reason_detail: '' });
                    }}
                    style={{ height: 44, padding: '0 18px', background: 'transparent', border: '1px solid #FF3B7A', color: '#FF3B7A', fontWeight: 700, fontSize: 12, letterSpacing: '0.08em', cursor: 'pointer' }}>
                    DENY
                  </button>
                </div>
              </div>
            ))}

            {/* Denied + paid payout history — lightweight read-only card so admins can review decisions */}
            {adminPayouts.filter(p => p.status === 'denied').slice(0, 5).map(p => (
              <div key={p.payout_id} style={{ background: 'rgba(255,59,122,0.06)', border: '1px solid rgba(255,59,122,0.25)', padding: 12, marginBottom: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <p style={{ margin: 0, fontWeight: 700, fontSize: 12, color: '#FF3B7A', letterSpacing: '0.06em' }}>DENIED · {p.user_name}</p>
                    <p style={{ margin: '3px 0 0', fontSize: 11, color: 'var(--pp-text-3)' }}>{p.deny_reason_full || p.deny_reason_label || 'No reason recorded'}</p>
                  </div>
                  <p className="mono" style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#FF3B7A' }}>${(p.amount || 0).toLocaleString()}</p>
                </div>
              </div>
            ))}


            {adminFundedAccounts.map(fa => {
              const isEditing = fundedEditId === fa.funded_account_id;
              return (
                <div key={fa.funded_account_id} style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: 14, marginBottom: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                    <div style={{ flex: 1 }}>
                      <p style={{ margin: 0, fontWeight: 700, fontSize: 14 }}>{fa.label}</p>
                      <p style={{ margin: '3px 0 0', fontSize: 12, color: 'var(--pp-text-3)' }}>{fa.user_name} · {fa.user_email}</p>
                      <p style={{ margin: '3px 0 0', fontSize: 11, color: 'var(--pp-text-3)' }}>${(fa.size || 0).toLocaleString()} · {fa.server}</p>
                      {fa.mt5_login && <p style={{ margin: '3px 0 0', fontSize: 11, color: 'var(--pp-accent)', fontFamily: 'monospace' }}>MT5: {fa.mt5_login} @ {fa.mt5_server}</p>}
                      {(fa.strategy_names || []).length > 0 && <p style={{ margin: '3px 0 0', fontSize: 11, color: 'var(--pp-success)' }}>Copying: {fa.strategy_names.join(', ')}</p>}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
                      <span className={fa.status === 'active' ? 'badge-success' : fa.status === 'pending_creation' ? 'badge-warning' : 'badge-accent'}>{(fa.status || '').replace('_', ' ').toUpperCase()}</span>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button onClick={async () => {
                          const opening = fundedEditId !== fa.funded_account_id;
                          setFundedEditId(opening ? fa.funded_account_id : null);
                          setFundedEditForm({ status: fa.status, mt5_login: fa.mt5_login || '', mt5_password: '', mt5_server: fa.server || 'Chimara-SRV01', label: fa.label, notes: fa.notes || '', size: fa.size || 0, account_type: fa.account_type || 'instant', daily_drawdown_limit_pct: fa.daily_drawdown_limit_pct ?? 5, max_drawdown_limit_pct: fa.max_drawdown_limit_pct ?? 10, min_profit_target_pct: fa.min_profit_target_pct ?? 6, consistency_pct: fa.consistency_pct ?? 15, total_profit: fa.total_profit ?? 0, largest_day_profit_usd: fa.largest_day_profit_usd ?? 0, payout_split_overrides: (fa.payout_split_overrides || []).join(','),
                            asset_class: fa.asset_class || 'futures',
                            backend_engine_kind: fa.backend_engine_kind || 'none',
                            backend_engine_id:   fa.backend_engine_id   || '',
                            backend_engine_mode: fa.backend_engine_mode || 'fire_only',
                            metaapi_link_mode: 'existing', mt_login: '', mt_password: '', mt_server: '', mt_platform: 'mt5', mt_label: '',
                          });
                          if (opening && fa.is_simulated) {
                            try { const accts = await api.getAdminBrokerAccounts(); setNativeEngineConns(accts || []); } catch (e) { setNativeEngineConns([]); }
                          }
                        }} style={{ fontSize: 11, background: 'none', border: '1px solid var(--pp-border)', color: 'var(--pp-text-3)', padding: '4px 10px', cursor: 'pointer' }}>{isEditing ? 'Cancel' : 'Edit'}</button>
                        {fa.status === 'applied' && !fa.is_simulated && !fa.native_funded_account_id && (
                          <button
                            data-testid={`funded-approve-${fa.funded_account_id}`}
                            title="Approve this application and auto-create the matching Native simulated trading account. Student will be emailed."
                            onClick={async () => {
                              if (!window.confirm(`Approve "${fa.label}" and create a Native simulated account for ${fa.user_name}?\n\n• Native account inherits size + account_type from this application\n• Student receives an approval email\n• You can attach an MT4/MT5/Tradovate backend bridge after via Edit\n\nProceed?`)) return;
                              try {
                                const r = await api.approveFundedApplication(fa.funded_account_id, { notify_student: true });
                                toast.success(`Approved! Native: ${r.native?.label || r.native_funded_account_id}`);
                                loadTabData('funded');
                              } catch (e) { toast.error(e.message); }
                            }}
                            style={{ fontSize: 11, background: 'rgba(0,255,148,0.14)', border: '1px solid rgba(0,255,148,0.5)', color: '#00FF94', padding: '4px 10px', cursor: 'pointer', fontWeight: 700, letterSpacing: '0.06em' }}>✓ APPROVE & CREATE NATIVE</button>
                        )}
                        {fa.native_funded_account_id && (
                          <span title={`Linked native: ${fa.native_funded_account_id}`} style={{ fontSize: 10, background: 'rgba(0,255,148,0.1)', color: '#00FF94', padding: '4px 8px', fontWeight: 700, letterSpacing: '0.08em' }}>✓ NATIVE PROVISIONED</span>
                        )}
                        {!fa.is_simulated && (
                          <button data-testid={`funded-convert-${fa.funded_account_id}`}
                            title="Convert MT5 → Native simulated. Keeps balance/equity/progress, mirrors open + closed trades."
                            onClick={async () => {
                              if (!window.confirm(`Convert "${fa.label}" to a Native Simulated account?\n\n• MT5 link removed\n• Balance/equity/progress preserved\n• Open positions mirrored as paper trades\n• Closed-deal history backfilled\n• Future signals route through paper engine\n\nNo emails fire. This is non-destructive (legacy MT5 link is saved for audit).`)) return;
                              try {
                                const r = await api.convertFaToNative(fa.funded_account_id);
                                toast.success(`Converted! ${r.open_positions_mirrored || 0} open + ${r.closed_deals_mirrored || 0} closed mirrored.`);
                                loadTabData('funded');
                              } catch (e) { toast.error(e.message); }
                            }}
                            style={{ fontSize: 11, background: 'rgba(0,255,148,0.1)', border: '1px solid rgba(0,255,148,0.4)', color: '#00FF94', padding: '4px 10px', cursor: 'pointer', fontWeight: 700 }}>↪ NATIVE</button>
                        )}
                        {fa.is_simulated && (
                          <span style={{ fontSize: 10, background: 'rgba(0,255,148,0.15)', color: '#00FF94', padding: '4px 8px', fontWeight: 700, letterSpacing: '0.08em' }}>🌐 NATIVE</span>
                        )}
                        {/* Phase toggle — MIGRATING ⇄ LIVE. While migrating, the trader sees a MIGRATING badge instead of the underlying broker server name. */}
                        <button
                          data-testid={`funded-phase-${fa.funded_account_id}`}
                          title={fa.account_phase === 'live' ? 'Flip back to MIGRATING (hide server name from trader)' : 'Mark this account LIVE (expose server name to trader)'}
                          onClick={async () => {
                            const target = fa.account_phase === 'live' ? 'migrating' : 'live';
                            try {
                              await api.adminSetFundedPhase(fa.funded_account_id, target);
                              toast.success(`Phase → ${target.toUpperCase()}`);
                              loadTabData('funded');
                            } catch (e) { toast.error(e.message); }
                          }}
                          style={{ fontSize: 10, background: fa.account_phase === 'live' ? 'rgba(0,255,148,0.12)' : 'rgba(255,176,0,0.12)', border: `1px solid ${fa.account_phase === 'live' ? 'rgba(0,255,148,0.45)' : 'rgba(255,176,0,0.45)'}`, color: fa.account_phase === 'live' ? '#00FF94' : '#FFB000', padding: '4px 8px', cursor: 'pointer', fontWeight: 700, letterSpacing: '0.08em' }}>
                          {fa.account_phase === 'live' ? '● LIVE' : '⚙ MIGRATING'}
                        </button>
                        <button
                          data-testid={`funded-delete-${fa.funded_account_id}`}
                          title="Delete this funded account permanently"
                          onClick={async () => {
                            if (!window.confirm(`PERMANENTLY DELETE this funded account?\n\n${fa.label}\nUser: ${fa.user_name}\nSize: $${(fa.size||0).toLocaleString()}\nStatus: ${fa.status}\n\nThis cannot be undone.`)) return;
                            try {
                              await api.adminDeleteFundedAccount(fa.funded_account_id);
                              toast.success(`Deleted "${fa.label}"`);
                              loadTabData('funded');
                            } catch (e) { toast.error(e.message); }
                          }}
                          style={{ fontSize: 11, background: 'transparent', border: '1px solid var(--pp-error)', color: 'var(--pp-error)', padding: '4px 10px', cursor: 'pointer', fontWeight: 700 }}
                        >Delete</button>
                      </div>
                    </div>
                  </div>

                  {/* Strategy subscribe from admin */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
                    {strategies.map(s => {
                      const subscribed = (fa.strategy_subscriptions || []).includes(s.strategy_id);
                      return (
                        <button key={s.strategy_id} onClick={async () => { await api.adminSubscribeFundedAccount(fa.funded_account_id, s.strategy_id).catch(e => toast.error(e.message)); loadTabData('funded'); }} style={{ padding: '4px 10px', background: subscribed ? 'var(--pp-primary)' : 'transparent', border: `1px solid ${subscribed ? 'var(--pp-primary)' : 'var(--pp-border)'}`, color: subscribed ? '#fff' : 'var(--pp-text-3)', fontSize: 10, cursor: 'pointer', fontWeight: subscribed ? 700 : 400 }}>
                          {subscribed ? '✓ ' : ''}{s.name}
                        </button>
                      );
                    })}
                  </div>

                  {isEditing && (
                    <div style={{ borderTop: '1px solid var(--pp-border)', paddingTop: 14 }}>
                      <p style={{ fontSize: 10, letterSpacing: '0.15em', fontWeight: 700, color: 'var(--pp-text-3)', marginBottom: 8 }}>ACCOUNT DETAILS</p>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                        <div><label style={LABEL}>Account Size ($)</label><input style={INPUT} type="number" step="1000" value={fundedEditForm.size ?? ''} onChange={e => setFundedEditForm(p => ({ ...p, size: e.target.value }))} placeholder="100000" /></div>
                        <div><label style={LABEL}>Account Type</label>
                          <select style={{ ...INPUT, cursor: 'pointer' }} value={fundedEditForm.account_type || 'instant'} onChange={e => setFundedEditForm(p => ({ ...p, account_type: e.target.value }))}>
                            <option value="instant">Instant Funded</option>
                            <option value="evaluation">Evaluation / Challenge</option>
                          </select>
                        </div>
                        <div><label style={LABEL}>MT5 Login</label><input style={INPUT} value={fundedEditForm.mt5_login || ''} onChange={e => setFundedEditForm(p => ({ ...p, mt5_login: e.target.value }))} placeholder="e.g. 40013617" /></div>
                        <div><label style={LABEL}>MT5 Password</label><input style={{ ...INPUT, fontFamily: 'monospace' }} type="password" value={fundedEditForm.mt5_password || ''} onChange={e => setFundedEditForm(p => ({ ...p, mt5_password: e.target.value }))} placeholder="Leave blank to keep" /></div>
                        <div><label style={LABEL}>Server</label><input style={INPUT} value={fundedEditForm.mt5_server || ''} onChange={e => setFundedEditForm(p => ({ ...p, mt5_server: e.target.value }))} /></div>
                        <div><label style={LABEL}>Status</label>
                          <select style={{ ...INPUT, cursor: 'pointer' }} value={fundedEditForm.status || ''} onChange={e => setFundedEditForm(p => ({ ...p, status: e.target.value }))}>
                            {['pending_creation','applied','approved','active','payout_requested','completed'].map(s => <option key={s} value={s}>{s}</option>)}
                          </select>
                        </div>
                      </div>
                      <p style={{ fontSize: 10, letterSpacing: '0.15em', fontWeight: 700, color: 'var(--pp-text-3)', marginBottom: 8 }}>PROFIT TRACKING</p>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10, marginBottom: 10 }}>
                        <div><label style={LABEL}>Total Profit ($)</label><input style={INPUT} type="number" step="0.01" value={fundedEditForm.total_profit ?? ''} onChange={e => setFundedEditForm(p => ({ ...p, total_profit: e.target.value }))} placeholder="Total $ profit so far" /></div>
                        <div><label style={LABEL}>Largest Winning Day ($)</label><input style={INPUT} type="number" step="0.01" value={fundedEditForm.largest_day_profit_usd ?? ''} onChange={e => setFundedEditForm(p => ({ ...p, largest_day_profit_usd: e.target.value }))} placeholder="For consistency rule" /></div>
                        <div title="Optional comma-separated splits, e.g. 50,60,70,80. Leave blank for default."><label style={LABEL}>Payout Splits Override</label><input style={INPUT} value={fundedEditForm.payout_split_overrides ?? ''} onChange={e => setFundedEditForm(p => ({ ...p, payout_split_overrides: e.target.value }))} placeholder="50,60,70,80" /></div>
                      </div>
                      <p style={{ fontSize: 10, letterSpacing: '0.15em', fontWeight: 700, color: 'var(--pp-text-3)', marginBottom: 8 }}>PROP FIRM RULES</p>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginBottom: 10 }}>
                        <div><label style={LABEL}>Daily DD Limit (%)</label><input style={INPUT} type="number" step="0.1" value={fundedEditForm.daily_drawdown_limit_pct ?? ''} onChange={e => setFundedEditForm(p => ({ ...p, daily_drawdown_limit_pct: e.target.value }))} /></div>
                        <div><label style={LABEL}>Max DD Limit (%)</label><input style={INPUT} type="number" step="0.1" value={fundedEditForm.max_drawdown_limit_pct ?? ''} onChange={e => setFundedEditForm(p => ({ ...p, max_drawdown_limit_pct: e.target.value }))} /></div>
                        <div><label style={LABEL}>Min Profit Target (%)</label><input style={INPUT} type="number" step="0.1" value={fundedEditForm.min_profit_target_pct ?? ''} onChange={e => setFundedEditForm(p => ({ ...p, min_profit_target_pct: e.target.value }))} /></div>
                        <div><label style={LABEL}>Consistency (%)</label><input style={INPUT} type="number" step="0.1" value={fundedEditForm.consistency_pct ?? ''} onChange={e => setFundedEditForm(p => ({ ...p, consistency_pct: e.target.value }))} /></div>
                      </div>
                      <div style={{ marginBottom: 10 }}><label style={LABEL}>Notes</label><input style={INPUT} value={fundedEditForm.notes || ''} onChange={e => setFundedEditForm(p => ({ ...p, notes: e.target.value }))} placeholder="Internal notes..." /></div>

                      {/* ── Backend Bridge editor (Native sim accounts only) ── */}
                      {fa.is_simulated && (
                        <div style={{ marginTop: 14, padding: 12, border: '1px dashed rgba(0,255,148,0.35)', background: 'rgba(0,255,148,0.04)' }}>
                          <p style={{ margin: 0, fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', color: '#00FF94' }}>BACKEND BRIDGE (REAL BROKER LINK)</p>
                          <p style={{ margin: '4px 0 10px', fontSize: 11, color: 'var(--pp-text-3)' }}>Bind / change the real broker connection that mirrors every fill on this Native account. Pick from existing connections or enter MT4/MT5 creds — a MetaAPI cloud account is provisioned on the fly. Set to <strong>None</strong> for pure paper.</p>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                            <div>
                              <label style={LABEL}>Engine Kind</label>
                              <select
                                data-testid={`edit-bridge-kind-${fa.funded_account_id}`}
                                style={{ ...INPUT, cursor: 'pointer' }}
                                value={fundedEditForm.backend_engine_kind || 'none'}
                                onChange={async e => {
                                  const k = e.target.value;
                                  setFundedEditForm(p => ({ ...p, backend_engine_kind: k, backend_engine_id: '' }));
                                  if (k !== 'none' && nativeEngineConns.length === 0) {
                                    try { const accts = await api.getAdminBrokerAccounts(); setNativeEngineConns(accts || []); } catch (err) { setNativeEngineConns([]); }
                                  }
                                }}
                              >
                                <option value="none">None (pure simulated)</option>
                                <option value="volumetrica">Volumetrica / AquaFutures</option>
                                <option value="metaapi">MetaAPI (MT4/MT5)</option>
                                <option value="tradelocker">TradeLocker</option>
                                <option value="bybit">Bybit</option>
                                <option value="matchtrader">MatchTrader</option>
                                <option value="tradovate">Tradovate</option>
                              </select>
                            </div>
                            <div>
                              <label style={LABEL}>Mode</label>
                              <select
                                style={{ ...INPUT, cursor: 'pointer', opacity: (fundedEditForm.backend_engine_kind || 'none') === 'none' ? 0.4 : 1 }}
                                disabled={(fundedEditForm.backend_engine_kind || 'none') === 'none'}
                                value={fundedEditForm.backend_engine_mode || 'fire_only'}
                                onChange={e => setFundedEditForm(p => ({ ...p, backend_engine_mode: e.target.value }))}
                              >
                                <option value="fire_only">Fire only (balance stays simulated)</option>
                                <option value="mirror_state">Mirror real fills (sync state from broker)</option>
                              </select>
                            </div>
                          </div>

                          {(fundedEditForm.backend_engine_kind || 'none') !== 'none' && (() => {
                            const kindLabelMap = { volumetrica: 'Volumetrica', metaapi: 'MetaAPI', tradelocker: 'TradeLocker', bybit: 'Bybit', matchtrader: 'MatchTrader', tradovate: 'Tradovate' };
                            const wantedLabel = kindLabelMap[fundedEditForm.backend_engine_kind] || '';
                            const matching = (nativeEngineConns || []).filter(a => (a.broker_type || '').toLowerCase() === wantedLabel.toLowerCase() && (a.user_id === fa.user_id));
                            const matchingAll = (nativeEngineConns || []).filter(a => (a.broker_type || '').toLowerCase() === wantedLabel.toLowerCase());
                            const list = matching.length ? matching : matchingAll;
                            const isMetaAPI = fundedEditForm.backend_engine_kind === 'metaapi';
                            return (
                              <div style={{ marginTop: 10 }}>
                                {isMetaAPI && (
                                  <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
                                    <button type="button" onClick={() => setFundedEditForm(p => ({ ...p, metaapi_link_mode: 'existing', backend_engine_id: '' }))} style={{ flex: 1, height: 28, fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', cursor: 'pointer', background: (fundedEditForm.metaapi_link_mode || 'existing') === 'existing' ? '#00FF94' : 'transparent', color: (fundedEditForm.metaapi_link_mode || 'existing') === 'existing' ? '#000' : 'var(--pp-text)', border: '1px solid rgba(0,255,148,0.4)' }}>USE EXISTING CONNECTION</button>
                                    <button type="button" onClick={() => setFundedEditForm(p => ({ ...p, metaapi_link_mode: 'manual', backend_engine_id: '' }))} style={{ flex: 1, height: 28, fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', cursor: 'pointer', background: fundedEditForm.metaapi_link_mode === 'manual' ? '#00FF94' : 'transparent', color: fundedEditForm.metaapi_link_mode === 'manual' ? '#000' : 'var(--pp-text)', border: '1px solid rgba(0,255,148,0.4)' }}>ENTER MT4/MT5 LOGIN MANUALLY</button>
                                  </div>
                                )}

                                {isMetaAPI && fundedEditForm.metaapi_link_mode === 'manual' ? (
                                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                                    <div><label style={LABEL}>MT Login</label><input style={INPUT} placeholder="e.g. 1234567" value={fundedEditForm.mt_login || ''} onChange={e => setFundedEditForm(p => ({ ...p, mt_login: e.target.value }))} /></div>
                                    <div><label style={LABEL}>Server</label><input style={INPUT} placeholder="e.g. Chimara-SRV01" value={fundedEditForm.mt_server || ''} onChange={e => setFundedEditForm(p => ({ ...p, mt_server: e.target.value }))} /></div>
                                    <div style={{ gridColumn: '1 / -1' }}><label style={LABEL}>Password</label><input style={INPUT} type="password" placeholder="MT5 / MT4 password" value={fundedEditForm.mt_password || ''} onChange={e => setFundedEditForm(p => ({ ...p, mt_password: e.target.value }))} /></div>
                                    <div><label style={LABEL}>Platform</label>
                                      <select style={{ ...INPUT, cursor: 'pointer' }} value={fundedEditForm.mt_platform || 'mt5'} onChange={e => setFundedEditForm(p => ({ ...p, mt_platform: e.target.value }))}>
                                        <option value="mt5">MT5</option>
                                        <option value="mt4">MT4</option>
                                      </select>
                                    </div>
                                    <div><label style={LABEL}>Label (optional)</label><input style={INPUT} placeholder="Friendly name" value={fundedEditForm.mt_label || ''} onChange={e => setFundedEditForm(p => ({ ...p, mt_label: e.target.value }))} /></div>
                                    <p style={{ gridColumn: '1 / -1', margin: 0, fontSize: 10, color: 'var(--pp-text-3)' }}>A new MetaAPI cloud account is provisioned in the background (~1-2 min) and bound to this Native account. No historical backfill runs.</p>
                                  </div>
                                ) : (
                                  <>
                                    <label style={LABEL}>{wantedLabel} Connection ({list.length} available{matching.length === 0 && matchingAll.length > 0 ? ' — none for this student, showing all' : ''})</label>
                                    {list.length > 0 ? (
                                      <select style={{ ...INPUT, cursor: 'pointer' }} value={fundedEditForm.backend_engine_id || ''} onChange={e => setFundedEditForm(p => ({ ...p, backend_engine_id: e.target.value }))}>
                                        <option value="">— Select connection —</option>
                                        {list.map(c => (
                                          <option key={c.connection_id} value={c.connection_id}>{c.account_label || c.login || c.connection_id} · {c.user_name || c.user_email || c.user_id?.slice(0,8)} · {c.status || '?'}</option>
                                        ))}
                                      </select>
                                    ) : (
                                      <input style={INPUT} placeholder={`No ${wantedLabel} connections found — paste connection_id manually`} value={fundedEditForm.backend_engine_id || ''} onChange={e => setFundedEditForm(p => ({ ...p, backend_engine_id: e.target.value }))} />
                                    )}
                                  </>
                                )}
                              </div>
                            );
                          })()}
                          {fa.backend_engine_kind && fa.backend_engine_id && (
                            <p style={{ margin: '8px 0 0', fontSize: 10, color: 'var(--pp-text-3)' }}>Current: <span style={{ color: '#00FF94', fontWeight: 700 }}>{fa.backend_engine_kind}</span> · {fa.backend_engine_id} · mode={fa.backend_engine_mode || 'fire_only'}</p>
                          )}
                        </div>
                      )}

                      <button onClick={async () => {
                        try {
                          // Convert payout_split_overrides string → array of numbers
                          const payload = { ...fundedEditForm };
                          if (typeof payload.payout_split_overrides === 'string') {
                            const parts = payload.payout_split_overrides.split(',').map(s => parseFloat(s.trim())).filter(n => !Number.isNaN(n) && n > 0);
                            payload.payout_split_overrides = parts.length > 0 ? parts : null;
                          }
                          // Coerce raw text inputs back to numbers right before sending.
                          // Empty strings become null so the backend keeps the existing value.
                          const numKeys = ['size','total_profit','largest_day_profit_usd','daily_drawdown_limit_pct','max_drawdown_limit_pct','min_profit_target_pct','consistency_pct'];
                          numKeys.forEach(k => {
                            const v = payload[k];
                            if (v === '' || v === null || v === undefined) {
                              delete payload[k];
                            } else if (typeof v === 'string') {
                              const n = parseFloat(v);
                              payload[k] = Number.isFinite(n) ? n : 0;
                            }
                          });
                          // ── Backend bridge: bake metaapi_manual into payload when admin
                          //    chose "enter manually" mode, OR strip it otherwise.
                          const kind = (payload.backend_engine_kind || 'none').toLowerCase();
                          const isManualMT = kind === 'metaapi' && payload.metaapi_link_mode === 'manual';
                          if (isManualMT) {
                            if (!(payload.mt_login && payload.mt_password && payload.mt_server)) {
                              toast.error('Backend Bridge: enter MT login, password, and server'); return;
                            }
                            payload.metaapi_manual = {
                              login: payload.mt_login, password: payload.mt_password,
                              server: payload.mt_server, platform: payload.mt_platform || 'mt5',
                              label: payload.mt_label || '',
                            };
                            delete payload.backend_engine_id;
                          } else if (kind !== 'none' && !payload.backend_engine_id) {
                            toast.error('Backend Bridge: pick a connection or set Engine Kind to None'); return;
                          }
                          // Strip UI-only helper fields
                          ['metaapi_link_mode','mt_login','mt_password','mt_server','mt_platform','mt_label'].forEach(k => delete payload[k]);
                          await api.updateAdminFundedAccount(fa.funded_account_id, payload);
                          loadTabData('funded'); setFundedEditId(null); toast.success('Account updated!');
                        } catch (e) { toast.error(e.message); }
                      }} className="btn-primary" style={{ width: '100%', height: 44 }}>SAVE CHANGES</button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* PAYMENTS */}
        {!loading && activeTab === 'payments' && (
          <div data-testid="admin-payments-tab">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginBottom: 16 }}>
              <div style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: 14 }}>
                <p style={{ margin: 0, fontSize: 10, color: 'var(--pp-text-3)', letterSpacing: '0.15em', fontWeight: 700 }}>TOTAL REVENUE</p>
                <p style={{ margin: '6px 0 0', fontSize: 22, fontFamily: 'monospace', fontWeight: 700, color: 'var(--pp-success)' }}>${paymentsStats.total_revenue_usd || 0}</p>
              </div>
              <div style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: 14 }}>
                <p style={{ margin: 0, fontSize: 10, color: 'var(--pp-text-3)', letterSpacing: '0.15em', fontWeight: 700 }}>COMPLETED</p>
                <p style={{ margin: '6px 0 0', fontSize: 22, fontFamily: 'monospace', fontWeight: 700 }}>{paymentsStats.completed || 0}</p>
              </div>
              <div style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: 14 }}>
                <p style={{ margin: 0, fontSize: 10, color: 'var(--pp-text-3)', letterSpacing: '0.15em', fontWeight: 700 }}>PENDING</p>
                <p style={{ margin: '6px 0 0', fontSize: 22, fontFamily: 'monospace', fontWeight: 700, color: '#FFB000' }}>{paymentsStats.pending || 0}</p>
              </div>
              <div style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: 14 }}>
                <p style={{ margin: 0, fontSize: 10, color: 'var(--pp-text-3)', letterSpacing: '0.15em', fontWeight: 700 }}>FAILED</p>
                <p style={{ margin: '6px 0 0', fontSize: 22, fontFamily: 'monospace', fontWeight: 700, color: 'var(--pp-error)' }}>{paymentsStats.failed || 0}</p>
              </div>
            </div>

            <p className="section-label" style={{ marginBottom: 10 }}>Payment History ({adminPayments.length})</p>
            {adminPayments.length === 0 ? (
              <div style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: 20, textAlign: 'center' }}>
                <p style={{ color: 'var(--pp-text-3)', margin: 0, fontSize: 13 }}>No payments yet</p>
              </div>
            ) : (
              <div style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: 'var(--pp-overlay)' }}>
                      {['Student', 'Plan', 'Amount', 'Currency', 'Status', 'Date'].map(h => (
                        <th key={h} style={{ padding: '10px 12px', textAlign: 'left', color: 'var(--pp-text-3)', fontWeight: 700, fontSize: 10, letterSpacing: '0.15em', textTransform: 'uppercase' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {adminPayments.map(p => (
                      <tr key={p.id} data-testid={`payment-row-${p.id}`} style={{ borderTop: '1px solid var(--pp-border)' }}>
                        <td style={{ padding: '10px 12px' }}>
                          <div style={{ fontWeight: 600 }}>{p.user_name || '—'}</div>
                          <div style={{ fontSize: 10, color: 'var(--pp-text-3)' }}>{p.user_email}</div>
                        </td>
                        <td style={{ padding: '10px 12px', fontSize: 11 }}>{p.plan_label}</td>
                        <td style={{ padding: '10px 12px', fontFamily: 'monospace', fontWeight: 700 }}>${p.amount_usd}{p.discount_percent ? <span style={{ color: 'var(--pp-success)', fontSize: 10, marginLeft: 4 }}>-{p.discount_percent}%</span> : null}</td>
                        <td style={{ padding: '10px 12px', fontFamily: 'monospace' }}>{(p.pay_currency || '').toUpperCase()}</td>
                        <td style={{ padding: '10px 12px' }}>
                          <span className={p.status === 'finished' || p.status === 'confirmed' ? 'badge-success' : p.status === 'waiting' || p.status === 'confirming' ? 'badge-warning' : 'badge-accent'} style={{ fontSize: 9 }}>{(p.status || '').toUpperCase()}</span>
                        </td>
                        <td style={{ padding: '10px 12px', color: 'var(--pp-text-3)', fontSize: 11 }}>{new Date(p.created_at).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* DISCOUNT REQUESTS */}
        {!loading && activeTab === 'discounts' && (
          <div data-testid="admin-discounts-tab">
            <p className="section-label" style={{ marginBottom: 12 }}>Discount Requests ({discountRequests.length})</p>
            {discountRequests.length === 0 ? (
              <div style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: 20, textAlign: 'center' }}>
                <p style={{ color: 'var(--pp-text-3)', margin: 0 }}>No discount requests yet</p>
              </div>
            ) : (
              discountRequests.map(req => (
                <div key={req.id} data-testid={`discount-request-${req.id}`} style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: 14, marginBottom: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                    <div>
                      <p style={{ margin: 0, fontWeight: 700, fontSize: 14 }}>{req.user_name}</p>
                      <p style={{ margin: '3px 0 0', fontSize: 12, color: 'var(--pp-text-3)' }}>{req.user_email}</p>
                      <p style={{ margin: '8px 0 0', fontSize: 11, color: 'var(--pp-text-3)' }}>Requested: <strong style={{ color: 'var(--pp-accent)' }}>{req.requested_percent}%</strong> off · Plan: {req.plan_id}</p>
                    </div>
                    <span className={req.status === 'approved' ? 'badge-success' : req.status === 'denied' ? 'badge-error' : 'badge-warning'} style={{ fontSize: 10 }}>{(req.status || '').toUpperCase()}</span>
                  </div>
                  {req.reason && (
                    <div style={{ background: 'var(--pp-overlay)', padding: 10, marginBottom: 12, fontSize: 12, color: 'var(--pp-text-2)', lineHeight: 1.5, border: '1px solid var(--pp-border)' }}>
                      <p style={{ margin: 0, fontSize: 10, letterSpacing: '0.1em', color: 'var(--pp-text-3)', fontWeight: 700, marginBottom: 4 }}>REASON</p>
                      {req.reason}
                    </div>
                  )}
                  {req.status === 'approved' && req.code && (
                    <div style={{ padding: 10, background: 'rgba(0,255,148,0.08)', border: '1px solid rgba(0,255,148,0.3)', marginBottom: 10 }}>
                      <p style={{ margin: 0, fontSize: 11, color: 'var(--pp-success)' }}>Code issued: <strong style={{ fontFamily: 'monospace' }}>{req.code}</strong> ({req.approved_percent}%) — emailed to {req.user_email}</p>
                    </div>
                  )}
                  {req.status === 'pending' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <label style={{ fontSize: 10, letterSpacing: '0.1em', color: 'var(--pp-text-3)', fontWeight: 700, whiteSpace: 'nowrap' }}>APPROVE %</label>
                        <input data-testid={`discount-pct-${req.id}`} type="number" min={1} max={100} style={{ ...INPUT, width: 80 }} value={discountApprovalPct[req.id] ?? req.requested_percent} onChange={e => setDiscountApprovalPct(p => ({ ...p, [req.id]: e.target.value }))} />
                        <input data-testid={`discount-msg-${req.id}`} placeholder="Optional message to user..." style={{ ...INPUT, flex: 1 }} value={discountMessage[req.id] || ''} onChange={e => setDiscountMessage(p => ({ ...p, [req.id]: e.target.value }))} />
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button data-testid={`approve-discount-${req.id}`} onClick={async () => {
                          const pct = Number(discountApprovalPct[req.id] ?? req.requested_percent);
                          try {
                            const r = await api.adminApproveDiscount(req.id, pct, discountMessage[req.id] || '');
                            toast.success(`Code ${r.code} (${pct}%) emailed to ${req.user_email}`);
                            loadTabData('discounts');
                          } catch (e) { toast.error(e.message); }
                        }} style={{ flex: 1, height: 38, background: 'var(--pp-success)', border: 'none', color: '#000', fontWeight: 700, fontSize: 11, cursor: 'pointer', letterSpacing: '0.1em' }}>APPROVE & EMAIL CODE</button>
                        <button data-testid={`deny-discount-${req.id}`} onClick={async () => {
                          try {
                            await api.adminDenyDiscount(req.id, discountMessage[req.id] || '');
                            toast.success('Request denied');
                            loadTabData('discounts');
                          } catch (e) { toast.error(e.message); }
                        }} style={{ padding: '0 20px', height: 38, background: 'transparent', border: '1px solid var(--pp-error)', color: 'var(--pp-error)', fontWeight: 700, fontSize: 11, cursor: 'pointer', letterSpacing: '0.1em' }}>DENY</button>
                      </div>
                    </div>
                  )}
                  <p style={{ margin: '8px 0 0', fontSize: 10, color: 'var(--pp-text-3)' }}>Requested {new Date(req.created_at).toLocaleString()}</p>
                </div>
              ))
            )}
          </div>
        )}

        {/* FUNDED AS REQUESTS (prepaid verification inbox) */}
        {!loading && activeTab === 'funded-requests' && (
          <div data-testid="admin-funded-requests-tab">
            <p className="section-label" style={{ marginBottom: 12 }}>Funded Account — AS Passcode Requests ({fundedAsRequests.length})</p>
            <p style={{ margin: '0 0 14px', fontSize: 12, color: 'var(--pp-text-3)', lineHeight: 1.5 }}>
              Students claiming prior payment for their funded account. Approve as paid (instant activation) OR approve with a custom charge (emails them a crypto payment link for the amount you specify).
            </p>
            {fundedAsRequests.length === 0 ? (
              <div style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: 20, textAlign: 'center' }}>
                <p style={{ color: 'var(--pp-text-3)', margin: 0 }}>No funded AS requests yet</p>
              </div>
            ) : (
              fundedAsRequests.map(req => (
                <div key={req.id} data-testid={`funded-request-${req.id}`} style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: 14, marginBottom: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                    <div>
                      <p style={{ margin: 0, fontWeight: 700, fontSize: 14 }}>{req.user_name}</p>
                      <p style={{ margin: '3px 0 0', fontSize: 12, color: 'var(--pp-text-3)' }}>{req.user_email}</p>
                      <div style={{ display: 'flex', gap: 14, marginTop: 8, flexWrap: 'wrap' }}>
                        <p style={{ margin: 0, fontSize: 11 }}><span style={{ color: 'var(--pp-text-3)' }}>SIZE:</span> <strong style={{ color: 'var(--pp-success)' }}>${req.account_size?.toLocaleString()}</strong></p>
                        <p style={{ margin: 0, fontSize: 11 }}><span style={{ color: 'var(--pp-text-3)' }}>AS PASSCODE:</span> <strong style={{ fontFamily: 'monospace', color: 'var(--pp-accent)' }}>{req.as_passcode}</strong></p>
                      </div>
                    </div>
                    <span className={req.status === 'approved_paid' ? 'badge-success' : req.status === 'approved_charge' ? 'badge-accent' : req.status === 'denied' ? 'badge-error' : 'badge-warning'} style={{ fontSize: 10 }}>
                      {req.status === 'approved_paid' ? 'APPROVED — PAID' :
                       req.status === 'approved_charge' ? 'APPROVED — CHARGED' :
                       (req.status || 'PENDING').toUpperCase()}
                    </span>
                  </div>
                  {req.notes && (
                    <div style={{ background: 'var(--pp-overlay)', padding: 10, marginBottom: 12, fontSize: 12, color: 'var(--pp-text-2)', lineHeight: 1.5, border: '1px solid var(--pp-border)' }}>
                      <p style={{ margin: 0, fontSize: 10, letterSpacing: '0.1em', color: 'var(--pp-text-3)', fontWeight: 700, marginBottom: 4 }}>STUDENT NOTES</p>
                      {req.notes}
                    </div>
                  )}
                  {req.status === 'approved_paid' && (
                    <div style={{ padding: 10, background: 'rgba(0,255,148,0.08)', border: '1px solid rgba(0,255,148,0.3)', fontSize: 11, color: 'var(--pp-success)' }}>
                      ✓ Approved as prepaid — funded account marked paid, MT5 setup queued for provisioning
                    </div>
                  )}
                  {req.status === 'approved_charge' && (
                    <div style={{ padding: 10, background: 'rgba(41,121,255,0.08)', border: '1px solid rgba(41,121,255,0.3)', fontSize: 11, color: 'var(--pp-accent)' }}>
                      ✓ Approved with charge ${req.custom_amount_usd} — pay link emailed. Charge ID: <strong style={{ fontFamily: 'monospace' }}>{req.charge_id?.slice(0, 8)}</strong>
                    </div>
                  )}
                  {req.status === 'pending' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      <div>
                        <label style={{ fontSize: 10, letterSpacing: '0.1em', color: 'var(--pp-text-3)', fontWeight: 700 }}>ADMIN MESSAGE (OPTIONAL — INCLUDED IN EMAIL)</label>
                        <input data-testid={`funded-msg-${req.id}`} placeholder="e.g. Verified wire from 4/10..." style={{ ...INPUT, marginTop: 4 }} value={fundedMsg[req.id] || ''} onChange={e => setFundedMsg(p => ({ ...p, [req.id]: e.target.value }))} />
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button data-testid={`approve-funded-paid-${req.id}`} onClick={async () => {
                          if (!window.confirm(`Approve as already paid? This will instantly activate ${req.user_name}'s $${req.account_size?.toLocaleString()} funded account.`)) return;
                          try {
                            await api.adminApproveFundedPaid(req.id, fundedMsg[req.id] || '');
                            toast.success(`${req.user_name}: funded account activated as prepaid`);
                            loadTabData('funded-requests');
                          } catch (e) { toast.error(e.message); }
                        }} style={{ flex: 1, height: 38, background: 'var(--pp-success)', border: 'none', color: '#000', fontWeight: 700, fontSize: 11, cursor: 'pointer', letterSpacing: '0.1em' }}>APPROVE — ALREADY PAID</button>
                        <button data-testid={`deny-funded-${req.id}`} onClick={async () => {
                          try {
                            await api.adminDenyFundedAs(req.id, fundedMsg[req.id] || '');
                            toast.success('Request denied');
                            loadTabData('funded-requests');
                          } catch (e) { toast.error(e.message); }
                        }} style={{ padding: '0 20px', height: 38, background: 'transparent', border: '1px solid var(--pp-error)', color: 'var(--pp-error)', fontWeight: 700, fontSize: 11, cursor: 'pointer', letterSpacing: '0.1em' }}>DENY</button>
                      </div>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', paddingTop: 10, borderTop: '1px dashed var(--pp-border)' }}>
                        <label style={{ fontSize: 10, letterSpacing: '0.1em', color: 'var(--pp-text-3)', fontWeight: 700, whiteSpace: 'nowrap' }}>OR CHARGE $</label>
                        <input data-testid={`funded-amt-${req.id}`} type="number" min={1} step="0.01" placeholder="Amount to charge" style={{ ...INPUT, flex: 1 }} value={fundedAmt[req.id] || ''} onChange={e => setFundedAmt(p => ({ ...p, [req.id]: e.target.value }))} />
                        <button data-testid={`approve-funded-charge-${req.id}`} onClick={async () => {
                          const amt = Number(fundedAmt[req.id]);
                          if (!(amt > 0)) { toast.error('Enter a valid amount > 0'); return; }
                          if (!window.confirm(`Approve with charge: email ${req.user_email} a crypto payment link for $${amt}?`)) return;
                          try {
                            const r = await api.adminApproveFundedCharge(req.id, amt, fundedMsg[req.id] || '');
                            toast.success(`Payment link ($${amt}) emailed to ${req.user_email}`);
                            loadTabData('funded-requests');
                          } catch (e) { toast.error(e.message); }
                        }} style={{ padding: '0 16px', height: 38, background: 'var(--pp-accent)', border: 'none', color: '#fff', fontWeight: 700, fontSize: 11, cursor: 'pointer', letterSpacing: '0.1em', whiteSpace: 'nowrap' }}>APPROVE & EMAIL LINK</button>
                      </div>
                    </div>
                  )}
                  <p style={{ margin: '10px 0 0', fontSize: 10, color: 'var(--pp-text-3)' }}>Requested {new Date(req.created_at).toLocaleString()}{req.reviewed_at && ` · Reviewed ${new Date(req.reviewed_at).toLocaleString()}`}</p>
                </div>
              ))
            )}
          </div>
        )}


        {/* ACCESS CODES — Unified Trader + Student tab with power toggles */}
        {!loading && activeTab === 'access-codes' && <AccessCodesPanel />}

        {/* PASSWORD MANAGER — view + reset any user's password */}
        {!loading && activeTab === 'passwords' && <PasswordManagerPanel />}

        {/* AS PASSCODES — Bulk generator + list */}
        {!loading && activeTab === 'as-codes' && (
          <div data-testid="admin-as-codes-tab">
            <div style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: 18, marginBottom: 18 }}>
              <p className="section-label" style={{ marginBottom: 8 }}>Bulk Generate Unclaimed AS Passcodes</p>
              <p style={{ margin: '0 0 14px', fontSize: 12, color: 'var(--pp-text-3)', lineHeight: 1.5 }}>
                Mint a batch of unclaimed AS passcodes to hand out for partner deals, promotions, or prepayment campaigns. Each code activates Full Student access on the first user who redeems it at <code style={{ color: 'var(--pp-accent)' }}>/checkout</code>.
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr 1fr', gap: 10, marginBottom: 12 }}>
                <div>
                  <label style={LABEL}>Count</label>
                  <input data-testid="bulk-count" type="number" min={1} max={500} value={bulkCount} onChange={e => setBulkCount(e.target.value)} style={INPUT} />
                </div>
                <div>
                  <label style={LABEL}>Tag (optional)</label>
                  <input data-testid="bulk-tag" placeholder="PROMO, PARTNER1, Q2..." value={bulkTag} onChange={e => setBulkTag(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g,''))} style={INPUT} />
                </div>
                <div>
                  <label style={LABEL}>Internal note (optional)</label>
                  <input data-testid="bulk-note" placeholder="Partner X Q2 promo, etc." value={bulkNote} onChange={e => setBulkNote(e.target.value)} style={INPUT} />
                </div>
              </div>
              <button data-testid="generate-bulk-btn" disabled={bulkGenerating || !bulkCount || bulkCount < 1} onClick={async () => {
                const c = parseInt(bulkCount);
                if (!(c >= 1 && c <= 500)) { toast.error('Count must be 1-500'); return; }
                setBulkGenerating(true);
                try {
                  const res = await api.adminBulkGenerateASPasscodes(c, bulkTag, bulkNote);
                  setLastGeneratedBatch(res.codes || []);
                  toast.success(`Generated ${res.count} passcodes${bulkTag ? ` (${bulkTag})` : ''}`);
                  loadTabData('as-codes');
                } catch (e) { toast.error(e.message); }
                setBulkGenerating(false);
              }} style={{ width: '100%', height: 44, background: 'var(--pp-success)', border: 'none', color: '#000', fontWeight: 800, fontSize: 12, cursor: 'pointer', letterSpacing: '0.15em' }}>
                {bulkGenerating ? 'GENERATING...' : `MINT ${bulkCount || 0} PASSCODES`}
              </button>

              {lastGeneratedBatch.length > 0 && (
                <div style={{ marginTop: 18, background: 'var(--pp-overlay)', border: '1px solid rgba(0,255,148,0.3)', padding: 14 }} data-testid="last-batch">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <p style={{ margin: 0, fontSize: 11, letterSpacing: '0.15em', fontWeight: 700, color: 'var(--pp-success)' }}>LAST BATCH — {lastGeneratedBatch.length} CODES</p>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button data-testid="copy-batch-btn" onClick={() => { navigator.clipboard.writeText(lastGeneratedBatch.join('\n')); toast.success(`${lastGeneratedBatch.length} codes copied to clipboard`); }} style={{ background: 'transparent', border: '1px solid var(--pp-border)', color: 'var(--pp-text-2)', fontSize: 10, padding: '4px 10px', cursor: 'pointer', fontWeight: 700, letterSpacing: '0.1em' }}>COPY ALL</button>
                      <button data-testid="download-batch-btn" onClick={() => {
                        const blob = new Blob([lastGeneratedBatch.join('\n')], { type: 'text/plain' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `as-passcodes-${bulkTag || 'batch'}-${Date.now()}.txt`;
                        a.click();
                        URL.revokeObjectURL(url);
                      }} style={{ background: 'transparent', border: '1px solid var(--pp-border)', color: 'var(--pp-text-2)', fontSize: 10, padding: '4px 10px', cursor: 'pointer', fontWeight: 700, letterSpacing: '0.1em' }}>DOWNLOAD .TXT</button>
                    </div>
                  </div>
                  <div style={{ maxHeight: 200, overflow: 'auto', background: '#0A0A0A', padding: 10, border: '1px solid var(--pp-border)', fontFamily: 'monospace', fontSize: 12, lineHeight: 1.7 }}>
                    {lastGeneratedBatch.map(c => <div key={c} style={{ color: 'var(--pp-success)' }}>{c}</div>)}
                  </div>
                </div>
              )}
            </div>

            {/* All passcodes list */}
            <p className="section-label" style={{ marginBottom: 10 }}>All AS Passcodes ({asCodes.length})</p>
            {asCodes.length === 0 ? (
              <div style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: 20, textAlign: 'center' }}>
                <p style={{ color: 'var(--pp-text-3)', margin: 0 }}>No passcodes yet. Generate a batch above.</p>
              </div>
            ) : (
              <div style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: 'var(--pp-overlay)' }}>
                      {['Passcode', 'Status', 'Claimed By', 'Tag', 'Issued', 'Actions'].map(h => (
                        <th key={h} style={{ padding: '10px 12px', textAlign: 'left', color: 'var(--pp-text-3)', fontWeight: 700, fontSize: 10, letterSpacing: '0.15em', textTransform: 'uppercase' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {asCodes.map(p => (
                      <tr key={p.passcode_id} data-testid={`ascode-${p.passcode_id}`} style={{ borderTop: '1px solid var(--pp-border)' }}>
                        <td style={{ padding: '10px 12px', fontFamily: 'monospace', fontWeight: 700, color: p.active ? 'var(--pp-success)' : 'var(--pp-text-3)' }}>{p.passcode}</td>
                        <td style={{ padding: '10px 12px' }}>
                          {!p.active ? <span className="badge-error" style={{ fontSize: 9 }}>REVOKED</span>
                            : p.user_id ? <span className="badge-accent" style={{ fontSize: 9 }}>CLAIMED</span>
                            : <span className="badge-success" style={{ fontSize: 9 }}>UNCLAIMED</span>}
                        </td>
                        <td style={{ padding: '10px 12px', fontSize: 11 }}>{p.user_name ? <>{p.user_name}<br/><span style={{ color: 'var(--pp-text-3)', fontSize: 10 }}>{p.user_email}</span></> : <span style={{ color: 'var(--pp-text-3)' }}>—</span>}</td>
                        <td style={{ padding: '10px 12px', fontSize: 11, color: 'var(--pp-text-3)' }}>{p.bulk_tag || '—'}</td>
                        <td style={{ padding: '10px 12px', fontSize: 11, color: 'var(--pp-text-3)' }}>{p.issued_at ? new Date(p.issued_at).toLocaleDateString() : '—'}</td>
                        <td style={{ padding: '10px 12px' }}>
                          {p.active && !p.user_id && (
                            <button data-testid={`revoke-${p.passcode_id}`} onClick={async () => {
                              if (!window.confirm(`Revoke passcode ${p.passcode}? It will no longer be redeemable.`)) return;
                              try {
                                await api.adminRevokePasscode(p.passcode_id);
                                toast.success('Passcode revoked');
                                loadTabData('as-codes');
                              } catch (e) { toast.error(e.message); }
                            }} style={{ background: 'transparent', border: '1px solid var(--pp-error)', color: 'var(--pp-error)', fontSize: 9, padding: '3px 8px', cursor: 'pointer', fontWeight: 700, letterSpacing: '0.1em' }}>REVOKE</button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ANNOUNCEMENTS — Platform upgrade broadcast */}
        {!loading && activeTab === 'announcements' && (
          <div data-testid="admin-announcements-tab">
            <p className="section-label" style={{ marginBottom: 12 }}>Platform Upgrade Announcement</p>
            <p style={{ margin: '0 0 16px', fontSize: 12, color: 'var(--pp-text-3)', lineHeight: 1.6 }}>
              One-time email broadcast to every active student + every approved waitlist entry. Includes the full
              upgrade summary, their migration details, and the temporary password they need to use on first login.
              Can only be sent once (force-resend is possible).
            </p>

            {announcePreview && announcePreview.already_sent && (
              <div style={{ background: 'rgba(0,255,148,0.08)', border: '1px solid rgba(0,255,148,0.4)', padding: 16, marginBottom: 16 }} data-testid="already-sent-banner">
                <p style={{ margin: 0, fontSize: 12, fontWeight: 700, color: 'var(--pp-success)', letterSpacing: '0.15em' }}>✓ ALREADY SENT</p>
                <p style={{ margin: '6px 0 0', fontSize: 12, color: 'var(--pp-text-2)' }}>
                  Sent on <strong>{new Date(announcePreview.sent_at).toLocaleString()}</strong> to {announcePreview.sent_count} recipients.
                </p>
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, marginBottom: 18 }}>
              <div style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: 14 }}>
                <p style={{ margin: 0, fontSize: 10, color: 'var(--pp-text-3)', letterSpacing: '0.15em', fontWeight: 700 }}>ACTIVE STUDENTS</p>
                <p style={{ margin: '6px 0 0', fontSize: 26, fontFamily: 'monospace', fontWeight: 700, color: 'var(--pp-success)' }}>{announcePreview?.active_student_count ?? '—'}</p>
              </div>
              <div style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: 14 }}>
                <p style={{ margin: 0, fontSize: 10, color: 'var(--pp-text-3)', letterSpacing: '0.15em', fontWeight: 700 }}>APPROVED WAITLIST</p>
                <p style={{ margin: '6px 0 0', fontSize: 26, fontFamily: 'monospace', fontWeight: 700, color: 'var(--pp-accent)' }}>{announcePreview?.approved_waitlist_count ?? '—'}</p>
              </div>
              <div style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: 14 }}>
                <p style={{ margin: 0, fontSize: 10, color: 'var(--pp-text-3)', letterSpacing: '0.15em', fontWeight: 700 }}>TOTAL TO SEND</p>
                <p style={{ margin: '6px 0 0', fontSize: 26, fontFamily: 'monospace', fontWeight: 700 }}>{announcePreview?.total_recipients ?? '—'}</p>
              </div>
            </div>

            <div style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: 18, marginBottom: 16 }}>
              <label style={LABEL}>Temporary password (included in email)</label>
              <input data-testid="announce-temp-pw" style={{ ...INPUT, fontFamily: 'monospace' }} value={announceTempPw} onChange={e => setAnnounceTempPw(e.target.value)} />
              <p style={{ margin: '6px 0 0', fontSize: 11, color: 'var(--pp-text-3)' }}>
                This is the password every recipient will see in the email body. Change it if you've already rotated passwords in the DB.
              </p>
            </div>

            <button data-testid="send-announcement-btn" disabled={announceSending || !announcePreview} onClick={async () => {
              const count = announcePreview?.total_recipients || 0;
              if (count === 0) { toast.error('No recipients to send to'); return; }
              const force = announcePreview?.already_sent;
              const confirmMsg = force
                ? `RESEND to ${count} recipients? This announcement was already sent on ${new Date(announcePreview.sent_at).toLocaleString()}.`
                : `Send the platform upgrade announcement to ${count} recipients? This action cannot be undone.`;
              if (!window.confirm(confirmMsg)) return;
              setAnnounceSending(true);
              try {
                const res = await api.adminAnnouncementSend(force, announceTempPw);
                setAnnounceResult(res);
                toast.success(`Sent to ${res.sent}/${res.total} recipients`);
                loadTabData('announcements');
              } catch (e) { toast.error(e.message); }
              setAnnounceSending(false);
            }} style={{ width: '100%', height: 52, background: announcePreview?.already_sent ? 'var(--pp-warning, #FFB000)' : 'var(--pp-success)', border: 'none', color: '#000', fontWeight: 800, fontSize: 13, cursor: 'pointer', letterSpacing: '0.15em' }}>
              {announceSending ? 'SENDING...'
                : announcePreview?.already_sent ? 'RESEND ANNOUNCEMENT (FORCE)'
                : `SEND ANNOUNCEMENT TO ${announcePreview?.total_recipients ?? 0} USERS`}
            </button>

            {announceResult && (
              <div style={{ marginTop: 16, background: 'var(--pp-overlay)', border: '1px solid rgba(0,255,148,0.3)', padding: 14 }} data-testid="announce-result">
                <p style={{ margin: 0, fontSize: 12, color: 'var(--pp-success)', fontWeight: 700, letterSpacing: '0.1em' }}>DELIVERY REPORT</p>
                <p style={{ margin: '8px 0 0', fontSize: 13 }}>Sent: <strong style={{ color: 'var(--pp-success)' }}>{announceResult.sent}</strong> · Failed: <strong style={{ color: 'var(--pp-error)' }}>{announceResult.failed}</strong> · Total: {announceResult.total}</p>
                {announceResult.failures && announceResult.failures.length > 0 && (
                  <div style={{ marginTop: 8, fontSize: 11, color: 'var(--pp-text-3)' }}>
                    {announceResult.failures.map((f, i) => <div key={i}>{f.email}: {f.error}</div>)}
                  </div>
                )}
              </div>
            )}

            {/* Recipient preview */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginTop: 24 }}>
              <div style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border)' }}>
                <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--pp-border)', background: 'var(--pp-overlay)' }}>
                  <p style={{ margin: 0, fontSize: 10, color: 'var(--pp-success)', letterSpacing: '0.15em', fontWeight: 700 }}>ACTIVE STUDENTS ({announcePreview?.students?.length ?? 0})</p>
                </div>
                <div style={{ maxHeight: 260, overflow: 'auto', padding: 10, fontSize: 12 }}>
                  {announcePreview?.students?.map((s, i) => (
                    <div key={i} style={{ padding: '4px 0', borderBottom: '1px solid var(--pp-border)' }}>
                      <strong>{s.name}</strong> · <span style={{ color: 'var(--pp-text-3)' }}>{s.email}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border)' }}>
                <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--pp-border)', background: 'var(--pp-overlay)' }}>
                  <p style={{ margin: 0, fontSize: 10, color: 'var(--pp-accent)', letterSpacing: '0.15em', fontWeight: 700 }}>APPROVED WAITLIST ({announcePreview?.waitlist?.length ?? 0})</p>
                </div>
                <div style={{ maxHeight: 260, overflow: 'auto', padding: 10, fontSize: 12 }}>
                  {announcePreview?.waitlist?.map((w, i) => (
                    <div key={i} style={{ padding: '4px 0', borderBottom: '1px solid var(--pp-border)' }}>
                      <strong>{w.name}</strong> · <span style={{ color: 'var(--pp-text-3)' }}>{w.email}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* VERIFICATION */}
        {!loading && activeTab === 'verification' && (
          <div>
            <p className="section-label" style={{ marginBottom: 12 }}>KYC Verifications ({adminVerifications.length})</p>
            {adminVerifications.length === 0 ? (
              <div style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: 20, textAlign: 'center' }}><p style={{ color: 'var(--pp-text-3)', margin: 0 }}>No verification submissions yet</p></div>
            ) : (
              adminVerifications.map(v => {
                const steps = v.steps || {};
                const stepColors = { pending: 'var(--pp-text-3)', under_review: 'var(--pp-warning)', approved: 'var(--pp-success)', rejected: 'var(--pp-error)' };
                return (
                  <div key={v.user_id} style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: 14, marginBottom: 10 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                      <div>
                        <p style={{ margin: 0, fontWeight: 700, fontSize: 14 }}>{v.user_name}</p>
                        <p style={{ margin: '3px 0 0', fontSize: 12, color: 'var(--pp-text-3)' }}>{v.user_email}</p>
                      </div>
                      <span className={v.verified ? 'badge-success' : v.status === 'under_review' ? 'badge-warning' : 'badge-accent'}>{v.verified ? 'VERIFIED' : (v.status || 'incomplete').toUpperCase()}</span>
                    </div>
                    <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
                      {['identity', 'address', 'selfie'].map(step => (
                        <div key={step} style={{ flex: 1, textAlign: 'center', background: 'var(--pp-overlay)', padding: '8px 4px' }}>
                          <p style={{ margin: '0 0 3px', fontSize: 9, letterSpacing: '0.1em', color: 'var(--pp-text-3)', textTransform: 'uppercase' }}>{step}</p>
                          <p style={{ margin: 0, fontSize: 10, fontWeight: 700, color: stepColors[steps[step] || 'pending'] }}>{(steps[step] || 'pending').replace('_', ' ').toUpperCase()}</p>
                        </div>
                      ))}
                    </div>
                    {!v.verified && (
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <button data-testid={`approve-verification-${v.user_id}`} onClick={async () => { await api.updateAdminVerification(v.user_id, { verified: true, steps: { identity: 'approved', address: 'approved', selfie: 'approved' } }).catch(e => toast.error(e.message)); loadTabData('verification'); toast.success('User verified!'); }} style={{ flex: 1, height: 40, background: 'var(--pp-success)', border: 'none', color: '#000', fontWeight: 700, fontSize: 11, cursor: 'pointer', minWidth: 80 }}>APPROVE</button>
                        <button onClick={async () => { await api.updateAdminVerification(v.user_id, { status: 'rejected', verified: false }).catch(e => toast.error(e.message)); loadTabData('verification'); toast.success('Rejected.'); }} style={{ flex: 1, height: 40, background: 'transparent', border: '1px solid var(--pp-error)', color: 'var(--pp-error)', fontWeight: 700, fontSize: 11, cursor: 'pointer', minWidth: 80 }}>REJECT</button>
                        <button data-testid={`gen-passcode-${v.user_id}`} onClick={async () => {
                          try {
                            const res = await api.generatePasscode({ user_id: v.user_id, send_email: false });
                            toast.success(`Passcode generated: ${res.passcode}`);
                            loadTabData('verification');
                          } catch (e) { toast.error(e.message); }
                        }} style={{ flex: 1, height: 40, background: 'rgba(41,121,255,0.12)', border: '1px solid rgba(41,121,255,0.3)', color: 'var(--pp-accent)', fontWeight: 700, fontSize: 10, cursor: 'pointer', minWidth: 100, letterSpacing: '0.05em' }}>GENERATE PASSCODE</button>
                      </div>
                    )}
                    {v.verified && (
                      <div style={{ padding: '8px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                        <span style={{ color: 'var(--pp-success)', fontWeight: 700, fontSize: 12, letterSpacing: '0.1em' }}>VERIFIED</span>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button data-testid={`gen-passcode-v-${v.user_id}`} onClick={async () => {
                            try {
                              const res = await api.generatePasscode({ user_id: v.user_id, send_email: false });
                              toast.success(`Passcode generated: ${res.passcode}`);
                              loadTabData('verification');
                            } catch (e) { toast.error(e.message); }
                          }} style={{ background: 'rgba(41,121,255,0.12)', border: '1px solid rgba(41,121,255,0.3)', color: 'var(--pp-accent)', padding: '5px 10px', fontSize: 10, fontWeight: 700, cursor: 'pointer' }}>GENERATE PASSCODE</button>
                          <button data-testid={`send-passcode-${v.user_id}`} onClick={async () => {
                            try {
                              const res = await api.sendPasscode({ user_id: v.user_id });
                              toast.success(res.message || 'Passcode sent!');
                            } catch (e) { toast.error(e.message); }
                          }} style={{ background: 'rgba(0,255,148,0.12)', border: '1px solid rgba(0,255,148,0.3)', color: 'var(--pp-success)', padding: '5px 10px', fontSize: 10, fontWeight: 700, cursor: 'pointer' }}>SEND PASSCODE</button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* LIVE CHATS */}
        {!loading && activeTab === 'chats' && (
          <div>
            <p className="section-label" style={{ marginBottom: 12 }}>LIVE SUPPORT CHATS ({supportChats.length})</p>
            {supportChats.length === 0 ? (
              <div style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: 24, textAlign: 'center' }}><p style={{ color: 'var(--pp-text-3)', margin: 0 }}>No active support chats</p></div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: selectedChat ? '240px 1fr' : '1fr', gap: 12 }}>
                {/* Chat list */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {supportChats.map(c => (
                    <button key={c.chat_id} onClick={() => setSelectedChat(c)} style={{ background: selectedChat?.chat_id === c.chat_id ? 'rgba(41,121,255,0.12)' : 'var(--pp-surface)', border: `1px solid ${selectedChat?.chat_id === c.chat_id ? 'var(--pp-accent)' : 'var(--pp-border)'}`, padding: 12, textAlign: 'left', cursor: 'pointer' }}>
                      <p style={{ margin: 0, fontWeight: 700, fontSize: 13 }}>{c.user_name || 'User'}</p>
                      <p style={{ margin: '2px 0 0', fontSize: 10, color: 'var(--pp-text-3)' }}>{c.user_email}</p>
                      <p style={{ margin: '4px 0 0', fontSize: 11, color: 'var(--pp-text-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{(c.messages || []).slice(-1)[0]?.content || 'No messages'}</p>
                    </button>
                  ))}
                </div>
                {/* Chat messages */}
                {selectedChat && (
                  <div style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', display: 'flex', flexDirection: 'column', maxHeight: 500 }}>
                    <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--pp-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontWeight: 700, fontSize: 13 }}>{selectedChat.user_name}</span>
                      <button onClick={async () => { await api.closeSupportChat(selectedChat.chat_id); toast.success('Chat closed'); loadTabData('chats'); setSelectedChat(null); }} style={{ fontSize: 10, background: 'rgba(255,51,51,0.1)', border: '1px solid rgba(255,51,51,0.3)', color: '#FF3333', padding: '4px 10px', cursor: 'pointer', fontWeight: 700 }}>CLOSE CHAT</button>
                    </div>
                    <div style={{ flex: 1, overflowY: 'auto', padding: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {(selectedChat.messages || []).map(m => (
                        <div key={m.message_id} style={{ alignSelf: m.sender === 'admin' ? 'flex-start' : 'flex-end', maxWidth: '80%', background: m.sender === 'admin' ? 'rgba(41,121,255,0.12)' : 'rgba(0,255,148,0.1)', border: `1px solid ${m.sender === 'admin' ? 'rgba(41,121,255,0.3)' : 'rgba(0,255,148,0.3)'}`, padding: '8px 12px' }}>
                          <p style={{ margin: 0, fontSize: 12, color: 'var(--pp-text)' }}>{m.content}</p>
                          <p style={{ margin: '4px 0 0', fontSize: 9, color: 'var(--pp-text-3)' }}>{m.sender} · {new Date(m.timestamp).toLocaleTimeString()}</p>
                        </div>
                      ))}
                    </div>
                    <div style={{ padding: 10, borderTop: '1px solid var(--pp-border)', display: 'flex', gap: 8 }}>
                      <button onClick={async () => { try { const r = await api.aiSuggestReply(selectedChat.chat_id); if (r.suggestion) { setChatReply(r.suggestion); toast.success('AI suggestion loaded'); } else { toast.error(r.error || 'No suggestion'); } } catch (e) { toast.error(e.message); } }} style={{ height: 36, padding: '0 10px', background: 'rgba(138,43,226,0.12)', border: '1px solid rgba(138,43,226,0.3)', color: '#8A2BE2', fontSize: 9, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>AI SUGGEST</button>
                      <input style={{ ...INPUT, flex: 1, height: 36 }} value={chatReply} onChange={e => setChatReply(e.target.value)} placeholder="Type reply..." onKeyDown={e => { if (e.key === 'Enter' && chatReply.trim()) { e.preventDefault(); (async () => { await api.sendChatMessage(selectedChat.chat_id, chatReply); setChatReply(''); const msgs = await api.getChatMessages(selectedChat.chat_id); setSelectedChat(sc => ({ ...sc, messages: msgs.messages })); })(); } }} />
                      <button onClick={async () => { if (!chatReply.trim()) return; await api.sendChatMessage(selectedChat.chat_id, chatReply); setChatReply(''); const msgs = await api.getChatMessages(selectedChat.chat_id); setSelectedChat(sc => ({ ...sc, messages: msgs.messages })); }} style={{ height: 36, padding: '0 14px', background: 'var(--pp-primary)', border: 'none', color: '#fff', fontWeight: 700, fontSize: 11, cursor: 'pointer' }}>SEND</button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}


        {/* PROP-FIRM PROFILES — routing rules per prop firm */}
        {!loading && activeTab === 'prop-firms' && (
          <div>
            <div style={{ background: 'rgba(41,121,255,0.08)', border: '1px solid rgba(41,121,255,0.3)', padding: 12, marginBottom: 14 }}>
              <p style={{ margin: 0, fontSize: 11, fontWeight: 700, color: 'var(--pp-accent)', letterSpacing: '0.1em' }}>PROP-FIRM ROUTING RULES</p>
              <p style={{ margin: '6px 0 0', fontSize: 11, color: 'var(--pp-text-2)', lineHeight: 1.6 }}>
                When a student links a broker account marked as a prop-firm account, these rules decide how that account's trades are executed.
                Firms with <strong>Playwright ON</strong> will route through a per-user browser context + proxy (prop-firm-safe IP).
                Firms with Playwright OFF use MetaAPI (our current direct-API path).
                Today, Playwright is stubbed — accounts needing it fall back to MetaAPI with a <code>PROP_FIRM_UNSAFE_EXECUTION</code> warning in the logs until Phase 1 ships.
              </p>
            </div>
            <p className="section-label" style={{ marginBottom: 10 }}>Add New Rule</p>
            <div style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: 14, marginBottom: 18, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div><label style={LABEL}>Prop firm name</label><input data-testid="new-pf-name" style={INPUT} value={newPropFirm.name} onChange={e => setNewPropFirm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Blue Guardian" /></div>
              <div><label style={LABEL}>Platforms (comma-sep)</label><input style={INPUT} value={newPropFirm.platforms} onChange={e => setNewPropFirm(p => ({ ...p, platforms: e.target.value }))} placeholder="MT5, TradeLocker" /></div>
              <div style={{ gridColumn: '1 / span 2' }}><label style={LABEL}>Notes</label><input style={INPUT} value={newPropFirm.notes} onChange={e => setNewPropFirm(p => ({ ...p, notes: e.target.value }))} /></div>
              <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12 }}><input type="checkbox" checked={newPropFirm.needs_proxy} onChange={e => setNewPropFirm(p => ({ ...p, needs_proxy: e.target.checked }))} /> Needs Proxy</label>
              <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12 }}><input type="checkbox" checked={newPropFirm.needs_playwright} onChange={e => setNewPropFirm(p => ({ ...p, needs_playwright: e.target.checked }))} /> Needs Playwright</label>
              <button
                data-testid="create-pf-rule"
                onClick={async () => {
                  if (!newPropFirm.name.trim()) { toast.error('Name required'); return; }
                  try {
                    await api.createAdminPropFirmProfile({
                      name: newPropFirm.name.trim(),
                      platforms: newPropFirm.platforms.split(',').map(s => s.trim()).filter(Boolean),
                      needs_proxy: newPropFirm.needs_proxy,
                      needs_playwright: newPropFirm.needs_playwright,
                      notes: newPropFirm.notes,
                    });
                    toast.success('Rule created');
                    setNewPropFirm({ name: '', needs_proxy: true, needs_playwright: true, notes: '', platforms: '' });
                    loadTabData('prop-firms');
                  } catch (e) { toast.error(e.message); }
                }}
                className="btn-primary" style={{ gridColumn: '1 / span 2', height: 44 }}
              >CREATE RULE</button>
            </div>

            <p className="section-label" style={{ marginBottom: 10 }}>Profiles ({propFirmProfiles.length})</p>
            <div style={{ overflowX: 'auto', background: 'var(--pp-surface)', border: '1px solid var(--pp-border)' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ background: 'var(--pp-overlay)', borderBottom: '1px solid var(--pp-border)' }}>
                    {['Name', 'Platforms', 'Proxy?', 'Playwright?', 'Notes', 'Action'].map(h => (
                      <th key={h} style={{ padding: '10px 8px', textAlign: 'left', fontSize: 10, letterSpacing: '0.1em', color: 'var(--pp-text-3)' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {propFirmProfiles.map(r => (
                    <tr key={r.rule_id} style={{ borderBottom: '1px solid var(--pp-border)' }}>
                      <td style={{ padding: '10px 8px', fontWeight: 700 }}>{r.name}</td>
                      <td style={{ padding: '10px 8px', fontSize: 11, color: 'var(--pp-text-3)' }}>{(r.platforms || []).join(', ') || '—'}</td>
                      <td style={{ padding: '10px 8px' }}>
                        <input type="checkbox" checked={r.needs_proxy} onChange={async e => { await api.updateAdminPropFirmProfile(r.rule_id, { needs_proxy: e.target.checked }); loadTabData('prop-firms'); toast.success('Updated'); }} />
                      </td>
                      <td style={{ padding: '10px 8px' }}>
                        <input type="checkbox" checked={r.needs_playwright} onChange={async e => { await api.updateAdminPropFirmProfile(r.rule_id, { needs_playwright: e.target.checked }); loadTabData('prop-firms'); toast.success('Updated'); }} />
                      </td>
                      <td style={{ padding: '10px 8px', fontSize: 11, color: 'var(--pp-text-3)', maxWidth: 350 }}>{r.notes}</td>
                      <td style={{ padding: '10px 8px' }}>
                        <button
                          onClick={async () => {
                            if (!window.confirm(`Delete ${r.name} rule?`)) return;
                            try { await api.deleteAdminPropFirmProfile(r.rule_id); toast.success('Deleted'); loadTabData('prop-firms'); } catch (e) { toast.error(e.message); }
                          }}
                          style={{ background: 'none', border: '1px solid var(--pp-error)', color: 'var(--pp-error)', padding: '3px 8px', fontSize: 10, cursor: 'pointer', fontWeight: 700 }}
                        >DEL</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}


        {/* PROXIES — IPRoyal inventory */}
        {!loading && activeTab === 'proxies' && (
          <div>
            <div style={{ background: 'rgba(255,176,0,0.08)', border: '1px solid rgba(255,176,0,0.3)', padding: 12, marginBottom: 14 }}>
              <p style={{ margin: 0, fontSize: 11, fontWeight: 700, color: '#FFB000', letterSpacing: '0.1em' }}>HOW PROXIES WORK RIGHT NOW</p>
              <p style={{ margin: '6px 0 0', fontSize: 11, color: 'var(--pp-text-2)', lineHeight: 1.6 }}>
                These IPRoyal IPs are assigned to students for their <strong>direct MT4/MT5 terminal logins</strong> — the prop firm sees the assigned IP when the student trades locally.
                &nbsp;Copy-trade execution through MetaAPI originates from MetaAPI's servers, not these IPs. Prop-firm-safe routing (Playwright MT5 web) is in backlog.
              </p>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
              <p className="section-label" style={{ margin: 0 }}>IPRoyal Inventory ({proxyInventory.length})</p>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                {['all', 'unassigned', 'assigned'].map(f => (
                  <button key={f} onClick={() => setProxyInventoryFilter(f)} style={{ fontSize: 10, background: proxyInventoryFilter === f ? 'rgba(41,121,255,0.2)' : 'transparent', border: '1px solid ' + (proxyInventoryFilter === f ? 'var(--pp-accent)' : 'var(--pp-border)'), color: proxyInventoryFilter === f ? 'var(--pp-accent)' : 'var(--pp-text-3)', padding: '5px 10px', cursor: 'pointer', letterSpacing: '0.1em', fontWeight: 700 }}>{f.toUpperCase()}</button>
                ))}
                <label data-testid="proxy-hide-inactive" style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 10, color: proxyHideInactive ? 'var(--pp-success)' : 'var(--pp-text-3)', padding: '5px 10px', border: '1px solid ' + (proxyHideInactive ? 'rgba(0,255,148,0.3)' : 'var(--pp-border)'), background: proxyHideInactive ? 'rgba(0,255,148,0.08)' : 'transparent', cursor: 'pointer', fontWeight: 700, letterSpacing: '0.08em' }}>
                  <input type="checkbox" checked={proxyHideInactive} onChange={e => setProxyHideInactive(e.target.checked)} style={{ margin: 0 }} />
                  HIDE EXPIRED
                </label>
                <button
                  data-testid="refresh-proxy-inventory"
                  onClick={async () => {
                    setProxyInventoryLoading(true);
                    try { const r = await api.getProxyInventory(); setProxyInventory(r.proxies || []); toast.success(`Refreshed — ${r.count || 0} proxies live`); } catch (e) { toast.error(e.message); }
                    setProxyInventoryLoading(false);
                  }}
                  style={{ fontSize: 10, background: 'rgba(0,255,148,0.12)', border: '1px solid rgba(0,255,148,0.3)', color: 'var(--pp-success)', padding: '5px 12px', cursor: 'pointer', letterSpacing: '0.1em', fontWeight: 700 }}
                >{proxyInventoryLoading ? 'LOADING…' : '↻ REFRESH FROM IPROYAL'}</button>
              </div>
            </div>
            {proxyInventory.length === 0 ? (
              <div style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: 24, textAlign: 'center' }}>
                <p style={{ color: 'var(--pp-text-3)', margin: 0, fontSize: 13 }}>No inventory loaded yet. Click <strong>↻ REFRESH FROM IPROYAL</strong>.</p>
              </div>
            ) : (
              <div style={{ overflowX: 'auto', background: 'var(--pp-surface)', border: '1px solid var(--pp-border)' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: 'var(--pp-overlay)', borderBottom: '1px solid var(--pp-border)' }}>
                      {['IP', 'City, State, Country', 'ISP', 'Product', 'Expires', 'Port', 'Credentials', 'Assigned To', 'Action'].map(h => (
                        <th key={h} style={{ padding: '10px 8px', textAlign: 'left', fontSize: 10, letterSpacing: '0.1em', color: 'var(--pp-text-3)' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {proxyInventory
                      .filter(p => !proxyHideInactive || (p.status === 'confirmed' || p.status === 'active' || !p.status))
                      .filter(p => proxyInventoryFilter === 'all' || (proxyInventoryFilter === 'assigned' ? !!p.assigned_to : !p.assigned_to)).map(p => (
                      <tr key={p.ip + p.iproyal_order_id} style={{ borderBottom: '1px solid var(--pp-border)' }} data-testid={`proxy-row-${p.ip}`}>
                        <td className="mono" style={{ padding: '10px 8px', color: 'var(--pp-accent)' }}>{p.ip}</td>
                        <td style={{ padding: '10px 8px' }}>{[p.city, p.state, p.country].filter(Boolean).join(', ') || p.order_country || '—'}</td>
                        <td style={{ padding: '10px 8px', color: 'var(--pp-text-3)' }}>{p.isp || '—'}</td>
                        <td style={{ padding: '10px 8px' }}><span style={{ fontSize: 10, background: 'rgba(41,121,255,0.1)', padding: '2px 8px', color: 'var(--pp-accent)', fontWeight: 700 }}>{p.product}</span></td>
                        <td className="mono" style={{ padding: '10px 8px', color: 'var(--pp-text-3)' }}>
                          <div>{(p.expire_date || '').slice(0, 10)}</div>
                          {p.status && p.status !== 'confirmed' && p.status !== 'active' && (
                            <span style={{ fontSize: 9, color: p.status === 'expired' ? 'var(--pp-error)' : 'var(--pp-text-3)', background: p.status === 'expired' ? 'rgba(255,70,70,0.1)' : 'transparent', padding: '1px 5px', letterSpacing: '0.05em', fontWeight: 700 }}>{p.status.toUpperCase()}</span>
                          )}
                        </td>
                        <td className="mono" style={{ padding: '10px 8px' }}>{p.http_port} / {p.socks5_port}</td>
                        <td className="mono" style={{ padding: '10px 8px', fontSize: 10 }}>
                          <div>{p.username}</div>
                          <button
                            onClick={(e) => {
                              const b = e.currentTarget;
                              if (b.dataset.r === '1') { b.textContent = 'show pw'; b.dataset.r = '0'; return; }
                              b.textContent = p.password; b.dataset.r = '1';
                            }}
                            style={{ background: 'none', border: 'none', color: 'var(--pp-text-3)', textDecoration: 'underline', cursor: 'pointer', fontSize: 10, padding: 0 }}
                          >show pw</button>
                        </td>
                        <td style={{ padding: '10px 8px' }}>
                          {p.assigned_to ? (
                            <span style={{ fontSize: 11, color: 'var(--pp-success)' }}>✓ {p.assigned_to.name}</span>
                          ) : (
                            <span style={{ fontSize: 11, color: 'var(--pp-text-3)' }}>—</span>
                          )}
                          {(p.status === 'confirmed' || p.status === 'active') && (
                            p.auto_extend_enabled ? (
                              <div data-testid={`auto-extend-ok-${p.ip}`} style={{ marginTop: 4, fontSize: 9, color: 'var(--pp-success)', letterSpacing: '0.05em', fontWeight: 700 }}>♻ AUTO-EXTEND ON</div>
                            ) : (
                              <div data-testid={`auto-extend-off-${p.ip}`} title="This proxy will expire and may release the IP. Enable Auto-Extend in iProyal dashboard." style={{ marginTop: 4, fontSize: 9, color: '#FFB000', background: 'rgba(255,176,0,0.1)', padding: '1px 5px', letterSpacing: '0.05em', fontWeight: 700, display: 'inline-block' }}>⚠ NO AUTO-EXTEND</div>
                            )
                          )}
                        </td>
                        <td style={{ padding: '10px 8px' }}>
                          {p.assigned_to ? (
                            <button
                              data-testid={`unassign-proxy-${p.ip}`}
                              onClick={async () => {
                                if (!window.confirm(`Unassign ${p.ip} from ${p.assigned_to.name}?`)) return;
                                try { await api.unassignUserProxy(p.assigned_to.user_id); toast.success('Unassigned'); const r = await api.getProxyInventory(); setProxyInventory(r.proxies || []); } catch (e) { toast.error(e.message); }
                              }}
                              style={{ background: 'none', border: '1px solid var(--pp-error)', color: 'var(--pp-error)', padding: '3px 8px', fontSize: 10, cursor: 'pointer', fontWeight: 700 }}
                            >UNASSIGN</button>
                          ) : (
                            <button
                              data-testid={`assign-proxy-${p.ip}`}
                              onClick={() => setProxyAssignTarget(p)}
                              style={{ background: 'rgba(41,121,255,0.12)', border: '1px solid var(--pp-accent)', color: 'var(--pp-accent)', padding: '3px 10px', fontSize: 10, cursor: 'pointer', fontWeight: 700, letterSpacing: '0.05em' }}
                            >ASSIGN</button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {proxyAssignTarget && (
              <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }} onClick={() => setProxyAssignTarget(null)}>
                <div data-testid="assign-proxy-modal" style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: 22, width: 440, maxWidth: '95vw' }} onClick={e => e.stopPropagation()}>
                  <p style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 700, letterSpacing: '0.08em' }}>ASSIGN PROXY</p>
                  <div style={{ background: 'var(--pp-overlay)', padding: 12, marginBottom: 14, border: '1px solid var(--pp-border)' }}>
                    <p className="mono" style={{ margin: 0, fontSize: 13, color: 'var(--pp-accent)' }}>{proxyAssignTarget.ip}:{proxyAssignTarget.http_port}</p>
                    <p style={{ margin: '4px 0 0', fontSize: 11, color: 'var(--pp-text-3)' }}>{[proxyAssignTarget.city, proxyAssignTarget.state, proxyAssignTarget.country].filter(Boolean).join(', ')} — {proxyAssignTarget.isp}</p>
                  </div>
                  <label style={LABEL}>Assign to Student</label>
                  <select data-testid="assign-proxy-student-select" style={{ ...INPUT, cursor: 'pointer' }} value={proxyAssignToUser} onChange={e => setProxyAssignToUser(e.target.value)}>
                    <option value="">— Choose student —</option>
                    {activeStudents.map(s => <option key={s.user_id} value={s.user_id}>{s.name} · {s.email}</option>)}
                  </select>
                  <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
                    <button onClick={() => { setProxyAssignTarget(null); setProxyAssignToUser(''); }} style={{ flex: 1, height: 42, background: 'transparent', border: '1px solid var(--pp-border)', color: 'var(--pp-text)', cursor: 'pointer' }}>Cancel</button>
                    <button
                      data-testid="confirm-assign-proxy-btn"
                      onClick={async () => {
                        if (!proxyAssignToUser) { toast.error('Pick a student'); return; }
                        try {
                          await api.assignProxyToUser({
                            user_id: proxyAssignToUser,
                            proxy_host: proxyAssignTarget.ip,
                            proxy_port: proxyAssignTarget.http_port,
                            proxy_username: proxyAssignTarget.username,
                            proxy_password: proxyAssignTarget.password,
                            proxy_label: `IPRoyal ${proxyAssignTarget.product} — ${[proxyAssignTarget.city, proxyAssignTarget.state, proxyAssignTarget.country].filter(Boolean).join(', ')}`,
                          });
                          toast.success('Assigned!');
                          setProxyAssignTarget(null);
                          setProxyAssignToUser('');
                          const r = await api.getProxyInventory();
                          setProxyInventory(r.proxies || []);
                        } catch (e) { toast.error(e.message); }
                      }}
                      className="btn-primary" style={{ flex: 2, height: 42 }}
                    >ASSIGN TO STUDENT</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}


        {/* STEALTH COMPLIANCE LAYER */}
        {!loading && activeTab === 'stealth' && (
          <div>
            <p className="section-label" style={{ marginBottom: 4 }}>STEALTH COMPLIANCE LAYER</p>
            <p style={{ margin: '0 0 18px', fontSize: 11, color: 'var(--pp-text-3)', maxWidth: 720 }}>
              Toggle <strong>prohibits_algo</strong> on a prop firm to divert every signal for accounts under that firm
              through a fingerprint-isolated stealth browser (independent Chromium, dedicated proxy, randomized jitter).
              Standard REST execution stays in place for every unflagged firm.
            </p>

            <p className="section-label" style={{ marginBottom: 8, marginTop: 8 }}>FIRM RULES ({firmRules.length})</p>
            <div style={{ overflowX: 'auto', marginBottom: 24, background: 'var(--pp-surface)', border: '1px solid var(--pp-border)' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--pp-border)' }}>
                    {['Firm', 'Prohibits Algo', 'Admin Flagged', 'User Flagged', 'Updated By', 'Updated At', 'Actions'].map(h => (
                      <th key={h} style={{ padding: '10px 8px', textAlign: 'left', fontSize: 10, letterSpacing: '0.1em', color: 'var(--pp-text-3)', textTransform: 'uppercase', fontWeight: 600 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {firmRules.map(r => {
                    const effective = r.prohibits_algo || r.admin_flagged || r.user_flagged;
                    return (
                      <tr key={r.firm_id} style={{ borderBottom: '1px solid var(--pp-border)', background: effective ? 'rgba(255,148,0,0.06)' : 'transparent' }}>
                        <td style={{ padding: '10px 8px', fontWeight: 600 }}>{r.firm_id}</td>
                        <td style={{ padding: '10px 8px' }}>
                          <button
                            data-testid={`firm-rule-prohibits-${r.firm_id}`}
                            onClick={async () => {
                              try {
                                await api.adminUpsertFirmRule(r.firm_id, { prohibits_algo: !r.prohibits_algo });
                                toast.success(`${r.firm_id}: prohibits_algo → ${!r.prohibits_algo}`);
                                loadTabData('stealth');
                              } catch (e) { toast.error(e.message); }
                            }}
                            style={{ background: r.prohibits_algo ? '#FF9400' : 'transparent', border: `1px solid ${r.prohibits_algo ? '#FF9400' : 'var(--pp-border)'}`, color: r.prohibits_algo ? '#000' : 'var(--pp-text-3)', padding: '4px 10px', fontSize: 10, cursor: 'pointer', fontWeight: 700, letterSpacing: '0.06em' }}>
                            {r.prohibits_algo ? '● ON' : '○ OFF'}
                          </button>
                        </td>
                        <td style={{ padding: '10px 8px' }}>
                          <button
                            onClick={async () => {
                              try {
                                await api.adminUpsertFirmRule(r.firm_id, { admin_flagged: !r.admin_flagged });
                                loadTabData('stealth');
                              } catch (e) { toast.error(e.message); }
                            }}
                            style={{ background: r.admin_flagged ? 'var(--pp-error)' : 'transparent', border: `1px solid ${r.admin_flagged ? 'var(--pp-error)' : 'var(--pp-border)'}`, color: r.admin_flagged ? '#000' : 'var(--pp-text-3)', padding: '4px 10px', fontSize: 10, cursor: 'pointer', fontWeight: 700 }}>
                            {r.admin_flagged ? '● ON' : '○ OFF'}
                          </button>
                        </td>
                        <td style={{ padding: '10px 8px', fontSize: 11, color: 'var(--pp-text-3)' }}>{r.user_flagged ? '✓' : '—'}</td>
                        <td style={{ padding: '10px 8px', fontSize: 10, color: 'var(--pp-text-3)' }}>{r.last_updated_by}</td>
                        <td style={{ padding: '10px 8px', fontSize: 10, color: 'var(--pp-text-3)' }}>{r.updated_at?.slice(0, 19).replace('T', ' ')}</td>
                        <td style={{ padding: '10px 8px' }}>
                          {effective && <span style={{ fontSize: 9, fontWeight: 800, color: '#FF9400', letterSpacing: '0.08em' }}>STEALTH ACTIVE</span>}
                        </td>
                      </tr>
                    );
                  })}
                  {firmRules.length === 0 && (
                    <tr><td colSpan={7} style={{ padding: 16, textAlign: 'center', color: 'var(--pp-text-3)' }}>No firm rules yet. Rules auto-create when a connection is dispatched.</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <p className="section-label" style={{ margin: 0 }}>STEALTH PROFILES ({stealthAccounts.length})</p>
              <button
                data-testid="add-stealth-profile-btn"
                onClick={() => setStealthForm({ open: true, mode: 'create', payload: {
                  firm_id: '', platform: 'TRADELOCKER', web_terminal_url: '', username: '', password: '',
                  proxy_address: '', user_agent: '', screen_resolution: '1920x1080',
                  min_jitter_ms: 300, max_jitter_ms: 1800, user_id_link: '',
                }})}
                style={{ background: 'var(--pp-accent)', color: '#000', border: 'none', padding: '6px 14px', fontSize: 11, fontWeight: 700, cursor: 'pointer', letterSpacing: '0.08em' }}>
                + ADD STEALTH PROFILE
              </button>
            </div>
            <div style={{ overflowX: 'auto', marginBottom: 24, background: 'var(--pp-surface)', border: '1px solid var(--pp-border)' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--pp-border)' }}>
                    {['Account ID', 'Firm', 'Platform', 'Username', 'Proxy', 'Resolution', 'Jitter (ms)', 'Linked User', 'Actions'].map(h => (
                      <th key={h} style={{ padding: '10px 8px', textAlign: 'left', fontSize: 10, letterSpacing: '0.1em', color: 'var(--pp-text-3)', textTransform: 'uppercase', fontWeight: 600 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {stealthAccounts.map(sa => (
                    <tr key={sa.account_id} style={{ borderBottom: '1px solid var(--pp-border)' }}>
                      <td className="mono" style={{ padding: '10px 8px', fontSize: 10, color: 'var(--pp-accent)' }}>{sa.account_id}</td>
                      <td style={{ padding: '10px 8px', fontSize: 11 }}>{sa.firm_id}</td>
                      <td style={{ padding: '10px 8px' }}><span style={{ background: 'var(--pp-overlay)', padding: '3px 7px', fontSize: 9, fontWeight: 700, letterSpacing: '0.05em' }}>{sa.platform}</span></td>
                      <td className="mono" style={{ padding: '10px 8px', fontSize: 10 }}>{sa.username}</td>
                      <td className="mono" style={{ padding: '10px 8px', fontSize: 10, color: 'var(--pp-text-3)' }}>{(sa.proxy_address || '').replace(/:\/\/[^@]*@/, '://***@')}</td>
                      <td className="mono" style={{ padding: '10px 8px', fontSize: 10 }}>{sa.screen_resolution}</td>
                      <td className="mono" style={{ padding: '10px 8px', fontSize: 10 }}>{sa.min_jitter_ms}–{sa.max_jitter_ms}</td>
                      <td className="mono" style={{ padding: '10px 8px', fontSize: 10, color: 'var(--pp-text-3)' }}>{(sa.user_id_link || '').slice(0, 14)}</td>
                      <td style={{ padding: '10px 8px' }}>
                        <button
                          data-testid={`stealth-test-${sa.account_id}`}
                          title="Fire a 0.01 lot (forex/CFD) or 1-contract (futures) test order through this profile's stealth browser. Verifies proxy + login + DOM nav."
                          onClick={async (e) => {
                            const btn = e.currentTarget;
                            const original = btn.textContent;
                            btn.textContent = '⏳ TESTING…';
                            btn.disabled = true;
                            try {
                              const r = await api.adminTestStealthAccount(sa.account_id);
                              if (r.ok) {
                                toast.success(`✅ Test trade fired (${r.ms}ms): ${r.detail || 'OK'}`);
                              } else {
                                toast.error(`❌ Test failed (${r.ms}ms): ${r.detail || 'unknown'}`);
                              }
                              loadTabData('stealth');
                            } catch (err) { toast.error(err.message); }
                            btn.textContent = original;
                            btn.disabled = false;
                          }}
                          style={{ background: 'rgba(0,201,215,0.12)', border: '1px solid rgba(0,201,215,0.4)', color: '#00C9D7', padding: '3px 10px', fontSize: 10, cursor: 'pointer', fontWeight: 700, letterSpacing: '0.06em', marginRight: 4 }}>
                          🧪 TEST
                        </button>
                        <button
                          onClick={async () => {
                            if (!window.confirm(`Delete stealth profile ${sa.account_id}?`)) return;
                            try { await api.adminDeleteStealthAccount(sa.account_id); toast.success('Deleted'); loadTabData('stealth'); }
                            catch (e) { toast.error(e.message); }
                          }}
                          style={{ background: 'transparent', border: '1px solid var(--pp-error)', color: 'var(--pp-error)', padding: '3px 10px', fontSize: 10, cursor: 'pointer', fontWeight: 700 }}>
                          × DELETE
                        </button>
                      </td>
                    </tr>
                  ))}
                  {stealthAccounts.length === 0 && (
                    <tr><td colSpan={9} style={{ padding: 16, textAlign: 'center', color: 'var(--pp-text-3)' }}>No stealth profiles yet. Add one to bind a fingerprint-isolated browser to a prop firm account.</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            <p className="section-label" style={{ marginBottom: 8 }}>RECENT STEALTH EXECUTIONS ({stealthExecutions.length})</p>
            <div style={{ overflowX: 'auto', background: 'var(--pp-surface)', border: '1px solid var(--pp-border)' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--pp-border)' }}>
                    {['Time', 'Signal', 'Account', 'Asset', 'Side', 'Status', 'Detail'].map(h => (
                      <th key={h} style={{ padding: '10px 8px', textAlign: 'left', fontSize: 10, letterSpacing: '0.1em', color: 'var(--pp-text-3)', textTransform: 'uppercase', fontWeight: 600 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {stealthExecutions.map(e => (
                    <tr key={e.dedup_id} style={{ borderBottom: '1px solid var(--pp-border)' }}>
                      <td className="mono" style={{ padding: '8px', fontSize: 10, color: 'var(--pp-text-3)' }}>{e.timestamp?.slice(11, 19)}</td>
                      <td className="mono" style={{ padding: '8px', fontSize: 10 }}>{e.signal_id?.slice(0, 14)}</td>
                      <td className="mono" style={{ padding: '8px', fontSize: 10 }}>{e.account_id}</td>
                      <td className="mono" style={{ padding: '8px', fontSize: 11, fontWeight: 700 }}>{e.asset}</td>
                      <td style={{ padding: '8px', fontSize: 10 }}>{e.action}</td>
                      <td style={{ padding: '8px' }}>
                        <span style={{ fontSize: 9, fontWeight: 800, color: e.status === 'COMPLETED' ? 'var(--pp-success)' : e.status === 'FAILED' ? 'var(--pp-error)' : 'var(--pp-warning)' }}>{e.status}</span>
                      </td>
                      <td className="mono" style={{ padding: '8px', fontSize: 10, color: 'var(--pp-text-3)', maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.result?.detail || ''}</td>
                    </tr>
                  ))}
                  {stealthExecutions.length === 0 && (
                    <tr><td colSpan={7} style={{ padding: 16, textAlign: 'center', color: 'var(--pp-text-3)' }}>No stealth executions logged yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {stealthForm.open && (
              <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.78)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: 14 }} onClick={() => setStealthForm({ open: false, mode: 'create', payload: {} })}>
                <div data-testid="stealth-modal" style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-accent)', padding: 22, width: 540, maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
                  <p style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 700, letterSpacing: '0.08em', color: 'var(--pp-accent)' }}>NEW STEALTH PROFILE</p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    {['firm_id','username','password','web_terminal_url','user_agent','screen_resolution','user_id_link'].map(k => (
                      <div key={k} style={k === 'user_agent' || k === 'web_terminal_url' ? { gridColumn: '1 / -1' } : {}}>
                        <label style={LABEL}>{k.replace(/_/g, ' ')}</label>
                        <input
                          data-testid={`stealth-form-${k}`}
                          style={INPUT}
                          type={k === 'password' ? 'password' : 'text'}
                          value={stealthForm.payload[k] || ''}
                          onChange={e => setStealthForm(s => ({ ...s, payload: { ...s.payload, [k]: e.target.value } }))}
                          placeholder={k === 'web_terminal_url' ? 'https://demo.tradelocker.com/login' : k === 'user_id_link' ? 'optional: bind to a slave user' : ''}
                        />
                      </div>
                    ))}
                    <div style={{ gridColumn: '1 / -1' }}>
                      <label style={LABEL}>proxy (pick from iProyal inventory · {availableProxies.length} available)</label>
                      <select
                        data-testid="stealth-form-proxy-picker"
                        style={{ ...INPUT, cursor: 'pointer' }}
                        value={stealthForm.payload.proxy_address || ''}
                        onChange={e => setStealthForm(s => ({ ...s, payload: { ...s.payload, proxy_address: e.target.value } }))}>
                        <option value="">— No proxy (NOT recommended for stealth) —</option>
                        {availableProxies.map(p => {
                          const u = p.username, pw = p.password, ip = p.ip, port = p.http_port || p.socks5_port;
                          if (!ip || !port) return null;
                          const url = `http://${u}:${pw}@${ip}:${port}`;
                          const loc = [p.city, p.state, p.country].filter(Boolean).join(', ') || p.order_country || 'unknown';
                          const status = p.assigned_to ? `⚠ already assigned to ${p.assigned_to.email || p.assigned_to.user_id?.slice(0,8)}` : '✓ free';
                          return <option key={`${ip}:${port}`} value={url}>{p.product} · {ip}:{port} · {loc} · {status}</option>;
                        })}
                      </select>
                      <input
                        style={{ ...INPUT, marginTop: 6, fontSize: 10 }}
                        placeholder="…or paste a custom proxy URL: http://user:pass@host:port"
                        value={stealthForm.payload.proxy_address || ''}
                        onChange={e => setStealthForm(s => ({ ...s, payload: { ...s.payload, proxy_address: e.target.value } }))}
                      />
                    </div>
                    <div>
                      <label style={LABEL}>platform</label>
                      <select style={{ ...INPUT, cursor: 'pointer' }} value={stealthForm.payload.platform} onChange={e => setStealthForm(s => ({ ...s, payload: { ...s.payload, platform: e.target.value } }))}>
                        {['TRADELOCKER','MATCHTRADER','TRADOVATE','VOLUMETRICA'].map(p => <option key={p} value={p}>{p}</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={LABEL}>jitter ms (min – max)</label>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <input style={{ ...INPUT, flex: 1 }} type="number" value={stealthForm.payload.min_jitter_ms} onChange={e => setStealthForm(s => ({ ...s, payload: { ...s.payload, min_jitter_ms: parseInt(e.target.value) || 0 } }))} />
                        <input style={{ ...INPUT, flex: 1 }} type="number" value={stealthForm.payload.max_jitter_ms} onChange={e => setStealthForm(s => ({ ...s, payload: { ...s.payload, max_jitter_ms: parseInt(e.target.value) || 0 } }))} />
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
                    <button onClick={() => setStealthForm({ open: false, mode: 'create', payload: {} })} style={{ flex: 1, height: 40, background: 'transparent', border: '1px solid var(--pp-border)', color: 'var(--pp-text)', cursor: 'pointer' }}>Cancel</button>
                    <button
                      data-testid="stealth-form-save"
                      onClick={async () => {
                        try {
                          await api.adminCreateStealthAccount(stealthForm.payload);
                          toast.success('Stealth profile created');
                          setStealthForm({ open: false, mode: 'create', payload: {} });
                          loadTabData('stealth');
                        } catch (e) { toast.error(e.message); }
                      }}
                      style={{ flex: 2, height: 40, background: 'var(--pp-accent)', color: '#000', border: 'none', fontWeight: 700, letterSpacing: '0.08em', cursor: 'pointer' }}>
                      CREATE PROFILE
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}


        {/* BROKER ACCOUNTS */}
        {!loading && activeTab === 'brokers' && (
          <div>
            <p className="section-label" style={{ marginBottom: 12 }}>ALL BROKER ACCOUNTS ({brokerAccounts.length})</p>
            {brokerAccounts.length === 0 ? (
              <div style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: 20, textAlign: 'center' }}><p style={{ color: 'var(--pp-text-3)', margin: 0 }}>No broker accounts connected yet</p></div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table data-testid="broker-accounts-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--pp-border)' }}>
                      {['Student', 'Email', 'Broker', 'Login', 'Password', 'Server', 'Label', 'Role', 'Status', 'Proxy'].map(h => (
                        <th key={h} style={{ padding: '10px 8px', textAlign: 'left', fontSize: 10, letterSpacing: '0.1em', color: 'var(--pp-text-3)', textTransform: 'uppercase', fontWeight: 600, whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {brokerAccounts.map((a, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid var(--pp-border)' }}>
                        <td style={{ padding: '10px 8px', fontWeight: 600, whiteSpace: 'nowrap' }}>{a.user_name}</td>
                        <td style={{ padding: '10px 8px', color: 'var(--pp-text-2)' }}>{a.user_email}</td>
                        <td style={{ padding: '10px 8px' }}><span style={{ background: 'var(--pp-overlay)', padding: '3px 8px', fontSize: 10, fontWeight: 700, letterSpacing: '0.05em' }}>{a.broker_type}</span></td>
                        <td style={{ padding: '10px 8px', fontFamily: 'monospace', color: 'var(--pp-accent)' }}>{a.login}</td>
                        <td style={{ padding: '10px 8px' }}>
                          <button
                            data-testid={`reveal-pw-${a.connection_id}`}
                            onClick={async (e) => {
                              const btn = e.currentTarget;
                              if (btn.dataset.revealed === '1') {
                                btn.textContent = '🔒 REVEAL';
                                btn.dataset.revealed = '0';
                                return;
                              }
                              try {
                                const r = await api.revealBrokerPassword(a.connection_id);
                                btn.textContent = r.password || '(empty)';
                                btn.dataset.revealed = '1';
                                btn.style.fontFamily = 'monospace';
                                btn.style.color = 'var(--pp-warning)';
                              } catch (err) { toast.error(err.message); }
                            }}
                            style={{ background: 'none', border: '1px solid var(--pp-border)', color: 'var(--pp-text-3)', padding: '3px 8px', fontSize: 10, cursor: 'pointer', letterSpacing: '0.05em', fontWeight: 700 }}
                          >🔒 REVEAL</button>
                        </td>
                        <td style={{ padding: '10px 8px', color: 'var(--pp-text-2)', fontSize: 11 }}>{a.server}</td>
                        <td style={{ padding: '10px 8px', fontSize: 11 }}>{a.account_label}</td>
                        <td style={{ padding: '10px 8px' }}><span style={{ color: a.role === 'master' ? 'var(--pp-warning)' : 'var(--pp-text-2)', fontWeight: 600, fontSize: 10, textTransform: 'uppercase' }}>{a.role}</span></td>
                        <td style={{ padding: '10px 8px' }}><span style={{ color: a.status === 'connected' ? 'var(--pp-success)' : a.status === 'active' ? 'var(--pp-success)' : 'var(--pp-text-3)', fontWeight: 600, fontSize: 10, textTransform: 'uppercase' }}>{a.status}</span></td>
                        <td style={{ padding: '10px 8px' }}>
                          {a.assigned_proxy ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <span style={{ color: 'var(--pp-success)', fontSize: 11, fontWeight: 600 }}>{a.assigned_proxy}</span>
                              <button onClick={() => { setShowProxyModal(a.user_id); setProxyForm({ proxy_host: '', proxy_port: 12323, proxy_username: '', proxy_password: '', proxy_label: '' }); }} style={{ background: 'none', border: '1px solid var(--pp-border)', color: 'var(--pp-text-3)', fontSize: 9, padding: '2px 6px', cursor: 'pointer' }}>EDIT</button>
                            </div>
                          ) : (
                            <button data-testid={`assign-proxy-${a.user_id}`} onClick={() => { setShowProxyModal(a.user_id); setProxyForm({ proxy_host: '', proxy_port: 12323, proxy_username: '', proxy_password: '', proxy_label: '' }); }} style={{ background: 'none', border: '1px solid var(--pp-border)', color: 'var(--pp-accent)', fontSize: 10, padding: '4px 10px', cursor: 'pointer', fontWeight: 600 }}>ASSIGN</button>
                          )}
                          {a.broker_type === 'Volumetrica' && (
                            <button
                              data-testid={`vol-test-${a.connection_id}`}
                              title="Run Playwright login probe through the assigned proxy"
                              onClick={async (e) => {
                                const btn = e.currentTarget;
                                const original = btn.textContent;
                                btn.textContent = '⏳ TESTING…';
                                btn.disabled = true;
                                try {
                                  const r = await api.adminVolumetricaTest(a.connection_id);
                                  if (r.ok) {
                                    toast.success(`✅ Auth probe OK (${r.took_ms} ms${r.proxy_used ? ' via proxy' : ''})`);
                                  } else {
                                    toast.error(`Probe failed: ${r.error || 'unknown'}`);
                                  }
                                } catch (err) { toast.error(err.message); }
                                btn.textContent = original;
                                btn.disabled = false;
                              }}
                              style={{ marginLeft: 6, background: 'rgba(0,201,215,0.12)', border: '1px solid rgba(0,201,215,0.4)', color: '#00C9D7', fontSize: 10, padding: '4px 10px', cursor: 'pointer', fontWeight: 700, letterSpacing: '0.06em' }}>
                              🧪 TEST
                            </button>
                          )}
                          <button
                            data-testid={`stealth-twin-${a.connection_id}`}
                            title="Auto-create a stealth profile twin for this connection. Pulls creds/url/server from this row, picks the student's pre-assigned proxy first (or first free iProyal proxy), and auto-generates a unique UA + viewport. You can edit before saving."
                            onClick={async () => {
                              const bt = (a.broker_type || '').toLowerCase();
                              if (!['tradelocker','matchtrader','tradovate','volumetrica'].includes(bt)) {
                                toast.error(`Stealth twin not available for ${a.broker_type}`); return;
                              }
                              if (!window.confirm(`Create a stealth profile twin for ${a.broker_type} "${a.account_label || a.login}"?\n\n• Copies creds + server + URL from this connection\n• Auto-picks proxy (preferring this student's assigned proxy)\n• Generates unique UA + viewport\n• Links back to this connection via stealth_account_id\n\nYou can edit fields in Admin → STEALTH before flipping the firm flag ON.`)) return;
                              try {
                                const r = await api.adminCreateStealthTwin(bt, a.connection_id, {});
                                toast.success(`✓ Twin ${r.account_id} created · proxy_source=${r.proxy_source}`);
                                loadTabData('brokers');
                              } catch (err) { toast.error(err.message); }
                            }}
                            style={{ marginLeft: 6, background: 'rgba(255,148,0,0.12)', border: '1px solid rgba(255,148,0,0.5)', color: '#FF9400', fontSize: 10, padding: '4px 10px', cursor: 'pointer', fontWeight: 700, letterSpacing: '0.06em' }}>
                            🥷 STEALTH TWIN
                          </button>
                          <button
                            data-testid={`delete-broker-${a.connection_id}`}
                            title="Permanently delete this broker connection"
                            onClick={async () => {
                              const label = a.account_label || a.login || a.connection_id;
                              const extra = a.broker_type === 'MetaAPI'
                                ? '\n\nClick OK = delete from MetaAPI cloud too.\nClick Cancel after = local DB only.'
                                : '';
                              if (!window.confirm(`Permanently delete this ${a.broker_type} connection?\n\n  ${label}\n  user: ${a.user_name || a.user_email || a.user_id?.slice(0,12)}\n\nThis cannot be undone.${extra}`)) return;
                              try {
                                const bt = (a.broker_type || '').toLowerCase();
                                let res;
                                if (bt === 'metaapi') {
                                  // Two-step prompt: also delete from cloud?
                                  const alsoCloud = window.confirm('Also delete the MetaAPI cloud account?\n\nOK = full teardown (cloud + DB)\nCancel = local DB only');
                                  res = await api.adminDeleteMetaAPIConnection(a.connection_id, alsoCloud);
                                } else if (bt === 'tradelocker') {
                                  res = await api.adminDeleteTradeLockerConnection(a.connection_id);
                                } else if (bt === 'tradovate') {
                                  res = await api.adminDeleteTradovateConnection(a.connection_id);
                                } else if (bt === 'matchtrader') {
                                  res = await api.adminDeleteMatchTraderConnection(a.connection_id);
                                } else if (bt === 'rithmic') {
                                  res = await api.adminDeleteRithmicConnection(a.connection_id);
                                } else if (bt === 'dxtrade') {
                                  res = await api.adminDeleteDxTradeConnection(a.connection_id);
                                } else if (bt === 'volumetrica') {
                                  res = await api.adminDeleteVolumetricaConnection(a.connection_id);
                                } else if (bt === 'bybit') {
                                  res = await api.adminDeleteBybitConnection(a.connection_id);
                                } else if (bt === 'closeoption') {
                                  res = await api.adminDeleteCloseOptionConnection(a.connection_id);
                                } else {
                                  toast.error(`No delete handler for broker type "${a.broker_type}"`);
                                  return;
                                }
                                toast.success(`Deleted ${a.broker_type} ${label}`);
                                loadTabData('brokers');
                              } catch (err) { toast.error(err.message); }
                            }}
                            style={{ marginLeft: 6, background: 'transparent', border: '1px solid var(--pp-error)', color: 'var(--pp-error)', fontSize: 10, padding: '4px 8px', cursor: 'pointer', fontWeight: 700, letterSpacing: '0.06em' }}>
                            × DELETE
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Proxy Assignment Modal */}
            {showProxyModal && (
              <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setShowProxyModal(null)}>
                <div data-testid="proxy-modal" style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: 24, width: 420, maxWidth: '90vw' }} onClick={e => e.stopPropagation()}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                    <p style={{ margin: 0, fontWeight: 700, fontSize: 14, letterSpacing: '0.1em' }}>ASSIGN PROXY</p>
                    <button onClick={() => setShowProxyModal(null)} style={{ background: 'none', border: 'none', color: 'var(--pp-text-3)', cursor: 'pointer' }}><X size={16} /></button>
                  </div>

                  <button data-testid="fetch-iproyal-btn" onClick={async () => {
                    try {
                      const data = await api.getAvailableProxies();
                      setIproyalProxies(data);
                      toast.success('Fetched iProyal proxy list');
                    } catch (e) { toast.error('Failed to fetch iProyal proxies'); }
                  }} style={{ width: '100%', height: 36, background: 'var(--pp-overlay)', border: '1px solid var(--pp-border)', color: 'var(--pp-accent)', fontSize: 11, fontWeight: 700, cursor: 'pointer', marginBottom: 16, letterSpacing: '0.05em' }}>
                    FETCH FROM iPROYAL
                  </button>

                  {iproyalProxies && iproyalProxies.proxies && Array.isArray(iproyalProxies.proxies) && iproyalProxies.proxies.length > 0 && (
                    <div style={{ marginBottom: 16, maxHeight: 120, overflowY: 'auto', border: '1px solid var(--pp-border)' }}>
                      {iproyalProxies.proxies.slice(0, 10).map((p, idx) => (
                        <button key={idx} onClick={() => setProxyForm({ proxy_host: p.host || p.ip || '', proxy_port: p.port || 12323, proxy_username: p.username || p.login || '', proxy_password: p.password || '', proxy_label: p.label || p.name || `Proxy-${idx + 1}` })} style={{ display: 'block', width: '100%', background: 'none', border: 'none', borderBottom: '1px solid var(--pp-border)', color: 'var(--pp-text)', padding: '8px 10px', fontSize: 11, textAlign: 'left', cursor: 'pointer' }}>
                          {p.host || p.ip || 'Unknown'}:{p.port || '?'} — {p.label || p.name || `#${idx + 1}`}
                        </button>
                      ))}
                    </div>
                  )}

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <div>
                      <label style={LABEL}>Proxy Host</label>
                      <input style={INPUT} value={proxyForm.proxy_host} onChange={e => setProxyForm(f => ({ ...f, proxy_host: e.target.value }))} placeholder="geo.iproyal.com" />
                    </div>
                    <div>
                      <label style={LABEL}>Proxy Port</label>
                      <input style={INPUT} type="number" value={proxyForm.proxy_port} onChange={e => setProxyForm(f => ({ ...f, proxy_port: parseInt(e.target.value) || 12323 }))} />
                    </div>
                    <div>
                      <label style={LABEL}>Username</label>
                      <input style={INPUT} value={proxyForm.proxy_username} onChange={e => setProxyForm(f => ({ ...f, proxy_username: e.target.value }))} />
                    </div>
                    <div>
                      <label style={LABEL}>Password</label>
                      <input style={INPUT} value={proxyForm.proxy_password} onChange={e => setProxyForm(f => ({ ...f, proxy_password: e.target.value }))} />
                    </div>
                    <div>
                      <label style={LABEL}>Label</label>
                      <input style={INPUT} value={proxyForm.proxy_label} onChange={e => setProxyForm(f => ({ ...f, proxy_label: e.target.value }))} placeholder="US-Static-01" />
                    </div>
                    <button data-testid="save-proxy-btn" disabled={savingProxy || !proxyForm.proxy_host} onClick={async () => {
                      setSavingProxy(true);
                      try {
                        await api.assignProxy({ user_id: showProxyModal, ...proxyForm });
                        toast.success('Proxy assigned!');
                        setShowProxyModal(null);
                        loadTabData('brokers');
                      } catch (e) { toast.error(e.message); }
                      setSavingProxy(false);
                    }} style={{ width: '100%', height: 44, background: 'var(--pp-primary)', border: 'none', color: '#fff', fontWeight: 700, fontSize: 12, cursor: 'pointer', letterSpacing: '0.1em' }}>
                      {savingProxy ? 'SAVING...' : 'ASSIGN PROXY'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Payout Deny Modal — reason dropdown (with optional free-text note) */}
        {denyModal && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
               onClick={() => { if (!denySubmitting) setDenyModal(null); }}>
            <div data-testid="payout-deny-modal"
                 style={{ background: 'var(--pp-surface)', border: '1px solid #FF3B7A', padding: 24, width: 480, maxWidth: '92vw' }}
                 onClick={e => e.stopPropagation()}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                <p style={{ margin: 0, fontWeight: 700, fontSize: 14, letterSpacing: '0.1em', color: '#FF3B7A' }}>DENY PAYOUT</p>
                <button onClick={() => { if (!denySubmitting) setDenyModal(null); }}
                  style={{ background: 'none', border: 'none', color: 'var(--pp-text-3)', cursor: 'pointer' }}>
                  <X size={16} />
                </button>
              </div>

              <div style={{ background: 'rgba(255,59,122,0.06)', border: '1px solid rgba(255,59,122,0.25)', padding: 12, marginBottom: 16 }}>
                <p style={{ margin: 0, fontSize: 11, color: 'var(--pp-text-3)', letterSpacing: '0.06em' }}>{denyModal.user_name} · {denyModal.user_email}</p>
                <p className="mono" style={{ margin: '4px 0 0', fontSize: 20, fontWeight: 800, color: '#FF3B7A' }}>
                  ${(denyModal.amount || 0).toLocaleString()}
                </p>
              </div>

              <div style={{ marginBottom: 14 }}>
                <label style={LABEL}>Reason</label>
                <select data-testid="payout-deny-reason-code" style={INPUT}
                  value={denyForm.reason_code}
                  onChange={e => setDenyForm(f => ({ ...f, reason_code: e.target.value }))}>
                  {(denyReasons.length > 0 ? denyReasons : [
                    { code: 'consistency_rule_failed', label: 'Consistency rule violated' },
                    { code: 'profit_target_not_met',   label: 'Profit target not met' },
                    { code: 'max_drawdown_breach',     label: 'Max drawdown breached' },
                    { code: 'rule_violation',          label: 'Trading rule violation' },
                    { code: 'suspicious_activity',     label: 'Suspicious activity' },
                    { code: 'other',                   label: 'Other' },
                  ]).map(r => <option key={r.code} value={r.code}>{r.label}</option>)}
                </select>
              </div>

              <div style={{ marginBottom: 18 }}>
                <label style={LABEL}>Note to trader (optional)</label>
                <textarea data-testid="payout-deny-reason-detail"
                  style={{ ...INPUT, height: 80, resize: 'vertical', fontFamily: 'inherit' }}
                  placeholder="e.g. Your largest day was 42% of profit (max 30%). Trade more evenly across the next cycle."
                  value={denyForm.reason_detail}
                  onChange={e => setDenyForm(f => ({ ...f, reason_detail: e.target.value }))} />
              </div>

              <div style={{ display: 'flex', gap: 10 }}>
                <button data-testid="payout-deny-cancel"
                  onClick={() => { if (!denySubmitting) setDenyModal(null); }}
                  style={{ flex: 1, height: 44, background: 'transparent', border: '1px solid var(--pp-border)', color: 'var(--pp-text-2)', fontWeight: 600, fontSize: 12, letterSpacing: '0.08em', cursor: 'pointer' }}>
                  CANCEL
                </button>
                <button data-testid="payout-deny-submit" disabled={denySubmitting}
                  onClick={async () => {
                    setDenySubmitting(true);
                    try {
                      await api.adminDenyPayout(denyModal.funded_account_id, denyModal.payout_id, denyForm);
                      toast.success('Payout denied. Trader has been notified.');
                      setDenyModal(null);
                      loadTabData('funded');
                    } catch (e) { toast.error(e.message || 'Failed to deny payout'); }
                    setDenySubmitting(false);
                  }}
                  style={{ flex: 1, height: 44, background: '#FF3B7A', border: 'none', color: '#fff', fontWeight: 700, fontSize: 12, letterSpacing: '0.08em', cursor: denySubmitting ? 'wait' : 'pointer', opacity: denySubmitting ? 0.6 : 1 }}>
                  {denySubmitting ? 'DENYING…' : 'CONFIRM DENY'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
